import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import sessionManager from '../utils/sessionManager';
import SessionTimeoutWarning from '../components/SessionTimeoutWarning';

// Helper to get the VDP link field from a row (case-insensitive, flexible)
const getVdpLink = (row) => {
  for (const key of Object.keys(row)) {
    const norm = key.replace(/\s|_/g, '').toLowerCase();
    if (norm === 'vdplink') return row[key];
  }
  return '';
};

// Simple CSV parser (for demo; use papaparse for more complex CSVs)
function parseCSV(csv) {
  const [headerLine, ...lines] = csv.trim().split('\n');
  // Normalize headers: remove quotes, trim, lowercase
  const headers = headerLine.split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
  return lines.map(line => {
    const values = line.split(',').map(v => v.replace(/"/g, '').trim());
    return headers.reduce((obj, h, i) => ({ ...obj, [h]: values[i] }), {});
  });
}

async function fetchDealershipsWithVehicleCounts() {
  const userRole = localStorage.getItem('adminRole');
  const userDealershipId = localStorage.getItem('adminDealershipId');

  // For regular admins, only fetch their assigned dealership
  if (userRole !== 'OWNER' && userDealershipId) {
    const res = await fetch(`/api/dealerships/${userDealershipId}`);
    const dealership = await res.json();
    const vehiclesRes = await fetch(`/api/dealerships/${dealership._id}/vehicles`);
    const vehicles = await vehiclesRes.json();
    return [{ ...dealership, vehicles: vehicles.length }];
  }

  // For owners, fetch all dealerships
  const res = await fetch('/api/dealerships');
  const dealerships = await res.json();
  const updatedDealerships = await Promise.all(
    dealerships.map(async (d) => {
      const res = await fetch(`/api/dealerships/${d._id}/vehicles`);
      const vehicles = await res.json();
      return { ...d, vehicles: vehicles.length };
    })
  );
  return updatedDealerships;
}

// Helper to get a field value by trying multiple possible keys
const getField = (obj, keys) => {
  for (const key of keys) {
    // First try exact match
    if (obj[key] !== undefined) {
      return obj[key];
    }
    // Then try case-insensitive search
    for (const k of Object.keys(obj)) {
      if (k.toLowerCase() === key.toLowerCase()) {
        return obj[k];
      }
    }
  }
  return '';
};

const getVehicleName = (v) => {
  const name = v.name || v.Name || v.vehicleName || v.VehicleName;
  if (name) return name;
  return [v.year, v.make, v.model].filter(Boolean).join(' ');
};

// Add this mapping above the modal code
const logFieldLabels = {
  calcDownPayment: 'Down Payment',
  includeTaxes: 'Are Taxes Included?',
  zipCode: 'Zip Code',
  calcCreditScore: 'Credit Score',
  showTradeIn: 'Trade-In',
  tradeInValue: 'Trade-In Estimate',
  hasTradePayoff: 'Has Payoff?',
  tradePayoff: 'Trade Payoff',
  calcTerm: 'Term',
  calcApr: 'APR',
  Term: 'Term',
  APR: 'APR',
};

const clickStreamLabels = {
  photo_clicked: 'Photo Navigation',
  pdf_clicked: 'PDF Clicks',
  video_clicked: 'Video Plays'
};

export default function AdminDashboard() {
  const [dealerships, setDealerships] = useState([]);
  const [showInventory, setShowInventory] = useState(false);
  const [selectedDealership, setSelectedDealership] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [csvContent, setCsvContent] = useState('');
  const [lastModified, setLastModified] = useState('');
  const [leads, setLeads] = useState([]);
  const [newDealership, setNewDealership] = useState({ name: '', email: '' });
  const [activeTab, setActiveTab] = useState('Inventory');
  const [leadSearch, setLeadSearch] = useState('');
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [leadsDealership, setLeadsDealership] = useState(null);
  const [showPacketModal, setShowPacketModal] = useState(false);
  const [packetVehicles, setPacketVehicles] = useState([]);
  const [packetDealership, setPacketDealership] = useState(null);
  const [packetSearch, setPacketSearch] = useState('');
  const [packetFilter, setPacketFilter] = useState('all'); // 'all', 'used', 'new'
  const [selectedPacketVehicle, setSelectedPacketVehicle] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showSalesTeamModal, setShowSalesTeamModal] = useState(false);
  const [settingsDealership, setSettingsDealership] = useState(null);
  const [salesTeamDealership, setSalesTeamDealership] = useState(null);
  const [settingsVin, setSettingsVin] = useState('');
  const [settingsVideoUrl, setSettingsVideoUrl] = useState('');
  const [settingsMessage, setSettingsMessage] = useState('');
  const [dealershipSettings, setDealershipSettings] = useState(null);
  const [inspectionPdfUrl, setInspectionPdfUrl] = useState('');
  const [pdfFile, setPdfFile] = useState(null);

  // Add state for send packet modal
  const [showSendPacketModal, setShowSendPacketModal] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendMethod, setSendMethod] = useState('email'); // 'email' or 'weblink'
  const [lastGeneratedLink, setLastGeneratedLink] = useState('');
  const [selectedVehicleForPacket, setSelectedVehicleForPacket] = useState(null);

  const [packetLogs, setPacketLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Add state for selected dealership for logs
  const [selectedDealershipForLogs, setSelectedDealershipForLogs] = useState(null);

  // Add a modal/box for dealership logs
  const [showLogsModal, setShowLogsModal] = useState(false);

  // Add state for log search
  const [logSearch, setLogSearch] = useState('');

  const [now, setNow] = useState(Date.now());

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [customerAdded, setCustomerAdded] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ firstName: '', lastName: '', email: '', phone: '' });

  // Window Sticker Finder state
  const [showWindowStickerModal, setShowWindowStickerModal] = useState(false);
  const [windowStickerVin, setWindowStickerVin] = useState('');
  const [windowStickerResults, setWindowStickerResults] = useState([]);
  const [windowStickerLoading, setWindowStickerLoading] = useState(false);

  // State for session timeout warning
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success', link: '' });

  // Show toast notification
  const showToast = (message, type = 'success', link = '') => {
    setToast({ show: true, message, type, link });
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  // Window Sticker Finder functions
  const searchWindowStickers = async (vin) => {
    if (!vin || vin.length !== 17) {
      showToast('Please enter a valid 17-character VIN', 'error');
      return;
    }

    setWindowStickerLoading(true);
    setWindowStickerResults([]);

    const manufacturers = [
      { name: 'GM', endpoint: 'gm', color: 'bg-blue-500' },
      { name: 'Chrysler', endpoint: 'chrysler', color: 'bg-red-500' },
      { name: 'Ford', endpoint: 'ford', color: 'bg-green-500' },
      { name: 'Subaru', endpoint: 'subaru', color: 'bg-indigo-500' }
    ];

    const results = [];

    // Search all manufacturers simultaneously
    const searchPromises = manufacturers.map(async (manufacturer) => {
      try {
        const response = await fetch(`/api/window-sticker/${manufacturer.endpoint}/${vin}`);
        
        if (response.ok) {
          // Create a blob for the PDF
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          return {
            manufacturer: manufacturer.name,
            status: 'found',
            url: url,
            color: manufacturer.color,
            downloadUrl: `/api/window-sticker/${manufacturer.endpoint}/${vin}`
          };
        } else {
          return {
            manufacturer: manufacturer.name,
            status: 'not_found',
            color: manufacturer.color,
            error: 'Window sticker not found'
          };
        }
      } catch (error) {
        return {
          manufacturer: manufacturer.name,
          status: 'error',
          color: manufacturer.color,
          error: error.message
        };
      }
    });

    const searchResults = await Promise.all(searchPromises);
    setWindowStickerResults(searchResults);
    setWindowStickerLoading(false);
  };

  // Add state for inventory search
  const [inventorySearch, setInventorySearch] = useState('');

  // Add state for viewing lead URLs
  const [viewLeadModal, setViewLeadModal] = useState(false);
  const [leadPacketLogs, setLeadPacketLogs] = useState([]);
  const [leadPacketLogsLoading, setLeadPacketLogsLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // Add state for deleting leads
  const [deletingLeadId, setDeletingLeadId] = useState(null);
  const [showDeleteLeadConfirm, setShowDeleteLeadConfirm] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);

  // Add state for viewing customer info modal
  const [showCustomerInfoModal, setShowCustomerInfoModal] = useState(false);
  const [customerInfoLog, setCustomerInfoLog] = useState(null);

  // Add state for email template modal
  const [showEmailTemplateModal, setShowEmailTemplateModal] = useState(false);
  const [emailTemplateLog, setEmailTemplateLog] = useState(null);
  const [emailCustomMessage, setEmailCustomMessage] = useState('');
  const [emailTemplateCopied, setEmailTemplateCopied] = useState(false);

  // Add state for selected packet logs and delete confirmation
  const [selectedPacketLogIds, setSelectedPacketLogIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Add state for viewing customer logs modal
  const [showCustomerLogsModal, setShowCustomerLogsModal] = useState(false);
  const [customerLogsLog, setCustomerLogsLog] = useState(null);

  const [activeLogTab, setActiveLogTab] = useState(null);
  const [activeClickStreamTab, setActiveClickStreamTab] = useState(null);
  const [isClickStreamMode, setIsClickStreamMode] = useState(false);

  // Add state for trade-in evaluations
  const [tradeInEvaluations, setTradeInEvaluations] = useState([]);
  const [tradeInLoading, setTradeInLoading] = useState(false);
  const [selectedTradeInEvaluation, setSelectedTradeInEvaluation] = useState(null);
  const [showTradeInDetailModal, setShowTradeInDetailModal] = useState(false);
  
  // Add state for photo lightbox
  const [showPhotoLightbox, setShowPhotoLightbox] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  
  // Add state for leads modal tabs
  const [leadsActiveTab, setLeadsActiveTab] = useState('leads');

  // Add this state near the top of the component
  const [leadSubmissionStatus, setLeadSubmissionStatus] = useState({});
  const [leadLastOpened, setLeadLastOpened] = useState({}); // { [lead.email]: 'Yes' | 'No' | '...' }
  const leadStatusCache = useRef({});

  // Add state for trade-in status update
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  // Add state for trade-in delete confirmation
  const [showDeleteTradeInModal, setShowDeleteTradeInModal] = useState(false);
  const [tradeInToDelete, setTradeInToDelete] = useState(null);
  const [deletingTradeInId, setDeletingTradeInId] = useState(null);

  // Photo upload link state
  const [generatingPhotoLink, setGeneratingPhotoLink] = useState(null);
  
  // Add state for approval confirmation modal
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [tradeInToApprove, setTradeInToApprove] = useState(null);
  const [approvingTradeInId, setApprovingTradeInId] = useState(null);

  const [autoCheckPdfFile, setAutoCheckPdfFile] = useState(null);
  const [msrpPdfFile, setMsrpPdfFile] = useState(null);
  const [autoCheckPdfUrl, setAutoCheckPdfUrl] = useState('');
  const [msrpPdfUrl, setMsrpPdfUrl] = useState('');

  // Add state for copied link feedback near the top
  const [copiedLogId, setCopiedLogId] = useState(null);
  
  // Add missing state variables for Send Packet modal
  const [pastedCustomerText, setPastedCustomerText] = useState('');

  const [copied, setCopied] = useState(false);

  // Add state for editing a vehicle in the settings modal
  const [editingVehicle, setEditingVehicle] = useState(null);

  // Add pagination state for customer leads
  const [leadsCurrentPage, setLeadsCurrentPage] = useState(1);
  const [leadsPerPage] = useState(10);

  // Add pagination state for trade-in evaluations
  const [tradeInCurrentPage, setTradeInCurrentPage] = useState(1);
  const [tradeInPerPage] = useState(10);

  // Add search state for trade-in evaluations
  const [tradeInSearch, setTradeInSearch] = useState('');
  
  // Add filter state for trade-in evaluations
  const [showTradeInFilterDropdown, setShowTradeInFilterDropdown] = useState(false);
  const [tradeInFilter, setTradeInFilter] = useState('recent-submission');
  const [tradeInStatusFilters, setTradeInStatusFilters] = useState({
    all: true,
    pending: false,
    approved: false
  });

  // Add state for edit modal fields
  const [editVideoUrl, setEditVideoUrl] = useState('');
  const [edit172File, setEdit172File] = useState(null);
  const [editAutoCheckFile, setEditAutoCheckFile] = useState(null);
  const [editMsrpFile, setEditMsrpFile] = useState(null);

  // Add state for update success message
  const [editSuccess, setEditSuccess] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Add state for settings vehicle search
  const [settingsVehicleSearch, setSettingsVehicleSearch] = useState('');
  
  // Add state for missing info filters
  const [filterMissingVideo, setFilterMissingVideo] = useState(false);
  const [filterMissingAutoCheck, setFilterMissingAutoCheck] = useState(false);
  const [filterMissingMsrp, setFilterMissingMsrp] = useState(false);
  const [filterMissingInspection, setFilterMissingInspection] = useState(false);

  // Add state for manual CSV watch path
  const [manualCsvWatchPath, setManualCsvWatchPath] = useState('');
  const [watchingCsvPath, setWatchingCsvPath] = useState('');
  
  // Add state for Owner Settings
  const [showOwnerSettingsModal, setShowOwnerSettingsModal] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '', dealershipId: '', role: 'ADMIN' });
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminMessage, setAdminMessage] = useState('');
  const [isOwner, setIsOwner] = useState(false);

  // Animation refs for space background
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const starsRef = useRef([]);

  // Add state for Sales Team Management
  const [salesTeamMembers, setSalesTeamMembers] = useState([]);
      const [salesTeamForm, setSalesTeamForm] = useState({ name: '', email: '', password: '' });
    const [salesTeamMessage, setSalesTeamMessage] = useState('');
    const [isAddingSalesTeamMember, setIsAddingSalesTeamMember] = useState(false);
    
    // Add state for Activity Analytics
    const [activityLogs, setActivityLogs] = useState([]);
    const [activityStats, setActivityStats] = useState(null);
    const [activityLoading, setActivityLoading] = useState(false);
    const [showActivityAnalytics, setShowActivityAnalytics] = useState(false);
    const [activityFilter, setActivityFilter] = useState('all');
    const [activityPeriod, setActivityPeriod] = useState('30');

  // Add state for View Salesperson section
  const [dealershipSalespeople, setDealershipSalespeople] = useState([]);
  
  // Add state for URL Activity tracking
  const [urlActivities, setUrlActivities] = useState([]);
  const [urlActivitiesLoading, setUrlActivitiesLoading] = useState(false);
  
  // State management for lead assignment
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [leadToAssign, setLeadToAssign] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [dealershipAdmins, setDealershipAdmins] = useState([]);

  // State management for password reset
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);

  // State management for leads filtering
  const [leadsFilter, setLeadsFilter] = useState('recent-lead');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  
  // State for date filters
  const [dateFilters, setDateFilters] = useState({
    today: false,
    threeDays: false,
    sevenDays: false,
    all: true
  });

  useEffect(() => {
    // Check authentication first
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    const adminName = localStorage.getItem('adminName');
    const adminEmail = localStorage.getItem('adminEmail');
    
    if (!isLoggedIn || !adminName || !adminEmail) {
      // Clear all localStorage and redirect to login
      localStorage.clear();
      window.location.replace('/admin/login');
      return;
    }
    
    // Check if user is owner
    const userRole = localStorage.getItem('adminRole');
    setIsOwner(userRole === 'OWNER');
    
    // Initialize session manager
    sessionManager.startSession(
      // Warning callback - show timeout warning
      () => {
        setShowTimeoutWarning(true);
      },
      // Logout callback - redirect to login
      () => {
        localStorage.clear();
        window.location.replace('/admin/login');
      }
    );
    
    // Check session validity on component mount
    if (!sessionManager.checkSession()) {
      localStorage.clear();
      window.location.replace('/admin/login');
      return;
    }
    
    // Cleanup session on unmount
    return () => {
      sessionManager.endSession();
    };
  }, []);

  // Initialize stars
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Create stars
    const stars = [];
    for (let i = 0; i < 150; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 1.5 + 0.3,
        speed: Math.random() * 0.3 + 0.05,
        opacity: Math.random() * 0.6 + 0.2,
        twinkle: Math.random() * 0.02 + 0.005
      });
    }
    starsRef.current = stars;
    
    // Handle resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset pagination when search changes
  useEffect(() => {
    setLeadsCurrentPage(1);
  }, [leadSearch, dateFilters, leadsFilter]);

  useEffect(() => {
    setTradeInCurrentPage(1);
  }, [tradeInSearch, tradeInFilter, tradeInStatusFilters]); // Reset pagination when trade-in search/filters change

  // Reset pagination when switching tabs
  useEffect(() => {
    setLeadsCurrentPage(1);
    setTradeInCurrentPage(1);
  }, [leadsActiveTab]);

  // Close trade-in filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTradeInFilterDropdown && !event.target.closest('.filter-dropdown')) {
        setShowTradeInFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTradeInFilterDropdown]);

  // Mouse tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const stars = starsRef.current;
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw moving stars
      stars.forEach(star => {
        // Move stars
        star.y += star.speed;
        if (star.y > canvas.height) {
          star.y = -5;
          star.x = Math.random() * canvas.width;
        }
        
        // Twinkle effect
        star.opacity += star.twinkle;
        if (star.opacity > 1 || star.opacity < 0.1) {
          star.twinkle = -star.twinkle;
        }
        
        // Mouse interaction - subtle attraction to mouse
        const dx = mouseRef.current.x - star.x;
        const dy = mouseRef.current.y - star.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          const force = (100 - distance) / 100;
          star.x += dx * force * 0.005;
          star.y += dy * force * 0.005;
        }
        
        // Draw star
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.fill();
        
        // Add subtle glow effect
        if (star.size > 1) {
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.size * 1.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(135, 206, 250, ${star.opacity * 0.2})`;
          ctx.fill();
        }
      });
      
      // Draw subtle nebula effect around mouse
      const gradient = ctx.createRadialGradient(
        mouseRef.current.x, mouseRef.current.y, 0,
        mouseRef.current.x, mouseRef.current.y, 150
      );
      gradient.addColorStop(0, 'rgba(138, 43, 226, 0.03)');
      gradient.addColorStop(0.5, 'rgba(75, 0, 130, 0.02)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Add state for PDF uploader functionality
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const fileInputRef = useRef(null);

  // Add state for session activity modal
  const [showSessionActivityModal, setShowSessionActivityModal] = useState(false);
  const [sessionActivityData, setSessionActivityData] = useState([]);
  const [sessionActivityLoading, setSessionActivityLoading] = useState(false);
  const [customerLogsLoading, setCustomerLogsLoading] = useState(false);
  const [fetchingWindowSticker, setFetchingWindowSticker] = useState(false);
  const [fetchingAutoCheck, setFetchingAutoCheck] = useState(false);
  const [sessionActivityCustomer, setSessionActivityCustomer] = useState(null);
  const [sessionActivityLink, setSessionActivityLink] = useState(null);
  const [sessionActivityDealershipId, setSessionActivityDealershipId] = useState(null);

  useEffect(() => {
    if (!showLogsModal) return;
    const interval = setInterval(() => setNow(Date.now()), 2000); // every 2 seconds
    return () => clearInterval(interval);
  }, [showLogsModal]);

  const openInventory = (dealership) => {
    setSelectedDealership(dealership);
    setShowInventory(true);
  };

  const closeInventory = () => {
    setShowInventory(false);
    setSelectedDealership(null);
    setInventory([]);
    setCsvContent('');
    setLastModified('');
  };

  useEffect(() => {
    let interval;
    if (showInventory && watchingCsvPath && selectedDealership) {
      interval = setInterval(() => {
        fetch(`/api/dealerships/${selectedDealership._id}/csv`, { cache: 'no-store' })
          .then(res => res.ok ? res.text() : '')
          .then(text => {
            setCsvContent(text);
            setInventory(text ? parseCSV(text) : []);
          });
        fetch(`/api/dealerships/${selectedDealership._id}/csv`, { method: 'HEAD' })
          .then(res => {
            const lm = res.headers.get('Last-Modified');
            if (lm) setLastModified(new Date(lm).toLocaleString());
            else setLastModified('');
          });
      }, 15000); // 15 seconds
    }
    return () => clearInterval(interval);
  }, [showInventory, watchingCsvPath, selectedDealership]);

  useEffect(() => {
    const userRole = localStorage.getItem('adminRole');
    const userDealershipId = localStorage.getItem('adminDealershipId');

    fetch('/api/dealerships')
      .then(res => res.json())
      .then(data => {
        // Add a check for valid ObjectId length (24 characters)
        const validDealerships = data.filter(d => d._id && d._id.length === 24);
        if (validDealerships.length !== data.length) {
          alert('Found and filtered out corrupted dealership data. Please check your database.');
        }

        // Filter dealerships based on admin's role and dealership ID
        let filteredDealerships;
        if (userRole === 'OWNER') {
          // Owners can see all dealerships
          filteredDealerships = validDealerships;
        } else {
          // Regular admins can only see their assigned dealership
          filteredDealerships = userDealershipId 
            ? validDealerships.filter(d => d._id === userDealershipId)
            : [];
        }
        
        setDealerships(filteredDealerships);
      });
  }, []);

  useEffect(() => {
    async function fetchVehicleCounts() {
      const updatedDealerships = await Promise.all(
        dealerships.map(async (d) => {
          const res = await fetch(`/api/dealerships/${d._id}/vehicles`);
          const vehicles = await res.json();
          return { ...d, vehicles: vehicles.length };
        })
      );
      setDealerships(updatedDealerships);
    }
    if (dealerships.length > 0) fetchVehicleCounts();
  }, [dealerships.length]);

  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (file && selectedDealership) {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`/api/dealerships/${selectedDealership._id}/upload-csv`, {
        method: 'POST',
        body: formData,
      });
      
      if (res.ok) {
        const data = await res.json();
        alert(data.message);
        // Add a check for valid ObjectId length (24 characters)
        const validDealerships = data.dealerships.filter(d => d._id && d._id.length === 24);
        if (validDealerships.length !== data.dealerships.length) {
          alert('Found and filtered out corrupted dealership data. Please check your database.');
        }
        setDealerships(validDealerships || []);
      } else {
        alert('Failed to upload inventory.');
      }

      // Refetch and display the new inventory for this dealership
      fetch(`/api/dealerships/${selectedDealership._id}/csv`, { cache: 'no-store' })
        .then(res => res.ok ? res.text() : '')
        .then(text => {
          setCsvContent(text);
          setInventory(text ? parseCSV(text) : []);
        });
      // Refetch last modified time
      fetch(`/api/dealerships/${selectedDealership._id}/csv`, { method: 'HEAD' })
        .then(res => {
          const lm = res.headers.get('Last-Modified');
          if (lm) setLastModified(new Date(lm).toLocaleString());
          else setLastModified('');
        });
    }
  };

  useEffect(() => {
    if (activeTab === 'Leads' && selectedDealership) {
      // Get user info from localStorage for role-based filtering
      const userRole = localStorage.getItem('adminRole');
      const userEmail = localStorage.getItem('adminEmail');
      const userId = localStorage.getItem('adminId');
      
      // Create query string with user info for role-based filtering
      const queryParams = new URLSearchParams({
        userRole: userRole || '',
        userEmail: userEmail || '',
        userId: userId || '',
        userName: localStorage.getItem('adminName') || 'User'
      });
      
      fetch(`/api/dealerships/${selectedDealership._id}/leads?${queryParams}`)
        .then(res => res.json())
        .then(data => {
          setLeads(data.leads || []);
          console.log(`Loaded ${(data.leads || []).length} leads for ${userRole} user in tab`);
        });
    }
  }, [activeTab, selectedDealership]);

  // Effect for web link generation
  useEffect(() => {
    if (sendMethod === 'weblink' && selectedVehicleForPacket && packetDealership) {
      const uniqueLink = `${window.location.origin}/vehicle-packet/${packetDealership._id}/${selectedVehicleForPacket.vin}/${Math.random().toString(36).substr(2, 9)}`;
      setLastGeneratedLink(uniqueLink);
    }
    // Optionally clear the link if switching back to email
    if (sendMethod === 'email') {
      setLastGeneratedLink('');
    }
  }, [sendMethod, selectedVehicleForPacket, packetDealership]);

  const filteredLeads = leads.filter(l => {
    // Search filter
    const searchMatch = (
      (
        ((l.firstName || '') + ' ' + (l.lastName || '')).toLowerCase().includes(leadSearch.toLowerCase())
      ) ||
      ((l.email || '').toLowerCase().includes(leadSearch.toLowerCase())) ||
      ((l.phone || '').toLowerCase().includes(leadSearch.toLowerCase())) ||
      ((l.vehicle?.stockNumber || '').toLowerCase().includes(leadSearch.toLowerCase()))
    );
    
    if (!searchMatch) return false;
    
    // Date filter - if all or no specific filter is active, show all leads
    if (dateFilters.all || (!dateFilters.today && !dateFilters.threeDays && !dateFilters.sevenDays)) {
      return true;
    }
    
    // Parse lead date
    const leadDate = new Date(l.date || l.createdAt || 0);
    
    // Skip invalid dates
    if (isNaN(leadDate.getTime())) {
      return dateFilters.all; // Only show if "all" is selected for invalid dates
    }
    
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
    
    if (dateFilters.today) {
      // Only show leads from today
      return leadDate >= todayStart && leadDate <= todayEnd;
    }
    
    if (dateFilters.threeDays) {
      // Show leads from last 3 days (including today)
      const threeDaysAgo = new Date(today);
      threeDaysAgo.setDate(today.getDate() - 2); // Last 3 days including today
      threeDaysAgo.setHours(0, 0, 0, 0);
      return leadDate >= threeDaysAgo && leadDate <= todayEnd;
    }
    
    if (dateFilters.sevenDays) {
      // Show leads from last 7 days (including today)
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 6); // Last 7 days including today
      sevenDaysAgo.setHours(0, 0, 0, 0);
      return leadDate >= sevenDaysAgo && leadDate <= todayEnd;
    }
    
    return false;
  }).sort((a, b) => {
    // Add safety checks to prevent sorting errors
    if (!a || !b) return 0;
    
    try {
      switch(leadsFilter) {
        case 'recent-lead':
          // Sort by most recent lead creation date
          return new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0);
        case 'recent-session':
          // Sort by most recent session activity (customer input log time from click streams)
          // Prioritize leads with actual click stream data over those without
          const hasClickStreamA = a.mostRecentClickTime !== null && a.mostRecentClickTime !== undefined;
          const hasClickStreamB = b.mostRecentClickTime !== null && b.mostRecentClickTime !== undefined;
          
          // If one has click stream data and the other doesn't, prioritize the one with data
          if (hasClickStreamA && !hasClickStreamB) {
            return -1; // A comes first
          }
          if (!hasClickStreamA && hasClickStreamB) {
            return 1; // B comes first
          }
          
          // If both have click stream data, sort by most recent click time
          if (hasClickStreamA && hasClickStreamB) {
            const timeA = new Date(a.mostRecentClickTime);
            const timeB = new Date(b.mostRecentClickTime);
            return timeB - timeA;
          }
          
          // If neither has click stream data, sort by creation date
          return new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0);
        case 'recent-calculator':
          // Sort by most recent calculator log activity
          // Prioritize leads with actual calculator log data over those without
          const hasCalcLogA = a.mostRecentCalcTime !== null && a.mostRecentCalcTime !== undefined;
          const hasCalcLogB = b.mostRecentCalcTime !== null && b.mostRecentCalcTime !== undefined;
          
          // If one has calc log data and the other doesn't, prioritize the one with data
          if (hasCalcLogA && !hasCalcLogB) {
            return -1; // A comes first
          }
          if (!hasCalcLogA && hasCalcLogB) {
            return 1; // B comes first
          }
          
          // If both have calc log data, sort by most recent calc time
          if (hasCalcLogA && hasCalcLogB) {
            const timeA = new Date(a.mostRecentCalcTime);
            const timeB = new Date(b.mostRecentCalcTime);
            return timeB - timeA;
          }
          
          // If neither has calc log data, sort by creation date
          return new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0);
        case 'most-clicks':
          // Sort by actual click stream count (highest first)
          const clicksA = a.clickStreamCount || 0;
          const clicksB = b.clickStreamCount || 0;
          if (clicksB !== clicksA) {
            return clicksB - clicksA;
          }
          // If click counts are equal, sort by date as secondary sort
          return new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0);
        default:
          return new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0);
      }
    } catch (error) {
      console.error('Error in lead sorting:', error);
      // Fallback to date sorting if there's an error
      return new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0);
    }
  });

  // Helper function to fetch click stream counts and calculator log timestamps for leads
  const fetchClickStreamCounts = async (dealershipId, leads) => {
    if (!dealershipId || !leads || !Array.isArray(leads)) {
      console.warn('Invalid parameters for fetchClickStreamCounts');
      return leads || [];
    }

    try {
      // Process leads in smaller batches to avoid overwhelming the API
      const batchSize = 10;
      const batches = [];
      
      for (let i = 0; i < leads.length; i += batchSize) {
        batches.push(leads.slice(i, i + batchSize));
      }
      
      const processedLeads = [];
      
      for (const batch of batches) {
        const batchResults = await Promise.allSettled(
          batch.map(async (lead) => {
            // Ensure lead has required properties
            if (!lead || !lead.email) {
              return {
                ...lead,
                clickStreamCount: 0,
                mostRecentClickTime: null,
                mostRecentCalcTime: null
              };
            }

            try {
              // Fetch click stream data with timeout
              const clickController = new AbortController();
              const clickTimeout = setTimeout(() => clickController.abort(), 5000);
              
              const clickRes = await fetch(`/api/dealerships/${dealershipId}/click-stream-log/${lead.email}`, {
                signal: clickController.signal
              });
              clearTimeout(clickTimeout);
              
              let clickData = [];
              let mostRecentClickTime = null;
              
              if (clickRes.ok) {
                clickData = await clickRes.json();
                if (Array.isArray(clickData) && clickData.length > 0) {
                  const timestamp = clickData[0].timestamp;
                  if (timestamp) {
                    mostRecentClickTime = new Date(timestamp);
                  }
                }
              }
              
              // Fetch calculator log data with timeout
              const calcController = new AbortController();
              const calcTimeout = setTimeout(() => calcController.abort(), 5000);
              
              const calcRes = await fetch(`/api/dealerships/api/packet-logs?dealershipId=${dealershipId}&email=${encodeURIComponent(lead.email)}`, {
                signal: calcController.signal
              });
              clearTimeout(calcTimeout);
              
              let mostRecentCalcTime = null;
              
              if (calcRes.ok) {
                const packetLogs = await calcRes.json();
                if (Array.isArray(packetLogs) && packetLogs.length > 0) {
                  let latestTimestamp = null;
                  
                  packetLogs.forEach(log => {
                    if (log && log.customerLogs && Array.isArray(log.customerLogs)) {
                      const calcLogs = log.customerLogs.filter(logEntry => 
                        logEntry && logEntry.field !== 'user_interaction'
                      );
                      if (calcLogs.length > 0) {
                        const logTimestamp = calcLogs[calcLogs.length - 1].timestamp;
                        if (logTimestamp && (!latestTimestamp || logTimestamp > latestTimestamp)) {
                          latestTimestamp = logTimestamp;
                        }
                      }
                    }
                  });
                  
                  if (latestTimestamp) {
                    mostRecentCalcTime = new Date(latestTimestamp);
                  }
                }
              }
              
              return {
                ...lead,
                clickStreamCount: Array.isArray(clickData) ? clickData.length : 0,
                mostRecentClickTime: mostRecentClickTime,
                mostRecentCalcTime: mostRecentCalcTime
              };
            } catch (err) {
              console.error('Error fetching data for lead:', lead.email, err);
              return {
                ...lead,
                clickStreamCount: 0,
                mostRecentClickTime: null,
                mostRecentCalcTime: null
              };
            }
          })
        );
        
        // Handle settled promises
        batchResults.forEach(result => {
          if (result.status === 'fulfilled') {
            processedLeads.push(result.value);
          } else {
            console.error('Failed to process lead:', result.reason);
            // Add a fallback lead with default values
            processedLeads.push({
              clickStreamCount: 0,
              mostRecentClickTime: null,
              mostRecentCalcTime: null
            });
          }
        });
      }
      
      return processedLeads;
    } catch (err) {
      console.error('Error fetching click stream counts:', err);
      return leads.map(lead => ({ 
        ...lead, 
        clickStreamCount: 0,
        mostRecentClickTime: null,
        mostRecentCalcTime: null 
      }));
    }
  };



  const openLeadsModal = async (dealership) => {
    setLeadsDealership(dealership);
    setShowLeadsModal(true);
    setLeadsActiveTab('leads'); // Reset to leads tab
    setTradeInEvaluations([]); // Reset trade-in evaluations
    setLeadSearch(''); // Clear search input when modal opens
    setTradeInSearch(''); // Clear trade-in search when modal opens
    
    try {
      // Get user info from localStorage
      const userRole = localStorage.getItem('adminRole');
      const userEmail = localStorage.getItem('adminEmail');
      const userId = localStorage.getItem('adminId'); // For sales members
      
      // Create query string with user info for role-based filtering
      const queryParams = new URLSearchParams({
        userRole: userRole || '',
        userEmail: userEmail || '',
        userId: userId || '',
        userName: localStorage.getItem('adminName') || 'User'
      });
      
      console.log('Fetching leads with role-based filtering:', { userRole, userEmail, userId });
      
      // Fetch leads
      const res = await fetch(`/api/dealerships/${dealership._id}/leads?${queryParams}`);
      if (res.ok) {
        const data = await res.json();
        let fetchedLeads = data.leads || [];
        
        // Ensure we have an array and each lead has required properties
        if (Array.isArray(fetchedLeads)) {
          // Fetch click stream counts for all leads with better error handling
          fetchedLeads = await fetchClickStreamCounts(dealership._id, fetchedLeads);
        } else {
          fetchedLeads = [];
        }
        
        setLeads(fetchedLeads);
        console.log(`Loaded ${fetchedLeads.length} leads with filter data for ${userRole} user`);
      } else {
        setLeads([]); // or handle error
      }
      
      // Fetch trade-in evaluations
      await fetchTradeInEvaluations(dealership._id);
    } catch (err) {
      console.error('Error fetching leads:', err);
      setLeads([]); // or handle error
    }
  };

  const handleSubmitLead = async (leadData) => {
    if (!selectedDealership) return;
    
    // Get user info from localStorage for createdBy field
    const userRole = localStorage.getItem('adminRole');
    const userEmail = localStorage.getItem('adminEmail');
    const userName = localStorage.getItem('adminName');
    const userId = localStorage.getItem('adminId');
    
    // Add createdBy information to the lead data
    const leadWithCreator = {
      ...leadData,
      createdBy: {
        email: userEmail,
        name: userName,
        role: userRole,
        id: userId
      }
    };
    
    console.log('Creating lead with creator info:', leadWithCreator);
    
    await fetch(`/api/dealerships/${selectedDealership._id}/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(leadWithCreator)
    });
  };

  // Open Vehicle Packet Modal
  const openPacketModal = async (dealership) => {
    setPacketDealership(dealership);
    setShowPacketModal(true);
    setPacketSearch('');
    setPacketFilter('all');
    // Fetch vehicles from CSV file directly (for accurate data)
    try {
      const res = await fetch(`/api/dealerships/${dealership._id}/vehicles-csv?limit=9999`);
      const data = await res.json();
      // Handle new response structure with pagination
      const vehicles = data.vehicles || data || [];
      setPacketVehicles(vehicles);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setPacketVehicles([]);
    }
  };
  const closePacketModal = () => {
    setShowPacketModal(false);
    setPacketDealership(null);
    setPacketVehicles([]);
    setPacketSearch('');
  };

  // Basic filtering - show vehicles with simple search and new/used filter
  const filteredPacketVehicles = packetVehicles.filter(v => {
    // First filter by search term
    if (packetSearch) {
      const s = packetSearch.toLowerCase();
      const searchMatch = (
        (v.stockNumber || '').toLowerCase().includes(s) ||
        (v.vin || '').toLowerCase().includes(s) ||
        (v.model || '').toLowerCase().includes(s) ||
        (v.make || '').toLowerCase().includes(s)
      );
      if (!searchMatch) return false;
    }
    
    // Then filter by new/used type
    if (packetFilter === 'used') {
      // Vehicle is used if mileage > 100 or if it's explicitly marked as used
      const mileage = Number(v.mileage || v.Mileage || v.miles || v.Miles || 0);
      const condition = (v.condition || v.Condition || v.type || v.Type || '').toLowerCase();
      return mileage > 100 || condition.includes('used') || condition.includes('pre-owned');
    } else if (packetFilter === 'new') {
      // Vehicle is new if mileage is 0-100 miles or if it's explicitly marked as new
      const mileage = Number(v.mileage || v.Mileage || v.miles || v.Miles || 0);
      const condition = (v.condition || v.Condition || v.type || v.Type || '').toLowerCase();
      return mileage <= 100 || condition.includes('new') || (!condition && mileage <= 100);
    }
    
    // For 'all' filter, return true (show all vehicles)
    return true;
  }).sort((a, b) => {
    // Sort by photo count (most photos first)
    const getPhotoCount = (vehicle) => {
      // Count photos from various possible fields
      let photoCount = 0;
      
      // Check imageUrls field (comma-separated string)
      const imageUrls = vehicle.imageUrls || vehicle.ImageUrls || vehicle['Image URLs'] || '';
      if (imageUrls && typeof imageUrls === 'string') {
        const urls = imageUrls.split(',').filter(url => url && url.trim().length > 0);
        photoCount += urls.length;
      }
      
      // Check individual image fields (imageOne, imageTwo, etc.)
      const imageFields = ['imageOne', 'imageTwo', 'imageThree', 'imageFour', 'imageFive', 'imageSix', 'imageSeven', 'imageEight'];
      imageFields.forEach(field => {
        const imageUrl = vehicle[field] || vehicle[field.charAt(0).toUpperCase() + field.slice(1)] || vehicle[field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())];
        if (imageUrl && imageUrl.trim().length > 0 && imageUrl.startsWith('http')) {
          photoCount++;
        }
      });
      
      // Ensure at least 0 photos
      return Math.max(0, photoCount);
    };
    
    const aPhotoCount = getPhotoCount(a);
    const bPhotoCount = getPhotoCount(b);
    
    // Sort descending (most photos first)
    return bPhotoCount - aPhotoCount;
  });



  let modalTitle = 'All Vehicles';
  if (packetFilter === 'used') modalTitle = 'Used Vehicles';
  if (packetFilter === 'new') modalTitle = 'New Vehicles';
  const vehicleCount = filteredPacketVehicles.length;

  // When opening the settings modal, fetch dealership settings
  const openSettingsModal = async (dealership) => {
    setSettingsDealership(dealership);
    setShowSettingsModal(true);
    setSettingsVin('');
    setSettingsVideoUrl('');
    setSettingsMessage('');
    // Fetch settings for this dealership
    const res = await fetch(`/api/dealerships/${dealership._id}/settings`);
    const settings = await res.json();
    // Fetch vehicles for this dealership (from CSV file directly)
    let vehicles = [];
    try {
      const vehiclesRes = await fetch(`/api/dealerships/${dealership._id}/vehicles-csv?limit=9999`);
      if (vehiclesRes.ok) {
        const data = await vehiclesRes.json();
        // Handle new response structure with pagination
        vehicles = data.vehicles || data || [];
      }
    } catch (e) {
      console.error('Error fetching vehicles for settings:', e);
      vehicles = [];
    }
    setDealershipSettings({ ...settings, vehicles });
  };

  // When opening the sales team modal
  const openSalesTeamModal = async (dealership) => {
    setSalesTeamDealership(dealership);
    setShowSalesTeamModal(true);
    setSalesTeamMessage('');
    // Fetch sales team for this dealership
    await fetchSalesTeam(dealership._id);
  };

  // Send packet handler
  const handleSendPacket = async () => {
    setSending(true);
    const uniqueLink = `${window.location.origin}/vehicle-packet/${packetDealership._id}/${selectedVehicleForPacket.vin}/${Math.random().toString(36).substr(2, 9)}`;
    setLastGeneratedLink(uniqueLink);
    if (sendMethod === 'weblink') {
      setSending(false);
      setSendResult('success');
      return;
    }
    const vehicleSummary = `${selectedVehicleForPacket.year || ''} ${selectedVehicleForPacket.make || ''} ${selectedVehicleForPacket.model || ''} ${selectedVehicleForPacket.trim || ''} (VIN: ${selectedVehicleForPacket.vin || 'N/A'})`;
    console.log('Sending packet...', { recipientEmail, uniqueLink, customMessage, vehicleSummary });
    
    try {
      // First, create a lead record for the recipient
      const leadData = {
        firstName: '', // Will be filled by customer when they visit the packet
        lastName: '', // Will be filled by customer when they visit the packet
        email: recipientEmail,
        phone: '', // Will be filled by customer when they visit the packet
        zip: '', // Will be filled by customer when they visit the packet
        dealershipId: packetDealership._id,
        vehicle: {
          year: selectedVehicleForPacket.year,
          make: selectedVehicleForPacket.make,
          model: selectedVehicleForPacket.model,
          trim: selectedVehicleForPacket.trim,
          vin: selectedVehicleForPacket.vin,
          stockNumber: selectedVehicleForPacket.stockNumber,
          mileage: selectedVehicleForPacket.mileage,
          price: selectedVehicleForPacket.price
        }
      };

      // Create the lead
      const leadRes = await fetch(`/api/dealerships/${packetDealership._id}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadData)
      });

      if (!leadRes.ok) {
        console.error('Failed to create lead:', await leadRes.text());
      } else {
        console.log('Lead created successfully');
        // Refresh the leads list to show the new lead
        if (leadsDealership && leadsDealership._id === packetDealership._id) {
          const res = await fetch(`/api/dealerships/${packetDealership._id}/leads`);
          if (res.ok) {
            const data = await res.json();
            setLeads(data.leads || []);
          }
        }
      }

      // Then send the email
      const res = await fetch('/api/send-packet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: recipientEmail,
          link: uniqueLink,
          customMessage,
          vehicleSummary
        }),
      });
      console.log('Response:', res);
      if (res.ok) {
        setSendResult('success');
      } else {
        setSendResult('error');
      }
    } catch (err) {
      console.error('Send packet error:', err);
      setSendResult('error');
    }
    setSending(false);
  };

  // When opening the Send Packet modal, set the default message
  const openSendPacketModal = (vehicle, dealership) => {
    setSelectedVehicleForPacket(vehicle);
    setPacketDealership(dealership);
    setCustomMessage(''); // Empty default message
    setShowSendPacketModal(true);
  };

  // Fetch logs for selected dealership
  useEffect(() => {
    if (!selectedDealershipForLogs) {
      setPacketLogs([]);
      return;
    }
    async function fetchLogs() {
      setLogsLoading(true);
      const res = await fetch(`/api/dealerships/api/packet-logs?dealershipId=${selectedDealershipForLogs._id}`);
      const logs = await res.json();
      setPacketLogs(logs);
      setLogsLoading(false);
    }
    fetchLogs();
  }, [selectedDealershipForLogs]);

  const refreshLogs = async () => {
    if (!selectedDealershipForLogs) return;
    setLogsLoading(true);
    const res = await fetch(`/api/dealerships/api/packet-logs?dealershipId=${selectedDealershipForLogs._id}`);
    const logs = await res.json();
    setPacketLogs(logs);
    setLogsLoading(false);
  };

  // Filter logs based on search
  const filteredPacketLogs = logSearch.trim() === '' ? packetLogs : packetLogs.filter(log => {
    const search = logSearch.toLowerCase();
    const customer = log.customer || {};
    return (
      (customer.firstName && customer.firstName.toLowerCase().includes(search)) ||
      (customer.lastName && customer.lastName.toLowerCase().includes(search)) ||
      (customer.email && customer.email.toLowerCase().includes(search)) ||
      (customer.phone && customer.phone.toLowerCase().includes(search))
    );
  });

  function handleGenerateNewLink() {
    if (!selectedCustomer) return;
    const link = generateUniqueLinkAndLog(selectedCustomer);
    setLastGeneratedLink(link);
  }

  function generateUniqueLinkAndLog(customer) {
    if (!selectedVehicleForPacket || !packetDealership || !customer) return '';
    const linkId = Math.random().toString(36).substr(2, 9);
    const fullUrl = `${window.location.origin}/vehicle-packet/${packetDealership._id}/${selectedVehicleForPacket.vin}/${linkId}`;
    const pathUrl = `/vehicle-packet/${packetDealership._id}/${selectedVehicleForPacket.vin}/${linkId}`;
    
    // Create packet log with path format (to match handle-open lookup)
    const userInfo = {
      name: localStorage.getItem('adminName') || 'Unknown',
      email: localStorage.getItem('adminEmail') || 'Unknown',
      role: localStorage.getItem('adminRole') || 'ADMIN',
      id: localStorage.getItem('adminId') || 'system'
    };
    
    fetch('/api/dealerships/api/packet-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerId: customer._id || customer.id,
        link: pathUrl, // Store path format instead of full URL
        linkId: linkId, // Include the linkId for matching
        vehicle: selectedVehicleForPacket,
        dealershipId: packetDealership._id,
        email: customer.email, // <-- add this line
        createdBy: userInfo
      }),
    })
    .then(response => response.json())
    .then(data => {
      console.log('Packet log created successfully:', data);
    })
    .catch(error => {
      console.error('Error creating packet log:', error);
    });

    // Track URL creation activity
    fetch('/api/dealerships/api/url-activity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        createdBy: {
          name: userInfo.name,
          email: userInfo.email,
          role: userInfo.role
        },
        dealership: packetDealership._id,
        dealershipName: packetDealership.name,
        customer: {
          firstName: customer.firstName,
          lastName: customer.lastName,
          email: customer.email,
          phone: customer.phone
        },
        vehicle: {
          vin: selectedVehicleForPacket.vin,
          year: selectedVehicleForPacket.year,
          make: selectedVehicleForPacket.make,
          model: selectedVehicleForPacket.model,
          stockNumber: selectedVehicleForPacket.stockNumber
        },
        customUrl: fullUrl,
        linkId: linkId
      }),
    });
    
    return fullUrl;
  }

  // Parse customer text for auto-filling form
  const parseCustomerText = (text) => {
    if (!text.trim()) return;

    // Extract email (more specific pattern)
    const emailMatch = text.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/);
    const email = emailMatch ? emailMatch[0] : '';

    // Extract phone number (various formats)
    const phoneMatch = text.match(/(?:C:|Cell:|Phone:|Mobile:|Tel:)?\s*\(?(\d{3})\)?[-.\s]*(\d{3})[-.\s]*(\d{4})/i) || 
                     text.match(/\(?(\d{3})\)?[-.\s]*(\d{3})[-.\s]*(\d{4})/);
    const phone = phoneMatch ? `(${phoneMatch[1]}) ${phoneMatch[2]}-${phoneMatch[3]}` : '';

    // Extract name from first line (most reliable approach)
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    let firstName = '';
    let lastName = '';
    
    if (lines.length > 0) {
      const firstLine = lines[0];
      // Remove common prefixes and suffixes
      const cleanedLine = firstLine.replace(/\(.*?\)/g, '').trim();
      const nameParts = cleanedLine.split(/\s+/);
      
      if (nameParts.length >= 2) {
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' '); // Join remaining parts as last name
      } else if (nameParts.length === 1) {
        firstName = nameParts[0];
      }
    }

    // Update the form with extracted data
    setNewCustomer({
      firstName: firstName,
      lastName: lastName,
      email: email,
      phone: phone
    });
  };

  // Add new customer for SendPacketModal
  async function handleAddCustomer(e) {
    e.preventDefault();
    if (!packetDealership) return;
    
    // Get user info from localStorage for createdBy field
    const userRole = localStorage.getItem('adminRole');
    const userEmail = localStorage.getItem('adminEmail');
    const userName = localStorage.getItem('adminName');
    const userId = localStorage.getItem('adminId');
    
    const customerData = {
      ...newCustomer,
      createdBy: {
        email: userEmail,
        name: userName,
        role: userRole,
        id: userId
      }
    };
    
    const res = await fetch(`/api/dealerships/${packetDealership._id}/customers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(customerData),
    });
    let customer = null;
    if (res.status === 409) {
      const data = await res.json();
      alert('Customer already exists for this dealership.');
      customer = data.customer;
    } else if (res.ok) {
      customer = await res.json();
      setCustomerAdded(true);
      setTimeout(() => setCustomerAdded(false), 2000);
      // Get user info from localStorage for createdBy field
      const userRole = localStorage.getItem('adminRole');
      const userEmail = localStorage.getItem('adminEmail');
      const userName = localStorage.getItem('adminName');
      const userId = localStorage.getItem('adminId');
      
      const leadRes = await fetch(`/api/dealerships/${packetDealership._id}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: newCustomer.firstName,
          lastName: newCustomer.lastName,
          email: newCustomer.email,
          phone: newCustomer.phone,
          zip: newCustomer.zip || '',
          dealershipId: packetDealership._id,
          vehicle: selectedVehicleForPacket || null,
          createdBy: {
            email: userEmail,
            name: userName,
            role: userRole,
            id: userId
          }
        })
      });
      
      if (!leadRes.ok) {
        console.error('Error creating lead:', await leadRes.text());
      }
    } else {
      const err = await res.json();
      alert('Error adding customer: ' + (err.error || 'Unknown error'));
      console.error('Add customer error:', err);
      return;
    }
    
    // Auto-select the customer (either newly created or existing)
    if (customer) {
      setSelectedCustomer(customer);
    }
    
    setCustomerSearch('');
    setCustomerResults([]);
    setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
  }

  // Handler to toggle selection
  const togglePacketLogSelection = (logId) => {
    setSelectedPacketLogIds(prev =>
      prev.includes(logId) ? prev.filter(id => id !== logId) : [...prev, logId]
    );
  };

  // Handler to delete selected logs
  const handleDeleteSelectedLogs = async () => {
    let allSuccess = true;
    for (const logId of selectedPacketLogIds) {
      const res = await fetch(`/api/dealerships/api/packet-logs/${logId}`, { method: 'DELETE' });
      if (!res.ok) {
        allSuccess = false;
      }
    }
    // Always refetch logs for the current lead after deletion
    if (selectedLead && leadsDealership) {
      setLeadPacketLogsLoading(true);
      const res = await fetch(`/api/dealerships/api/packet-logs?dealershipId=${leadsDealership._id}&email=${encodeURIComponent(selectedLead.email)}`);
      const logs = await res.json();
      setLeadPacketLogs(logs);
      setLeadPacketLogsLoading(false);
    }
    setSelectedPacketLogIds([]);
    setShowDeleteConfirm(false);
    if (!allSuccess) {
      alert('One or more deletions failed. Please try again.');
    }
  };

  // Add polling for 'now' when viewLeadModal is open
  useEffect(() => {
    if (!viewLeadModal) return;
    const interval = setInterval(() => setNow(Date.now()), 2000);
    return () => clearInterval(interval);
  }, [viewLeadModal]);

  // Helper to check submission status for a lead
  async function checkLeadSubmissionStatus(lead, dealershipId) {
    if (!lead.email || !dealershipId) return 'No';
    // Don't use cache for last opened times, always fetch fresh data
    setLeadSubmissionStatus(prev => ({ ...prev, [lead.email]: '...' }));
    try {
      const res = await fetch(`/api/dealerships/api/packet-logs?dealershipId=${dealershipId}&email=${encodeURIComponent(lead.email)}`);
      const logs = await res.json();
      const submitted = logs.some(log => {
        return (
        (log.options && Object.keys(log.options).length > 0) ||
        (log.submittedOptions && Object.keys(log.submittedOptions).length > 0)
      );
      });
      
      // Also check for last opened time
      const openedLogs = logs.filter(log => log.opened && log.openedAt);
      if (openedLogs.length > 0) {
        // Find the most recent opened time
        const lastOpenedTime = Math.max(...openedLogs.map(log => new Date(log.openedAt).getTime()));
        setLeadLastOpened(prev => ({ ...prev, [lead.email]: new Date(lastOpenedTime) }));
      } else {
        setLeadLastOpened(prev => ({ ...prev, [lead.email]: null }));
      }
      
      leadStatusCache.current[lead.email] = submitted ? 'Yes' : 'No';
      setLeadSubmissionStatus(prev => ({ ...prev, [lead.email]: submitted ? 'Yes' : 'No' }));
      return submitted ? 'Yes' : 'No';
    } catch (err) {
      setLeadSubmissionStatus(prev => ({ ...prev, [lead.email]: 'No' }));
      setLeadLastOpened(prev => ({ ...prev, [lead.email]: null }));
      return 'No';
    }
  }

  // Function to refresh all lead data
  const refreshLeadData = async () => {
    if (!leadsDealership || !leads || leads.length === 0) return;
    leads.forEach(lead => {
      if (lead.email) {
        checkLeadSubmissionStatus(lead, leadsDealership._id);
      }
    });
  };

  // Trade-in evaluation functions
  const fetchTradeInEvaluations = async (dealershipId) => {
    setTradeInLoading(true);
    try {
      const response = await fetch(`/api/trade-in-evaluations/${dealershipId}`);
      if (response.ok) {
        const data = await response.json();
        setTradeInEvaluations(data.evaluations || []);
      } else {
        console.error('Failed to fetch trade-in evaluations');
        setTradeInEvaluations([]);
      }
    } catch (error) {
      console.error('Error fetching trade-in evaluations:', error);
      setTradeInEvaluations([]);
    } finally {
      setTradeInLoading(false);
    }
  };

  const viewTradeInDetails = async (evaluationId) => {
    try {
      const response = await fetch(`/api/trade-in-evaluations/single/${evaluationId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedTradeInEvaluation(data.evaluation);
        setShowTradeInDetailModal(true);
      } else {
        alert('Failed to fetch trade-in evaluation details');
      }
    } catch (error) {
      console.error('Error fetching trade-in details:', error);
      alert('Error fetching trade-in evaluation details');
    }
  };

  // Photo lightbox functions
  const openPhotoLightbox = (photos, startIndex = 0) => {
    const processedPhotos = photos.map(photo => ({
      ...photo,
      url: `/uploads/trade-in-photos/${photo.filename}`
    }));
    setLightboxPhotos(processedPhotos);
    setCurrentPhotoIndex(startIndex);
    setShowPhotoLightbox(true);
  };

  const closePhotoLightbox = () => {
    setShowPhotoLightbox(false);
    setCurrentPhotoIndex(0);
    setLightboxPhotos([]);
  };

  const goToNextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % lightboxPhotos.length);
  };

  const goToPrevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + lightboxPhotos.length) % lightboxPhotos.length);
  };

  // Photo upload link functions - Simplified
  const generatePhotoUploadLink = async (evaluation) => {
    setGeneratingPhotoLink(evaluation._id);
    
    try {
      const response = await fetch(`/api/trade-in-evaluations/${evaluation._id}/photo-upload-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        // Copy the link to clipboard
        await navigator.clipboard.writeText(result.uploadLink);
        
        const photoCount = evaluation.photos ? evaluation.photos.length : 0;
        const hasPhotos = photoCount > 0;
        const messageText = hasPhotos 
          ? `Photo upload link generated and copied! Share with ${evaluation.customerInfo?.firstName} ${evaluation.customerInfo?.lastName} to upload additional photos. They already have ${photoCount} photo${photoCount !== 1 ? 's' : ''} - new photos will be added to their collection.`
          : `Photo upload link generated and copied! Share with ${evaluation.customerInfo?.firstName} ${evaluation.customerInfo?.lastName} to upload photos of their vehicle.`;
        
        showToast(messageText, 'success', result.uploadLink);
        
        // Refresh the trade-in evaluations to show updated status
        if (leadsDealership) {
          fetchTradeInEvaluations(leadsDealership._id);
        }
      } else {
        showToast('Failed to generate photo upload link: ' + result.message, 'error');
      }
    } catch (error) {
      console.error('Error generating photo upload link:', error);
      showToast('Error generating photo upload link. Please try again.', 'error');
    } finally {
      setGeneratingPhotoLink(null);
    }
  };

  // Keyboard navigation for photo lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showPhotoLightbox) return;
      
      switch (e.key) {
        case 'Escape':
          closePhotoLightbox();
          break;
        case 'ArrowLeft':
          goToPrevPhoto();
          break;
        case 'ArrowRight':
          goToNextPhoto();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showPhotoLightbox, lightboxPhotos.length]);

  // Update trade-in evaluation status
  const updateTradeInStatus = async (evaluationId, newStatus) => {
    setStatusUpdateLoading(true);
    try {
      const response = await fetch(`/api/trade-in-evaluations/${evaluationId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: newStatus,
          updatedBy: localStorage.getItem('adminEmail') || 'Admin'
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Status updated successfully:', result);
        
        // Update the selected evaluation with the fresh data from the server
        setSelectedTradeInEvaluation(result.evaluation);
        
        // Refresh the evaluations list
        if (leadsDealership) {
          await fetchTradeInEvaluations(leadsDealership._id);
        }
      } else {
        console.error('Failed to update status');
        alert('Failed to update status. Please try again.');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status. Please try again.');
    } finally {
      setStatusUpdateLoading(false);
      setShowStatusUpdateModal(false);
      setSelectedStatus('');
    }
  };

  // Delete trade-in evaluation functions
  const handleDeleteTradeIn = (evaluation) => {
    setTradeInToDelete(evaluation);
    setShowDeleteTradeInModal(true);
  };

  const confirmDeleteTradeIn = async () => {
    if (!tradeInToDelete || !leadsDealership) return;
    
    setDeletingTradeInId(tradeInToDelete._id);
    try {
      const response = await fetch(`/api/trade-in-evaluations/${tradeInToDelete._id}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        // Remove the evaluation from the local state
        setTradeInEvaluations(prev => prev.filter(e => e._id !== tradeInToDelete._id));
        
        // If this was the selected evaluation in the detail modal, close it
        if (selectedTradeInEvaluation && selectedTradeInEvaluation._id === tradeInToDelete._id) {
          setShowTradeInDetailModal(false);
          setSelectedTradeInEvaluation(null);
        }
      } else {
        const error = await response.json();
        alert(`Error deleting trade-in evaluation: ${error.message}`);
      }
    } catch (error) {
      alert(`Error deleting trade-in evaluation: ${error.message}`);
    } finally {
      setDeletingTradeInId(null);
      setShowDeleteTradeInModal(false);
      setTradeInToDelete(null);
    }
  };

  // Delete lead function
  const handleDeleteLead = async (lead) => {
    setLeadToDelete(lead);
    setShowDeleteLeadConfirm(true);
  };

  const confirmDeleteLead = async () => {
    if (!leadToDelete || !leadsDealership) return;
    
    setDeletingLeadId(leadToDelete._id);
    try {
      // Get user info for logging
      const userRole = localStorage.getItem('adminRole');
      const userEmail = localStorage.getItem('adminEmail');
      const userName = localStorage.getItem('adminName');
      const userId = localStorage.getItem('adminId');
      const userParams = new URLSearchParams({
        userRole: userRole || 'admin',
        userEmail: userEmail || 'admin@system.com',
        userName: userName || 'Admin',
        userId: userId || 'system'
      });
      
      const response = await fetch(`/api/dealerships/${leadsDealership._id}/leads/${leadToDelete._id}?${userParams}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        const result = await response.json();
        // Remove the lead from the local state
        setLeads(prev => prev.filter(l => l._id !== leadToDelete._id));
        // Show success message
        alert(`Lead deleted successfully! Also removed ${result.customersDeleted} customers and ${result.packetLogsDeleted} packet logs.`);
      } else {
        const error = await response.json();
        alert(`Error deleting lead: ${error.error}`);
      }
    } catch (error) {
      alert(`Error deleting lead: ${error.message}`);
    } finally {
      setDeletingLeadId(null);
      setShowDeleteLeadConfirm(false);
      setLeadToDelete(null);
    }
  };

  // Lead assignment functions
  const openAssignModal = async (lead) => {
    if (!leadsDealership) return;
    
    setLeadToAssign(lead);
    setSelectedAssignee(lead.assignedTo?.email || '');
    setShowAssignModal(true);
    
    // Fetch sales team members for the dealership
    try {
      const response = await fetch(`/api/dealerships/${leadsDealership._id}/sales-team`);
      if (response.ok) {
        const members = await response.json();
        console.log('Sales team members fetched:', members);
        setSalesTeamMembers(members);
      } else {
        console.error('Failed to fetch sales team members:', response.status, response.statusText);
        setSalesTeamMembers([]);
      }
    } catch (error) {
      console.error('Error fetching sales team:', error);
      setSalesTeamMembers([]);
    }
    
    // Fetch admin users for the dealership
    try {
      const adminResponse = await fetch('/api/dealerships/admin/users');
      if (adminResponse.ok) {
        const allAdmins = await adminResponse.json();
        // Filter admins for this specific dealership
        const filteredAdmins = allAdmins.filter(admin => 
          admin.dealership?._id === leadsDealership._id && admin.role === 'ADMIN'
        );
        console.log('Admin users fetched for dealership:', filteredAdmins);
        setDealershipAdmins(filteredAdmins);
      } else {
        console.error('Failed to fetch admin users:', adminResponse.status, adminResponse.statusText);
        setDealershipAdmins([]);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
      setDealershipAdmins([]);
    }
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setLeadToAssign(null);
    setSelectedAssignee('');
    setSalesTeamMembers([]);
    setDealershipAdmins([]);
  };

  const handleAssignLead = async () => {
    if (!leadToAssign || !leadsDealership) return;
    
    setAssignmentLoading(true);
    
    try {
      const userRole = localStorage.getItem('adminRole');
      const userEmail = localStorage.getItem('adminEmail');
      const userName = localStorage.getItem('adminName');
      
      if (selectedAssignee === '') {
        // Unassign the lead
        const response = await fetch(`/api/dealerships/${leadsDealership._id}/leads/${leadToAssign._id}/assign`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            unassignedBy: {
              email: userEmail,
              name: userName,
              role: userRole
            }
          })
        });
        
        if (response.ok) {
          // Update the lead in the local state
          setLeads(prev => prev.map(l => 
            l._id === leadToAssign._id 
              ? { ...l, assignedTo: null }
              : l
          ));
          alert('Lead unassigned successfully!');
        } else {
          const error = await response.json();
          alert(`Error unassigning lead: ${error.error}`);
        }
      } else {
        // Assign the lead
        // First check if the selected assignee is in sales team members
        let selectedMember = salesTeamMembers.find(m => m.email === selectedAssignee);
        let assigneeRole = 'SALES';
        
        // If not found in sales team, check in admin users
        if (!selectedMember) {
          selectedMember = dealershipAdmins.find(a => a.email === selectedAssignee);
          assigneeRole = 'ADMIN';
        }
        
        if (!selectedMember) {
          alert('Selected assignee not found');
          return;
        }
        
        const response = await fetch(`/api/dealerships/${leadsDealership._id}/leads/${leadToAssign._id}/assign`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            assignedTo: {
              email: selectedMember.email,
              name: selectedMember.name,
              role: assigneeRole,
              id: selectedMember._id
            },
            assignedBy: {
              email: userEmail,
              name: userName,
              role: userRole
            }
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          // Update the lead in the local state
          setLeads(prev => prev.map(l => 
            l._id === leadToAssign._id 
              ? result.lead
              : l
          ));
          alert(`Lead assigned to ${selectedMember.name} successfully!`);
        } else {
          const error = await response.json();
          alert(`Error assigning lead: ${error.error}`);
        }
      }
      
      closeAssignModal();
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setAssignmentLoading(false);
    }
  };

  // When leads or leadsDealership change, check submission status for all leads
  useEffect(() => {
    if (!leadsDealership || !leads || leads.length === 0) return;
    leads.forEach(lead => {
      if (!lead.email) return;
      // Always refresh the data, don't rely on cache for last opened times
        checkLeadSubmissionStatus(lead, leadsDealership._id);
    });
  }, [leads, leadsDealership]);

  // Add periodic refresh for lead data when leads modal is open


  // Add this after the state declarations for settingsVin, settingsDealership, inspectionPdfUrl, autoCheckPdfUrl, msrpPdfUrl
  useEffect(() => {
    if (!settingsVin || !settingsDealership || !settingsDealership._id) {
      setInspectionPdfUrl('');
      setAutoCheckPdfUrl('');
      setMsrpPdfUrl('');
      setSettingsVideoUrl('');
      return;
    }
    const vinSafe = settingsVin.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    fetch(`/api/dealerships/${settingsDealership._id}/settings`)
      .then(res => res.json())
      .then(settings => {
        setInspectionPdfUrl((settings?.inspectionPdfs?.[vinSafe] || settings?.inspectionPdfs?.get?.(vinSafe) || '') ? `${settings?.inspectionPdfs?.[vinSafe] || settings?.inspectionPdfs?.get?.(vinSafe)}?cb=${Date.now()}` : '');
        setAutoCheckPdfUrl(settings?.autoCheckPdfs?.[vinSafe] || settings?.autoCheckPdfs?.get?.(vinSafe) || '');
        setMsrpPdfUrl(settings?.msrpPdfs?.[vinSafe] || settings?.msrpPdfs?.get?.(vinSafe) || '');
        // Add this for video URL:
        if (settings?.vehicleVideos) {
          const direct = settings.vehicleVideos[settingsVin];
          const upper = settings.vehicleVideos[settingsVin?.toUpperCase()];
          const lower = settings.vehicleVideos[settingsVin?.toLowerCase()];
          setSettingsVideoUrl(direct || upper || lower || '');
        } else {
          setSettingsVideoUrl('');
        }
      });
  }, [settingsVin, settingsDealership]);

  // When opening the edit modal, prefill fields and reset success
  useEffect(() => {
    if (editingVehicle && dealershipSettings) {
      const vinSafe = (editingVehicle.vin || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      setEditVideoUrl(dealershipSettings.vehicleVideos?.[vinSafe] || '');
      setEdit172File(null);
      setEditAutoCheckFile(null);
      setEditMsrpFile(null);
      setEditSuccess(false);
    }
  }, [editingVehicle, dealershipSettings]);

  async function handleUpdateVehicle() {
    if (!settingsDealership || !editingVehicle) return;
    setEditLoading(true);
    setEditError('');
    setEditSuccess(false);
    const dealershipId = settingsDealership._id;
    const vin = editingVehicle.vin;
    const vinSafe = (vin || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    try {
      // Upload 172 PDF
      if (edit172File && edit172File !== 'REPLACE') {
        const formData = new FormData();
        formData.append('pdf', edit172File);
        formData.append('vin', vinSafe);
        // Add stockNumber and uploadedAs for legacy replacement logic
        const stockSafe = (editingVehicle.stocknumber || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        if (stockSafe) formData.append('stockNumber', stockSafe);
        // Determine if this upload is by stock number or VIN
        let uploadedAs = 'vin';
        if (edit172File.name && stockSafe && edit172File.name.replace(/\.[^/.]+$/, '').toUpperCase() === stockSafe) {
          uploadedAs = 'stock';
        }
        formData.append('uploadedAs', uploadedAs);
        await fetch(`/api/dealerships/${dealershipId}/settings/inspection-pdf`, {
          method: 'POST',
          body: formData,
        });
        setEdit172File(null); // Reset after successful upload
      }
      // Upload AutoCheck PDF
      if (editAutoCheckFile) {
        const formData = new FormData();
        formData.append('pdf', editAutoCheckFile);
        formData.append('vin', vinSafe);
        await fetch(`/api/dealerships/${dealershipId}/settings/autocheck-pdf`, {
          method: 'POST',
          body: formData,
        });
      }
      // Upload MSRP PDF
      if (editMsrpFile) {
        const formData = new FormData();
        formData.append('pdf', editMsrpFile);
        formData.append('vin', vinSafe);
        await fetch(`/api/dealerships/${dealershipId}/settings/msrp-pdf`, {
          method: 'POST',
          body: formData,
        });
      }
      // Save video URL if changed
      if (editVideoUrl && editVideoUrl !== (dealershipSettings.vehicleVideos?.[vinSafe] || '')) {
        await fetch(`/api/dealerships/${dealershipId}/settings/vehicle-video`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vin: vinSafe, videoUrl: editVideoUrl }),
        });
      }
      // Refresh settings and vehicle list
      const res = await fetch(`/api/dealerships/${dealershipId}/settings`);
      const settings = await res.json();
      // Also refresh the vehicle list from CSV
      let vehicles = [];
      try {
        const csvRes = await fetch(`/api/dealerships/${dealershipId}/csv`);
        if (csvRes.ok) {
          const csvText = await csvRes.text();
          vehicles = csvText ? parseCSV(csvText) : [];
        }
      } catch (e) {
        vehicles = [];
      }
      setDealershipSettings({ ...settings, vehicles });
      setEditSuccess(true);
    } catch (err) {
      setEditError('Failed to update vehicle. Please try again.');
    }
    setEditLoading(false);
  }

  // Function to determine which window sticker API to use based on vehicle make
  const getWindowStickerEndpoint = (vehicleMake) => {
    const make = vehicleMake?.toLowerCase() || '';
    
    // GM brands
    if (make.includes('chevrolet') || make.includes('chevy') || make.includes('gmc') || 
        make.includes('cadillac') || make.includes('buick')) {
      return 'gm';
    }
    
    // Chrysler/Stellantis brands
    if (make.includes('chrysler') || make.includes('dodge') || make.includes('jeep') || 
        make.includes('ram') || make.includes('plymouth') || make.includes('fiat')) {
      return 'chrysler';
    }
    
    // Ford brands
    if (make.includes('ford') || make.includes('lincoln') || make.includes('mercury')) {
      return 'ford';
    }
    
    // Subaru brands
    if (make.includes('subaru')) {
      return 'subaru';
    }
    
    // Default to GM if unknown
    return 'gm';
  };

  const handleFetchWindowSticker = async () => {
    if (!editingVehicle?.vin) {
      alert('No VIN available for this vehicle');
      return;
    }

    // Get vehicle make from the editing vehicle data
    const vehicleMake = editingVehicle.make || editingVehicle.Make || '';
    const endpoint = getWindowStickerEndpoint(vehicleMake);
    const brandName = endpoint === 'gm' ? 'GM' : endpoint === 'chrysler' ? 'Chrysler' : endpoint === 'ford' ? 'Ford' : 'Subaru';

    setFetchingWindowSticker(true);
    try {
      const response = await fetch(`/api/window-sticker/${endpoint}/${editingVehicle.vin}`);
      
      if (response.ok) {
        // Create a blob from the response
        const blob = await response.blob();
        
        // Create a File object from the blob to upload as MSRP PDF
        const file = new File([blob], `${brandName}WindowSticker_${editingVehicle.vin}.pdf`, {
          type: 'application/pdf'
        });
        
        // Upload the window sticker as MSRP PDF
        const formData = new FormData();
        formData.append('pdf', file);
        formData.append('vin', editingVehicle.vin.replace(/[^a-zA-Z0-9]/g, '').toUpperCase());
        
        const uploadResponse = await fetch(`/api/dealerships/${settingsDealership._id}/settings/msrp-pdf`, {
          method: 'POST',
          body: formData,
        });
        
        if (uploadResponse.ok) {
          // Refresh the dealership settings to show the new MSRP PDF
          const res = await fetch(`/api/dealerships/${settingsDealership._id}/settings`);
          const settings = await res.json();
          
          // Also refresh the vehicle list from CSV
          let vehicles = [];
          try {
            const csvRes = await fetch(`/api/dealerships/${settingsDealership._id}/csv`);
            if (csvRes.ok) {
              const csvText = await csvRes.text();
              vehicles = csvText ? parseCSV(csvText) : [];
            }
          } catch (e) {
            vehicles = [];
          }
          
          setDealershipSettings({ ...settings, vehicles });
          
          // Update the local MSRP PDF URL state
          const vinSafe = editingVehicle.vin.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          setMsrpPdfUrl(settings?.msrpPdfs?.[vinSafe] || settings?.msrpPdfs?.get?.(vinSafe) || '');
          
          alert(`${brandName} Window Sticker successfully set as MSRP PDF!`);
        } else {
          const uploadError = await uploadResponse.json();
          alert(`Error uploading window sticker: ${uploadError.error || 'Upload failed'}`);
        }
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error || `Failed to fetch ${brandName} window sticker`}`);
      }
    } catch (error) {
      console.error(`${brandName} window sticker fetch error:`, error);
      alert(`Failed to fetch ${brandName} window sticker. Please try again.`);
    } finally {
      setFetchingWindowSticker(false);
    }
  };

  const handleFetchAutoCheck = async () => {
    if (!editingVehicle?.vin) {
      alert('No VIN available for this vehicle');
      return;
    }

    setFetchingAutoCheck(true);
    try {
      const response = await fetch(`/api/autocheck/${editingVehicle.vin}`);
      
      if (response.ok) {
        // Create a blob from the response
        const blob = await response.blob();
        
        // Create a File object from the blob to upload as AutoCheck PDF
        const file = new File([blob], `AutoCheck_${editingVehicle.vin}.pdf`, {
          type: 'application/pdf'
        });
        
        // Upload the AutoCheck report as AutoCheck PDF
        const formData = new FormData();
        formData.append('pdf', file); 
        formData.append('vin', editingVehicle.vin.replace(/[^a-zA-Z0-9]/g, '').toUpperCase());
        
        const uploadResponse = await fetch(`/api/dealerships/${settingsDealership._id}/settings/autocheck-pdf`, {
          method: 'POST',
          body: formData,
        });
        
        if (uploadResponse.ok) {
          // Refresh the dealership settings to show the new AutoCheck PDF
          const res = await fetch(`/api/dealerships/${settingsDealership._id}/settings`);
          const settings = await res.json();
          
          // Also refresh the vehicle list from CSV
          let vehicles = [];
          try {
            const csvRes = await fetch(`/api/dealerships/${settingsDealership._id}/csv`);
            if (csvRes.ok) {
              const csvText = await csvRes.text();
              vehicles = csvText ? parseCSV(csvText) : [];
            }
          } catch (e) {
            vehicles = [];
          }
          
          setDealershipSettings({ ...settings, vehicles });
          
          // Update the local AutoCheck PDF URL state
          const vinSafe = editingVehicle.vin.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          setAutoCheckPdfUrl(settings?.autoCheckPdfs?.[vinSafe] || settings?.autoCheckPdfs?.get?.(vinSafe) || '');
          
          alert('AutoCheck Report successfully fetched and uploaded!');
        } else {
          const uploadError = await uploadResponse.json();
          alert(`Error uploading AutoCheck report: ${uploadError.error || 'Upload failed'}`);
        }
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error || 'Failed to fetch AutoCheck report'}`);
      }
    } catch (error) {
      console.error('AutoCheck fetch error:', error);
      alert('Failed to fetch AutoCheck report. Please try again.');
    } finally {
      setFetchingAutoCheck(false);
    }
  };



  // Reset success message on any edit
  function handleEditChange(fn) {
    return (...args) => {
      setEditSuccess(false);
      fn(...args);
    };
  }

  // File upload functions for PDF uploader
  const handleFileSelection = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
    setUploadMessage('');
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearFiles = () => {
    setSelectedFiles([]);
    setUploadMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleStartUpload = async () => {
    if (selectedFiles.length === 0 || !settingsDealership) return;

    setUploading(true);
    setUploadMessage('Starting upload...');

    try {
      const results = [];
      
      for (const file of selectedFiles) {
        // Extract VIN or Stock Number from filename
        const fileName = file.name.toLowerCase();
        const fileExtension = fileName.split('.').pop();
        
        // Try to find VIN or Stock Number in filename
        let vehicleIdentifier = null;
        let uploadType = null;

        // First, try to extract from filename without extension
        const nameWithoutExt = fileName.replace(/\.[^/.]+$/, ''); // Remove file extension
        
        // Check if the entire filename (without extension) looks like a VIN (17 characters)
        if (nameWithoutExt.length === 17 && /^[a-hj-npr-z0-9]{17}$/i.test(nameWithoutExt)) {
          vehicleIdentifier = nameWithoutExt.toUpperCase();
        } else {
          // Check if filename contains a VIN (17 characters, alphanumeric)
          const vinMatch = fileName.match(/[a-hj-npr-z0-9]{17}/i);
          if (vinMatch) {
            vehicleIdentifier = vinMatch[0].toUpperCase();
          } else {
            // Check for stock number (usually shorter, alphanumeric)
            const stockMatch = fileName.match(/[a-z0-9]{3,15}/i);
            if (stockMatch) {
              vehicleIdentifier = stockMatch[0].toUpperCase();
            }
          }
        }

        if (!vehicleIdentifier) {
          results.push({ file: file.name, success: false, error: 'No VIN or Stock Number found in filename' });
          continue;
        }

        // Debug: Log what we found
        console.log(`File: ${file.name}, Extracted identifier: ${vehicleIdentifier}`);
        
        // Determine upload type based on filename
        if (fileName.includes('inspection') || fileName.includes('172') || fileName.includes('multi')) {
          uploadType = 'inspection';
        } else if (fileName.includes('autocheck') || fileName.includes('carfax')) {
          uploadType = 'autocheck';
        } else if (fileName.includes('msrp') || fileName.includes('window')) {
          uploadType = 'msrp';
        } else if (fileExtension === 'mp4' || fileName.includes('video')) {
          uploadType = 'video';
        } else {
          uploadType = 'inspection'; // Default to inspection
        }

        // Find the vehicle by VIN or Stock Number
        let targetVehicle = null;
        let actualVin = vehicleIdentifier;
        let actualStockNumber = null;
        let uploadedAs = 'vin';
        
        if (dealershipSettings?.vehicles) {
          // First try to find by VIN
          targetVehicle = dealershipSettings.vehicles.find(v => 
            (v.vin || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase() === vehicleIdentifier
          );
          
          // If not found by VIN, try to find by stock number (exact match)
          if (!targetVehicle) {
            targetVehicle = dealershipSettings.vehicles.find(v => 
              (v.stocknumber || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase() === vehicleIdentifier
            );
          }
          
          // If still not found, try partial stock number match
          if (!targetVehicle) {
            targetVehicle = dealershipSettings.vehicles.find(v => {
              const stockClean = (v.stocknumber || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
              return stockClean.includes(vehicleIdentifier) || vehicleIdentifier.includes(stockClean);
            });
          }
          
          // If found by stock number, use that vehicle's VIN
          if (targetVehicle && targetVehicle.vin) {
            actualVin = targetVehicle.vin.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          }
          // Always get the normalized stock number if available
          if (targetVehicle && targetVehicle.stocknumber) {
            actualStockNumber = targetVehicle.stocknumber.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          }
        }

        // Determine if this upload is by stock number or VIN
        if (actualStockNumber && vehicleIdentifier === actualStockNumber) {
          uploadedAs = 'stock';
        }

        if (!targetVehicle) {
          results.push({ file: file.name, success: false, error: `No vehicle found with VIN or Stock Number: ${vehicleIdentifier}` });
          continue;
        }

        // Debug: Log the vehicle we found
        console.log(`Looking for identifier: ${vehicleIdentifier}`);
        console.log(`Found vehicle: ${targetVehicle.year} ${targetVehicle.make} ${targetVehicle.model}, VIN: ${targetVehicle.vin}, Stock: ${targetVehicle.stocknumber}, Using VIN: ${actualVin}, uploadedAs: ${uploadedAs}`);

        // Create FormData and upload
        const formData = new FormData();
        formData.append('pdf', file);
        formData.append('vin', actualVin);
        if (actualStockNumber) formData.append('stockNumber', actualStockNumber);
        formData.append('uploadedAs', uploadedAs);

        let endpoint = '';
        switch (uploadType) {
          case 'inspection':
            endpoint = `/api/dealerships/${settingsDealership._id}/settings/inspection-pdf`;
            break;
          case 'autocheck':
            endpoint = `/api/dealerships/${settingsDealership._id}/settings/autocheck-pdf`;
            break;
          case 'msrp':
            endpoint = `/api/dealerships/${settingsDealership._id}/settings/msrp-pdf`;
            break;
          case 'video':
            endpoint = `/api/dealerships/${settingsDealership._id}/settings/vehicle-video`;
            formData.delete('pdf');
            formData.append('videoUrl', URL.createObjectURL(file));
            break;
        }

        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            body: formData,
          });

          if (response.ok) {
            const result = await response.json();
            results.push({ file: file.name, success: true, vehicleIdentifier, uploadType });
          } else {
            const error = await response.text();
            results.push({ file: file.name, success: false, error: error || 'Upload failed' });
          }
        } catch (error) {
          results.push({ file: file.name, success: false, error: error.message });
        }
      }

      // Show results
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        setUploadMessage(`Successfully uploaded ${successful.length} file(s)! ${failed.length > 0 ? `${failed.length} failed.` : ''}`);
        // If any inspection PDF was uploaded, update the cache-busting URL for that VIN
        for (const r of results) {
          if (r.success && r.uploadType === 'inspection' && r.vehicleIdentifier) {
            const vin = r.vehicleIdentifier;
            const url = `/uploads/inspections/${settingsDealership._id}/${vin}.pdf?cb=${Date.now()}`;
            setInspectionPdfUrl(url);
          }
        }
        // Refresh dealership settings to show updated files
        if (settingsDealership) {
          const res = await fetch(`/api/dealerships/${settingsDealership._id}/settings`);
          const settings = await res.json();
          // Also fetch vehicles to preserve the vehicle list
          let vehicles = [];
          try {
            const csvRes = await fetch(`/api/dealerships/${settingsDealership._id}/csv`);
            if (csvRes.ok) {
              const csvText = await csvRes.text();
              vehicles = csvText ? parseCSV(csvText) : [];
            }
          } catch (e) {
            vehicles = [];
          }
          setDealershipSettings({ ...settings, vehicles });
        }
      } else {
        setUploadMessage(`Upload failed: ${failed.map(f => f.error).join(', ')}`);
      }

      // Clear files on success
      if (successful.length > 0) {
        handleClearFiles();
      }

    } catch (error) {
      setUploadMessage(`Upload error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Function to fetch session activity for a customer
  const fetchSessionActivity = async (customerEmail, dealershipId, specificLink = null) => {
    setSessionActivityLoading(true);
    try {
      if (!customerEmail) {
        console.error('No customer email provided');
        setSessionActivityData([]);
        return;
      }
      
      console.log('Fetching session activity for:', customerEmail, 'dealership:', dealershipId, 'link:', specificLink);
      let url = `/api/dealerships/${dealershipId}/session-activity/${encodeURIComponent(customerEmail)}`;
      const params = new URLSearchParams();
      if (specificLink) {
        params.append('link', specificLink);
      }
      // Add timestamp to prevent caching
      params.append('_t', Date.now().toString());
      url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Session activity data:', data);
        setSessionActivityData(data);
      } else {
        console.error('Failed to fetch session activity:', response.status);
        setSessionActivityData([]);
      }
    } catch (error) {
      console.error('Error fetching session activity:', error);
      setSessionActivityData([]);
    } finally {
      setSessionActivityLoading(false);
    }
  };

  const fetchCustomerLogs = async (logId) => {
    setCustomerLogsLoading(true);
    try {
      if (!logId) {
        console.error('No log ID provided');
        return;
      }
      
      console.log('Fetching customer logs for ID:', logId);
      let url = `/api/dealerships/api/packet-logs/${logId}`;
      const params = new URLSearchParams();
      // Add timestamp to prevent caching
      params.append('_t', Date.now().toString());
      url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Customer logs data:', data);
        setCustomerLogsLog(data);
        // Reset the active tab to ensure we show the first available tab
        setActiveLogTab(null);
      } else {
        console.error('Failed to fetch customer logs:', response.status);
      }
    } catch (error) {
      console.error('Error fetching customer logs:', error);
    } finally {
      setCustomerLogsLoading(false);
    }
  };

  // Function to open session activity modal
  const openSessionActivityModal = (customerEmail, dealershipId, specificLink = null) => {
    console.log('openSessionActivityModal called with:', { customerEmail, dealershipId, specificLink });
    setSessionActivityCustomer(customerEmail);
    setSessionActivityLink(specificLink);
    setSessionActivityDealershipId(dealershipId);
    setIsClickStreamMode(false); // Set to session activity mode
    setShowSessionActivityModal(true);
    fetchSessionActivity(customerEmail, dealershipId, specificLink);
  };

  // Function to open click stream log modal (reuse session activity modal with different data)
  const openClickStreamLogModal = (customerEmail, dealershipId, specificLink = null) => {
    console.log('openClickStreamLogModal called with:', { customerEmail, dealershipId, specificLink });
    
    // Set initial state
    setSessionActivityLoading(true);
    setSessionActivityCustomer(customerEmail);
    setSessionActivityLink(specificLink || '');
    setSessionActivityDealershipId(dealershipId);
    setIsClickStreamMode(true); // Set to click stream mode
    setShowSessionActivityModal(true);
    
    // Fetch click stream data and show in session activity modal
    fetch(`/api/dealerships/${dealershipId}/click-stream-log/${encodeURIComponent(customerEmail)}?link=${encodeURIComponent(specificLink || '')}&_t=${Date.now()}`)
      .then(res => res.json())
      .then(data => {
        console.log('Click stream data:', data);
        setSessionActivityData(data);
        setSessionActivityLoading(false);
      })
      .catch(error => {
        console.error('Error fetching click stream log:', error);
        setSessionActivityData([]);
        setSessionActivityLoading(false);
      });
  };



  // Fetch the current watched path when opening the modal or changing dealership
  useEffect(() => {
    if (showInventory && selectedDealership) {
      fetch(`/api/dealerships/${selectedDealership._id}/csv-watch-path`)
        .then(res => res.json())
        .then(data => setWatchingCsvPath(data.path || ''));
    }
  }, [showInventory, selectedDealership]);

  // Owner Settings Functions
  const fetchAdminUsers = async () => {
    try {
      const res = await fetch('/api/dealerships/admin/users');
      if (res.ok) {
        const users = await res.json();
        setAdminUsers(users);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setAdminLoading(true);
    setAdminMessage('');

    try {
      // Find the selected dealership to get its name
      const selectedDealership = dealerships.find(d => d._id === newAdmin.dealershipId);
      
      const res = await fetch('/api/dealerships/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newAdmin,
          dealershipName: selectedDealership?.name || 'Unknown Dealership'
        }),
      });

      if (res.ok) {
        const createdUser = await res.json();
        
        // Only add to adminUsers if it's actually an ADMIN role
        // SALES role users are now created as SalesTeamMembers and will appear in the sales team section
        if (createdUser.role === 'ADMIN') {
          setAdminUsers([...adminUsers, createdUser]);
        }
        
        setNewAdmin({ name: '', email: '', password: '', dealershipId: '', role: 'ADMIN' });
        setAdminMessage('User created successfully!');
        setTimeout(() => setAdminMessage(''), 3000);
        
        // If it was a sales user, refresh the dealership salespeople data
        if (createdUser.role === 'SALES') {
          fetchAllDealershipSalespeople();
        }
      } else {
        const error = await res.json();
        setAdminMessage(`Error: ${error.message}`);
      }
    } catch (error) {
      setAdminMessage('Error creating user');
    }

    setAdminLoading(false);
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const res = await fetch(`/api/dealerships/admin/users/${adminId}`, {
        method: 'DELETE',
      });

      if (res.ok) {
        setAdminUsers(adminUsers.filter(admin => admin._id !== adminId));
        setAdminMessage('User deleted successfully!');
        setTimeout(() => setAdminMessage(''), 3000);
      } else {
        const error = await res.json();
        setAdminMessage(`Error: ${error.message}`);
      }
    } catch (error) {
      setAdminMessage('Error deleting user');
    }
  };

  // Handle owner email notifications toggle
  const handleToggleOwnerNotifications = (currentValue) => {
    const newValue = !currentValue;
    localStorage.setItem('ownerEmailNotifications', newValue.toString());
    setAdminMessage(newValue ? 'Owner email notifications enabled!' : 'Owner email notifications disabled!');
    setTimeout(() => setAdminMessage(''), 3000);
    // Force a re-render to update the checkbox state
    setNow(Date.now());
  };

  // Fetch all salespeople for all dealerships
  const fetchAllDealershipSalespeople = async () => {
    try {
      const dealershipData = [];
      
      for (const dealership of dealerships) {
        // Fetch sales team for this dealership
        const salesResponse = await fetch(`/api/dealerships/${dealership._id}/sales-team`);
        const salesTeam = salesResponse.ok ? await salesResponse.json() : [];
        
        // Get admin users for this dealership (only ADMIN role, not SALES)
        const dealershipAdmins = adminUsers.filter(admin => 
          admin.dealership?._id === dealership._id && admin.role === 'ADMIN'
        );
        
        dealershipData.push({
          dealership,
          salesTeam,
          admins: dealershipAdmins
        });
      }
      
      setDealershipSalespeople(dealershipData);
    } catch (error) {
      console.error('Error fetching dealership salespeople:', error);
    }
  };

  // Delete admin from view salesperson section
  const handleDeleteAdminFromView = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const res = await fetch(`/api/dealerships/admin/users/${adminId}`, {
        method: 'DELETE',
      });

      if (res.ok) {
        // Update adminUsers state (remove from admin users)
        setAdminUsers(adminUsers.filter(admin => admin._id !== adminId));
        
        // Refresh dealership salespeople data to update both admin and sales team lists
        fetchAllDealershipSalespeople();
        
        setAdminMessage('User deleted successfully!');
        setTimeout(() => setAdminMessage(''), 3000);
      } else {
        const error = await res.json();
        setAdminMessage(`Error: ${error.message}`);
      }
    } catch (error) {
      setAdminMessage('Error deleting user');
    }
  };

  // Delete salesperson from view salesperson section
  const handleDeleteSalespersonFromView = async (dealershipId, salespersonId) => {
    if (!window.confirm('Are you sure you want to delete this salesperson?')) return;

    try {
      const response = await fetch(`/api/dealerships/${dealershipId}/sales-team/${salespersonId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        // Update dealershipSalespeople state
        setDealershipSalespeople(dealershipSalespeople.map(dealershipData => ({
          ...dealershipData,
          salesTeam: dealershipData.salesTeam.filter(member => member._id !== salespersonId)
        })));
        
        setAdminMessage('Salesperson deleted successfully!');
        setTimeout(() => setAdminMessage(''), 3000);
      } else {
        const error = await response.json();
        setAdminMessage(`Error: ${error.message || 'Failed to delete salesperson'}`);
      }
    } catch (error) {
      console.error('Error deleting salesperson:', error);
      setAdminMessage('Error deleting salesperson');
    }
  };

  // Activity Analytics Functions
  const fetchActivityLogs = async (dealershipId, filterOverride = null) => {
    setActivityLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '100',
        page: '1'
      });
      
      const currentFilter = filterOverride || activityFilter;
      if (currentFilter !== 'all') {
        params.append('actionType', currentFilter);
      }

      const response = await fetch(`/api/dealerships/${dealershipId}/activity-logs?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setActivityLogs(data.logs || []);
      } else {
        const errorData = await response.text();
        console.error('Error fetching activity logs:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchActivityStats = async (dealershipId) => {
    try {
      const response = await fetch(`/api/dealerships/${dealershipId}/activity-stats?days=${activityPeriod}`);
      
      if (response.ok) {
        const data = await response.json();
        setActivityStats(data);
      } else {
        const errorData = await response.text();
        console.error('Error fetching activity stats:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error fetching activity stats:', error);
    }
  };

  // Load activity data when analytics section is opened
  const handleShowActivityAnalytics = () => {
    setShowActivityAnalytics(true);
    if (salesTeamDealership) {
      fetchActivityLogs(salesTeamDealership._id);
      fetchActivityStats(salesTeamDealership._id);
    }
  };

  // Reload activity data when filter changes
  const handleActivityFilterChange = (newFilter) => {
    setActivityFilter(newFilter);
    if (salesTeamDealership) {
      fetchActivityLogs(salesTeamDealership._id, newFilter);
    }
  };

  // Reload activity data when period changes
  const handleActivityPeriodChange = (newPeriod) => {
    setActivityPeriod(newPeriod);
    if (salesTeamDealership) {
      fetchActivityStats(salesTeamDealership._id);
    }
  };

  // Handle clearing all logs for a specific filter
  const handleClearAllLogs = async (filter) => {
    if (!salesTeamDealership) return;
    
    const filterText = filter === 'all' ? 'all activity logs' : `${filter} logs`;
    const confirmMessage = `Are you sure you want to clear ${filterText}? This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') {
        params.append('actionType', filter);
      }
      
      const response = await fetch(`/api/dealerships/${salesTeamDealership._id}/activity-logs?${params}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`Successfully cleared ${result.deletedCount} ${filterText}`);
        
        // Refresh the data
        fetchActivityLogs(salesTeamDealership._id);
        fetchActivityStats(salesTeamDealership._id);
      } else {
        const error = await response.json();
        alert(`Error clearing logs: ${error.message}`);
      }
    } catch (error) {
      console.error('Error clearing logs:', error);
      alert('Error clearing logs. Please try again.');
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get action type badge color
  const getActionTypeBadgeColor = (actionType) => {
    switch (actionType) {
      case 'create': return 'bg-green-100 text-green-800';
      case 'update': return 'bg-blue-100 text-blue-800';
      case 'delete': return 'bg-red-100 text-red-800';
      case 'view': return 'bg-gray-100 text-gray-800';
      case 'login': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Sales Team Management Functions
  const handleAddSalesTeamMember = async (e) => {
    e.preventDefault();
    if (!salesTeamDealership) {
      setSalesTeamMessage('Error: No dealership selected');
      return;
    }

    if (salesTeamForm.password.length < 8) {
      setSalesTeamMessage('Password must be at least 8 characters long');
      return;
    }

    setIsAddingSalesTeamMember(true);
    try {
      const response = await fetch(`/api/dealerships/${salesTeamDealership._id}/sales-team`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(salesTeamForm),
      });
      
      if (response.ok) {
        const newMember = await response.json();
        setSalesTeamMembers([...salesTeamMembers, newMember]);
        setSalesTeamForm({ name: '', email: '', password: '' });
        setSalesTeamMessage('Sales team member added successfully');
      } else {
        const error = await response.json();
        setSalesTeamMessage(error.message || 'Failed to add sales team member');
      }
    } catch (error) {
      console.error('Error adding sales team member:', error);
      setSalesTeamMessage('Error adding sales team member');
    } finally {
      setIsAddingSalesTeamMember(false);
    }
  };

  const handleDeleteSalesTeamMember = async (memberId) => {
    try {
      const response = await fetch(`/api/dealerships/${salesTeamDealership._id}/sales-team/${memberId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        setSalesTeamMembers(salesTeamMembers.filter(member => member._id !== memberId));
        setSalesTeamMessage('Sales team member removed successfully');
      } else {
        setSalesTeamMessage('Failed to remove sales team member');
      }
    } catch (error) {
      console.error('Error removing sales team member:', error);
      setSalesTeamMessage('Error removing sales team member');
    }
  };

  const fetchSalesTeam = async (dealershipId) => {
    try {
      const response = await fetch(`/api/dealerships/${dealershipId}/sales-team`);
      if (response.ok) {
        const data = await response.json();
        setSalesTeamMembers(data);
      } else {
        setSalesTeamMessage('Failed to fetch sales team');
      }
    } catch (error) {
      console.error('Error fetching sales team:', error);
      setSalesTeamMessage('Error fetching sales team');
    }
  };

  // Toggle email notifications for sales team member
  const handleToggleSalesTeamNotifications = async (memberId, currentStatus, dealershipId = null) => {
    // Use the provided dealershipId or fall back to salesTeamDealership
    const targetDealershipId = dealershipId || (salesTeamDealership ? salesTeamDealership._id : null);
    
    if (!targetDealershipId) {
      console.error('No dealership ID available for sales team notification toggle');
      return;
    }

    try {
      const response = await fetch(`/api/dealerships/${targetDealershipId}/sales-team/${memberId}/email-notifications`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emailNotifications: !currentStatus }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update the local state in salesTeamMembers if available
        if (salesTeamMembers && salesTeamMembers.length > 0) {
          setSalesTeamMembers(salesTeamMembers.map(member => 
            member._id === memberId 
              ? { ...member, emailNotifications: !currentStatus }
              : member
          ));
        }
        
        // Update the dealershipSalespeople state if available
        if (dealershipSalespeople && dealershipSalespeople.length > 0) {
          setDealershipSalespeople(dealershipSalespeople.map(dealershipData => ({
            ...dealershipData,
            salesTeam: dealershipData.salesTeam.map(member => 
              member._id === memberId 
                ? { ...member, emailNotifications: !currentStatus }
                : member
            )
          })));
        }
        
        // Show success message
        const message = result.message || `Email notifications ${!currentStatus ? 'enabled' : 'disabled'}`;
        if (setSalesTeamMessage) {
          setSalesTeamMessage(message);
          setTimeout(() => setSalesTeamMessage(''), 3000);
        }
        if (setAdminMessage) {
          setAdminMessage(message);
          setTimeout(() => setAdminMessage(''), 3000);
        }
      } else {
        const error = await response.json();
        const errorMessage = `Error: ${error.message}`;
        if (setSalesTeamMessage) setSalesTeamMessage(errorMessage);
        if (setAdminMessage) setAdminMessage(errorMessage);
      }
    } catch (error) {
      console.error('Error toggling email notifications:', error);
      const errorMessage = 'Error updating email notifications';
      if (setSalesTeamMessage) setSalesTeamMessage(errorMessage);
      if (setAdminMessage) setAdminMessage(errorMessage);
    }
  };

  // Toggle email notifications for admin user
  const handleToggleAdminNotifications = async (adminId, currentStatus) => {
    try {
      const response = await fetch(`/api/dealerships/admin/${adminId}/email-notifications`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emailNotifications: !currentStatus }),
      });

      if (response.ok) {
        const result = await response.json();
        // Update the local state
        setAdminUsers(adminUsers.map(admin => 
          admin._id === adminId 
            ? { ...admin, emailNotifications: !currentStatus }
            : admin
        ));
        
        // Also update dealershipSalespeople if it contains this admin
        setDealershipSalespeople(dealershipSalespeople.map(dealershipData => ({
          ...dealershipData,
          admins: dealershipData.admins.map(admin => 
            admin._id === adminId 
              ? { ...admin, emailNotifications: !currentStatus }
              : admin
          )
        })));
        
        setAdminMessage(result.message);
        setTimeout(() => setAdminMessage(''), 3000);
      } else {
        const error = await response.json();
        setAdminMessage(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error toggling admin email notifications:', error);
      setAdminMessage('Error updating email notifications');
    }
  };

  // Check if user is owner on mount
  useEffect(() => {
    const userRole = localStorage.getItem('adminRole');
    setIsOwner(userRole === 'OWNER');
  }, []);

  // Check if user is sales team member
  const isSalesTeamMember = localStorage.getItem('adminRole') === 'SALES';

  // Fetch admin users when owner settings modal opens
  useEffect(() => {
    if (showOwnerSettingsModal && isOwner) {
      fetchAdminUsers();
    }
  }, [showOwnerSettingsModal, isOwner]);

  // Fetch dealership salespeople when admin users or dealerships change
  useEffect(() => {
    if (showOwnerSettingsModal && isOwner && adminUsers.length > 0 && dealerships.length > 0) {
      fetchAllDealershipSalespeople();
    }
  }, [showOwnerSettingsModal, isOwner, adminUsers, dealerships]);

  // Fetch URL activities when owner settings modal opens
  useEffect(() => {
    if (showOwnerSettingsModal && isOwner) {
      fetchUrlActivities();
    }
  }, [showOwnerSettingsModal, isOwner]);

  // Customer search functionality
  useEffect(() => {
    if (!customerSearch.trim() || !packetDealership) {
      setCustomerResults([]);
      setCustomerSearchLoading(false);
      return;
    }
    
    setCustomerSearchLoading(true);
    const searchTerm = customerSearch.toLowerCase();
    
    const searchCustomers = async () => {
      try {
        // Get user info for role-based filtering
        const userRole = localStorage.getItem('adminRole');
        const userEmail = localStorage.getItem('adminEmail');
        const userId = localStorage.getItem('adminId');
        const userName = localStorage.getItem('adminName');
        
        const queryParams = new URLSearchParams({
          userRole: userRole || '',
          userEmail: userEmail || '',
          userId: userId || '',
          userName: userName || 'User'
        });
        
        // Search both customers and leads
        const [customersRes, leadsRes] = await Promise.all([
          fetch(`/api/dealerships/${packetDealership._id}/customers?${queryParams}`),
          fetch(`/api/dealerships/${packetDealership._id}/leads?${queryParams}`)
        ]);
        
        const customers = customersRes.ok ? await customersRes.json() : [];
        const leads = leadsRes.ok ? await leadsRes.json() : [];
        
        // Filter customers with intelligent search logic
        const looksLikeEmail = searchTerm.includes('@') && !searchTerm.includes(' ');
        
        const filteredCustomers = customers.filter(c => {
          if (looksLikeEmail) {
            // If it looks like an email, only search email field
            return c.email && c.email.toLowerCase().includes(searchTerm);
          } else {
            // Otherwise, focus on name searching (first name, last name, full names)
            const firstNameMatch = c.firstName && c.firstName.toLowerCase().includes(searchTerm);
            const lastNameMatch = c.lastName && c.lastName.toLowerCase().includes(searchTerm);
            
            // Full name search (first + last)
            const fullName = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase().trim();
            const fullNameMatch = fullName.includes(searchTerm);
            
            // Reverse full name search (last + first) for searches like "Smith John"
            const reverseFullName = `${c.lastName || ''} ${c.firstName || ''}`.toLowerCase().trim();
            const reverseFullNameMatch = reverseFullName.includes(searchTerm);
            
            // Also include phone search as secondary
            const phoneMatch = c.phone && c.phone.includes(searchTerm);
            
            return firstNameMatch || lastNameMatch || fullNameMatch || reverseFullNameMatch || phoneMatch;
          }
        });
        
        // Filter leads with intelligent search logic and add leadId for identification
        const filteredLeads = leads.filter(l => {
          if (looksLikeEmail) {
            // If it looks like an email, only search email field
            return l.email && l.email.toLowerCase().includes(searchTerm);
          } else {
            // Otherwise, focus on name searching (first name, last name, full names)
            const firstNameMatch = l.firstName && l.firstName.toLowerCase().includes(searchTerm);
            const lastNameMatch = l.lastName && l.lastName.toLowerCase().includes(searchTerm);
            
            // Full name search (first + last)
            const fullName = `${l.firstName || ''} ${l.lastName || ''}`.toLowerCase().trim();
            const fullNameMatch = fullName.includes(searchTerm);
            
            // Reverse full name search (last + first) for searches like "Smith John"
            const reverseFullName = `${l.lastName || ''} ${l.firstName || ''}`.toLowerCase().trim();
            const reverseFullNameMatch = reverseFullName.includes(searchTerm);
            
            // Also include phone search as secondary
            const phoneMatch = l.phone && l.phone.includes(searchTerm);
            
            return firstNameMatch || lastNameMatch || fullNameMatch || reverseFullNameMatch || phoneMatch;
          }
        }).map(l => ({ ...l, leadId: l._id }));
        
        setCustomerResults([...filteredCustomers, ...filteredLeads]);
      } catch (error) {
        console.error('Error searching customers:', error);
        setCustomerResults([]);
      } finally {
        setCustomerSearchLoading(false);
      }
    };
    
    const timeoutId = setTimeout(searchCustomers, 300);
    return () => clearTimeout(timeoutId);
  }, [customerSearch, packetDealership]);

  // Fetch sales team when sales team modal opens
  useEffect(() => {
    if (showSalesTeamModal && salesTeamDealership) {
      fetchSalesTeam(salesTeamDealership._id);
    }
  }, [showSalesTeamModal, salesTeamDealership]);



  // Fetch URL activities
  const fetchUrlActivities = async () => {
    setUrlActivitiesLoading(true);
    try {
      const response = await fetch('/api/dealerships/api/url-activity?limit=50');
      if (response.ok) {
        const activities = await response.json();
        setUrlActivities(activities);
      } else {
        console.error('Failed to fetch URL activities');
      }
    } catch (error) {
      console.error('Error fetching URL activities:', error);
    } finally {
      setUrlActivitiesLoading(false);
    }
  };

  // Session timeout warning handlers
  const handleStayLoggedIn = () => {
    setShowTimeoutWarning(false);
  };

  const handleLogoutNow = () => {
    sessionManager.logout();
  };

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown && !event.target.closest('.filter-dropdown')) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterDropdown]);

  // Password reset handlers
  const handlePasswordReset = (user, userType) => {
    setPasswordResetUser({ ...user, userType });
    setNewPassword('');
    setShowPasswordResetModal(true);
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    setPasswordResetLoading(true);
    
    try {
      const { userType } = passwordResetUser;
      let endpoint;
      
      if (userType === 'admin') {
        endpoint = `/api/dealerships/admin/users/${passwordResetUser._id}/custom-password`;
      } else if (userType === 'sales') {
        endpoint = `/api/dealerships/${passwordResetUser.dealership}/sales-team/${passwordResetUser._id}/custom-password`;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: newPassword }),
      });
      
      if (response.ok) {
        const result = await response.json();
        setAdminMessage(`Password updated successfully for ${passwordResetUser.name}`);
        setShowPasswordResetModal(false);
        setPasswordResetUser(null);
        setNewPassword('');
        setTimeout(() => setAdminMessage(''), 3000);
      } else {
        const error = await response.json();
        setAdminMessage(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setAdminMessage('Error resetting password');
    } finally {
      setPasswordResetLoading(false);
    }
  };

  return (
    <div className="admin-dashboard min-h-screen relative overflow-hidden">
      {/* Animated Space Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{
          background: 'linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #533483 100%)'
        }}
      />
      
      {/* Floating particles overlay */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-purple-400 rounded-full animate-ping"></div>
        <div className="absolute top-3/4 right-1/4 w-0.5 h-0.5 bg-blue-400 rounded-full animate-pulse"></div>
        <div className="absolute top-1/2 left-1/6 w-1 h-1 bg-cyan-400 rounded-full animate-bounce"></div>
        <div className="absolute top-1/6 right-1/3 w-0.5 h-0.5 bg-purple-300 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-1/4 left-1/3 w-1 h-1 bg-indigo-400 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen text-white p-2 sm:p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Gradient Header Bar */}
        <div className="w-full h-2 rounded-t-2xl mb-2" style={{background: 'linear-gradient(90deg, #1e293b 0%, #6366f1 100%)'}} />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4 sm:mb-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-white tracking-tight drop-shadow-lg break-words">
              {isSalesTeamMember ? 'Sales Dashboard' : 'Admin Dashboard'} <span className="text-xs bg-green-700 text-white px-2 py-1 rounded ml-2">{isOwner ? 'OWNER' : isSalesTeamMember ? 'SALES' : 'ADMIN'}</span>
            </h1>
            
            {/* User Profile Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/20">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    {localStorage.getItem('adminName') ? localStorage.getItem('adminName').charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                <div className="text-white">
                  <div className="text-sm font-semibold">
                    {localStorage.getItem('adminName') || 'User'}
                  </div>
                  <div className="text-xs opacity-75">
                    {localStorage.getItem('adminEmail') || 'No email'}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            {isOwner && (
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowOwnerSettingsModal(true);
                  fetchAdminUsers();
                }}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-lg font-medium shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
              >
                Owner Settings
              </button>
            )}
            {!isSalesTeamMember && (
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Get the dealership ID from localStorage that we set during login
                  const dealershipId = localStorage.getItem('adminDealershipId');
                  const dealership = dealerships.find(d => d._id === dealershipId);
                  if (dealership) {
                    openSalesTeamModal(dealership);
                  } else {
                    alert('Please select a dealership first');
                  }
                }}
                className="bg-white text-black px-4 py-2 rounded-lg font-semibold shadow hover:bg-gray-200 hover:scale-105 hover:shadow-lg transition-transform duration-150 text-sm sm:text-base"
              >
                Settings
              </button>
            )}
            <button
              onClick={() => {
                sessionManager.logout();
              }}
              className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold shadow hover:bg-red-700 hover:scale-105 hover:shadow-lg transition-transform duration-150 text-sm sm:text-base"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="dashboard-cards grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 flex flex-col items-center">
            <div className="text-4xl font-bold text-black mb-2">{dealerships.length}</div>
            <div className="text-gray-700 font-semibold">Active Dealerships</div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 flex flex-col items-center">
            <div className="text-4xl font-bold text-black mb-2">{dealerships.reduce((sum, d) => sum + d.vehicles, 0)}</div>
            <div className="text-gray-700 font-semibold">Total Vehicles</div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 flex flex-col items-center">
            <div className="text-4xl font-bold text-black mb-2">100%</div>
            <div className="text-gray-700 font-semibold">Uptime</div>
          </div>
        </div>

        {/* Divider */}
        <div className="w-full h-0.5 bg-gradient-to-r from-gray-700 via-gray-400 to-gray-700 opacity-30 rounded mb-8" />

        {/* Dealerships Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0 flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-white">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m2.25-18v18m13.5-18v18m2.25-18v18M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.75m-.75 3h.75m-.75 3h.75m-3.75-16.5h.75m-.75 3h.75m-.75 3h.75m-3.75-6h.75m-.75 3h.75" />
                </svg>
              </div>
            Active Dealerships
            </h2>

          </div>

          {/* Add Dealership Form - Only visible to owners */}
          {isOwner && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-6">
              <form onSubmit={async (e) => {
                e.preventDefault();
                const res = await fetch('/api/dealerships', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(newDealership),
                });
                if (res.ok) {
                  const added = await res.json();
                  setDealerships([...dealerships, added]);
                  setNewDealership({ name: '', email: '' });
                } else {
                  alert('Failed to add dealership');
                }
              }}>
                <div className="flex flex-col sm:flex-row gap-3 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dealership Name</label>
                    <input
                      type="text"
                      placeholder="Enter dealership name"
                      value={newDealership.name}
                      onChange={e => setNewDealership({ ...newDealership, name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      placeholder="Enter email address"
                      value={newDealership.email}
                      onChange={e => setNewDealership({ ...newDealership, email: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-2 rounded-lg font-semibold shadow-lg transition-all duration-200 hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add Dealership
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Dealerships Grid */}
          <div className="space-y-4">
            {dealerships.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m2.25-18v18m13.5-18v18m2.25-18v18M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.75m-.75 3h.75m-.75 3h.75m-3.75-16.5h.75m-.75 3h.75m-.75 3h.75m-3.75-6h.75m-.75 3h.75" />
                  </svg>
                          </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Dealerships Yet</h3>
                <p className="text-gray-500">Add your first dealership using the form above.</p>
                        </div>
            ) : (
              dealerships.map((d, idx) => (
                <div key={d._id} className="bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 hover:border-gray-300">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    {/* Dealership Info */}
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-lg">{d.name.charAt(0).toUpperCase()}</span>
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{d.name}</h3>
                            <a href={`mailto:${d.email}`} className="text-blue-600 hover:text-blue-800 transition-colors">
                              {d.email}
                            </a>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-900">{d.vehicles}</div>
                            <div className="text-sm text-gray-500">Vehicles</div>
                          </div>
                          <div className="text-center">
                            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              d.connected 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              <div className={`w-2 h-2 rounded-full mr-2 ${
                                d.connected ? 'bg-green-500' : 'bg-gray-400'
                              }`}></div>
                              {d.connected ? 'Connected' : 'Offline'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3 min-w-0 lg:min-w-[300px]">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            <button
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg font-medium shadow-sm transition-all duration-200 hover:shadow-md text-sm whitespace-nowrap"
                              onClick={() => openLeadsModal(d)}
                            >
                              View Customer Leads
                            </button>
                            <button
                          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg font-medium shadow-sm transition-all duration-200 hover:shadow-md text-sm whitespace-nowrap"
                              onClick={() => openInventory(d)}
                            >
                              CSV File Management
                            </button>
                            <button
                          className={`px-3 py-2 rounded-lg font-medium shadow-sm transition-all duration-200 text-sm whitespace-nowrap ${
                            d.vehicles > 0 
                              ? 'bg-purple-600 hover:bg-purple-700 text-white hover:shadow-md cursor-pointer' 
                              : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                          }`}
                              onClick={() => d.vehicles > 0 && openPacketModal(d)}
                              disabled={d.vehicles === 0}
                              title={d.vehicles === 0 ? 'No vehicles available - please upload a CSV file first' : 'Send Packet'}
                            >
                              Send Packet {d.vehicles === 0 && '(No Vehicles)'}
                            </button>
                          </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                            <button
                          className={`px-3 py-2 rounded-lg font-medium shadow-sm transition-all duration-200 text-sm min-h-[40px] ${
                            d.vehicles > 0 
                              ? 'bg-gray-600 hover:bg-gray-700 text-white hover:shadow-md cursor-pointer' 
                              : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                          }`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (d.vehicles > 0) {
                                  openSettingsModal(d);
                                }
                              }}
                              disabled={d.vehicles === 0}
                              title={d.vehicles === 0 ? 'No vehicles available - please upload a CSV file first' : 'Settings'}
                            >
                              Settings {d.vehicles === 0 && '(No Vehicles)'}
                            </button>
                            <button
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg font-medium shadow-sm transition-all duration-200 hover:shadow-md text-sm min-h-[40px]"
                              onClick={() => {
                                // Use dealership name instead of ID, take only first word if there are spaces
                                const dealershipName = d.name ? d.name.split(' ')[0].toLowerCase() : d._id;
                                window.location.href = `/trade-in/${dealershipName}`;
                              }}
                            >
                              Trade-In
                            </button>
                            <button
                          className="bg-orange-600 hover:bg-orange-700 text-white px-2 py-2 rounded-lg font-medium shadow-sm transition-all duration-200 hover:shadow-md text-xs min-h-[40px] leading-tight"
                              onClick={() => setShowWindowStickerModal(true)}
                            >
                              Window Sticker
                            </button>
                            <button
                          className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-lg font-medium shadow-sm transition-all duration-200 hover:shadow-md text-sm hidden"
                              onClick={() => { setSelectedDealershipForLogs(d); setShowLogsModal(true); }}
                            >
                              View Customer Logs
                            </button>
                            <button
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg font-medium shadow-sm transition-all duration-200 hover:shadow-md text-sm hidden"
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete dealership "${d.name}"?`)) {
                                  await fetch(`/api/dealerships/${d._id}`, { method: 'DELETE' });
                                  setDealerships(dealerships.filter(x => x._id !== d._id));
                                }
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Packet Logs Section */}
        {showLogsModal && selectedDealershipForLogs && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-2 sm:p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-3 sm:p-6 w-full max-w-full sm:max-w-[95vw] lg:max-w-[90vw] xl:max-w-[85vw] relative overflow-y-auto max-h-screen sm:max-h-[95vh]">
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-3xl sm:text-2xl font-bold p-2 sm:p-1"
                onClick={() => setShowLogsModal(false)}
                style={{ minWidth: 44, minHeight: 44 }}
              >
                &times;
              </button>
              <h2 className="text-lg sm:text-xl font-bold text-black mb-4 pr-12 sm:pr-8 flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="break-words">Vehicle Packet Link Logs for</span>
                <span className="font-bold text-blue-600">{selectedDealershipForLogs.name}</span>
                <button
                  onClick={refreshLogs}
                  className="ml-0 sm:ml-2 p-1 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-black shadow transition w-8 h-8 flex items-center justify-center"
                  title="Refresh Logs"
                  aria-label="Refresh Logs"
                  type="button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 104.582 9m15.356 2V9m0 0h-3" />
                  </svg>
                </button>
              </h2>
              {/* Log Search Bar */}
              <div className="mb-4">
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-sm sm:text-base text-black"
                  placeholder="Search by customer name, email, or phone..."
                  value={logSearch}
                  onChange={e => setLogSearch(e.target.value)}
                />
              </div>
              {logsLoading ? (
                <div className="text-gray-500">Loading logs...</div>
              ) : (
                <div className="overflow-x-auto max-h-[70vh] sm:max-h-[65vh] lg:max-h-[70vh]">
                  {/* Mobile Card View */}
                  <div className="block sm:hidden space-y-3">
                    {filteredPacketLogs.length === 0 ? (
                      <div className="text-gray-500 text-center py-4">No logs found.</div>
                    ) : (
                      filteredPacketLogs.map(log => {
                        // Check if customer has submitted on this log - check multiple possible fields
                        const submittedOptions = log.submittedOptions || log.submitted_options || log.options;
                        const hasSubmitted = submittedOptions && Object.keys(submittedOptions).length > 0;
                        
                        return (
                          <div key={log._id} className={`rounded-lg p-3 border space-y-2 ${
                            hasSubmitted 
                              ? 'bg-green-50 border-green-200 shadow-sm' 
                              : 'bg-gray-50 border-gray-200'
                          }`}>
                            {hasSubmitted && (
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-green-700 font-semibold text-xs">Customer Submitted Information</span>
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <div className="font-semibold text-black text-sm">
                                {log.customer?.firstName} {log.customer?.lastName}
                              </div>
                              <div className="text-xs">
                                {log.lastActiveAt && (Date.now() - new Date(log.lastActiveAt).getTime() < 10 * 1000) ? 
                                  <span className="text-green-600 font-bold">● Active now</span> : 
                                  <span className="text-gray-400">Offline</span>
                                }
                              </div>
                            </div>
                          <div className="text-xs text-gray-600 break-words">
                            <strong>Email:</strong> {log.customer?.email}
                          </div>
                          <div className="text-xs text-gray-600 break-all">
                            <strong>Link:</strong> <a href={log.link} className="text-blue-700 underline" target="_blank" rel="noopener noreferrer">{log.link}</a>
                          </div>
                          <div className="text-xs text-gray-600 break-words">
                            <strong>Vehicle:</strong> {log.vehicle?.year} {log.vehicle?.make} {log.vehicle?.model}
                          </div>
                          <div className="text-xs text-gray-600">
                            <strong>Created:</strong> {log.createdAt ? new Date(log.createdAt).toLocaleString() : ''}
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-xs">
                              <strong>Opened:</strong> {log.opened ? 
                                <span className="text-green-700 font-bold">Yes</span> : 
                                <span className="text-gray-500">No</span>
                              }
                            </div>
                            {log.openedAt && (
                              <div className="text-xs text-gray-500">
                                <strong>Last Opened:</strong> {new Date(log.openedAt).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })
                    )}
                  </div>
                  
                  {/* Desktop Table View */}
                  <table className="hidden sm:table min-w-full border rounded-xl overflow-hidden text-sm table-fixed">
                    <colgroup>
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '25%' }} />
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <thead>
                      <tr className="bg-gradient-to-r from-gray-50 to-gray-100 text-black">
                        <th className="px-3 py-3 text-left font-semibold">Customer</th>
                        <th className="px-3 py-3 text-left font-semibold">Active</th>
                        <th className="px-3 py-3 text-left font-semibold">Email</th>
                        <th className="px-3 py-3 text-left font-semibold">Link</th>
                        <th className="px-3 py-3 text-left font-semibold">Vehicle</th>
                        <th className="px-3 py-3 text-left font-semibold">Created</th>
                        <th className="px-3 py-3 text-left font-semibold">Opened</th>
                        <th className="px-3 py-3 text-left font-semibold">Last Opened</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPacketLogs.map(log => {
                        // Check if customer has submitted on this log - check multiple possible fields
                        const submittedOptions = log.submittedOptions || log.submitted_options || log.options;
                        const hasSubmitted = submittedOptions && Object.keys(submittedOptions).length > 0;
                        
                        return (
                          <tr key={log._id} className={`border-b hover:bg-gray-50 transition-colors ${
                            hasSubmitted 
                              ? 'bg-green-50 border-green-200 hover:bg-green-100' 
                              : 'hover:bg-gray-50'
                          }`}>
                            <td className="px-3 py-3 text-black">
                              <div className="flex items-center gap-2">
                                {hasSubmitted && (
                                  <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                                )}
                                <span className="truncate">{log.customer?.firstName} {log.customer?.lastName}</span>
                              </div>
                          </td>
                            <td className="px-3 py-3 text-black text-center">
                              {log.lastActiveAt && (Date.now() - new Date(log.lastActiveAt).getTime() < 10 * 1000) ? 
                                <span className="text-green-600 font-bold">● Online</span> : 
                                <span className="text-gray-400">Offline</span>
                              }
                            </td>
                            <td className="px-3 py-3 text-black">
                              <a href={`mailto:${log.customer?.email}`} className="text-blue-600 hover:text-blue-800 truncate block">
                                {log.customer?.email}
                              </a>
                            </td>
                            <td className="px-3 py-3">
                              <a href={log.link} className="text-blue-700 hover:text-blue-900 underline truncate block" target="_blank" rel="noopener noreferrer">
                                {log.link}
                              </a>
                            </td>
                            <td className="px-3 py-3 text-black">
                              <div className="truncate" title={`${log.vehicle?.year} ${log.vehicle?.make} ${log.vehicle?.model} ${log.vehicle?.vin}`}>
                                {log.vehicle?.year} {log.vehicle?.make} {log.vehicle?.model}
                              </div>
                            </td>
                            <td className="px-3 py-3 text-black text-sm">
                              {log.createdAt ? new Date(log.createdAt).toLocaleDateString() : ''}
                            </td>
                            <td className="px-3 py-3 text-black font-bold text-center">
                              {log.opened ? 
                                <span className="text-green-700 bg-green-100 px-2 py-1 rounded-full text-xs">Yes</span> : 
                                <span className="text-gray-500 bg-gray-100 px-2 py-1 rounded-full text-xs">No</span>
                              }
                            </td>
                            <td className="px-3 py-3 text-black text-sm">
                              {log.openedAt ? new Date(log.openedAt).toLocaleString() : ''}
                            </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredPacketLogs.length === 0 && <div className="hidden sm:block text-gray-500 mt-2">No logs found.</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Inventory Modal */}
        {showInventory && selectedDealership && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-2 sm:p-4">
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 w-full max-w-full sm:max-w-[95vw] md:max-w-4xl relative overflow-y-auto max-h-screen sm:max-h-[95vh]">
              
              {/* Enhanced Header */}
              <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-t-3xl px-6 py-8 relative">
                <button
                  className="absolute top-4 right-4 text-white hover:text-gray-200 text-2xl font-bold w-10 h-10 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center transition-all duration-200"
                  onClick={closeInventory}
                >
                  &times;
                </button>
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white break-words">
                      {selectedDealership.name} Inventory
                    </h3>
                    <p className="text-blue-100 font-medium">CSV File Management & Auto-Watch</p>
                  </div>
                </div>
                {/* Show currently watched path if any */}
                {watchingCsvPath && (
                  <div className="flex items-center gap-2 bg-white bg-opacity-20 rounded-full px-4 py-2 w-fit">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-white font-semibold text-sm">WATCHING</span>
                    <span className="text-blue-100 text-sm">{watchingCsvPath}</span>
                  </div>
                )}
              </div>

              <div className="p-6">
                {/* Auto-Watch CSV Directory Section */}
                <div className="mb-6">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-bold text-gray-800">Auto-Watch CSV Directory</h4>
                    </div>
                    <div className="flex gap-3 items-center">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h2a2 2 0 012 2v2H8V5z" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          placeholder="/path/to/csv/directory"
                          value={manualCsvWatchPath}
                          onChange={e => setManualCsvWatchPath(e.target.value)}
                          className="block w-full pl-10 pr-4 py-3 text-sm border border-blue-300 rounded-xl bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <button
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        onClick={async () => {
                          // Save the path and start watching
                          setWatchingCsvPath(manualCsvWatchPath);
                          await fetch(`/api/dealerships/${selectedDealership._id}/watch-csv-path`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ path: manualCsvWatchPath })
                          });
                          alert('Now watching directory for new CSV files. The most recent CSV will be used automatically.');
                          // Refetch the watched path to update UI
                          fetch(`/api/dealerships/${selectedDealership._id}/csv-watch-path`)
                            .then(res => res.json())
                            .then(data => setWatchingCsvPath(data.path || ''));
                        }}
                        disabled={!manualCsvWatchPath}
                      >
                        <svg className="w-5 h-5 mr-2 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Watch Path
                      </button>
                    </div>
                  </div>
                </div>

                {/* Status Information */}
                {lastModified && (
                  <div className="mb-4 flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">Last updated:</span>
                    <span>{lastModified}</span>
                  </div>
                )}

                {/* Current CSV File */}
                {selectedDealership && selectedDealership.csvFile && (
                  <div className="mb-6">
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 border border-green-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-blue-600 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-bold text-gray-800">Current CSV File</h4>
                      </div>
                      <a
                        href={`/api/dealerships/${selectedDealership._id}/csv`}
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold transition-colors duration-200"
                        download
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {selectedDealership.csvFile}
                      </a>
                    </div>
                  </div>
                )}

                {/* File Upload Section */}
                <div className="mb-6">
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-bold text-gray-800">Replace Inventory CSV</h4>
                    </div>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleCsvUpload}
                        className="block w-full text-sm text-gray-900 border border-purple-300 rounded-xl cursor-pointer bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-l-xl file:border-0 file:text-sm file:font-semibold file:bg-gradient-to-r file:from-purple-600 file:to-indigo-600 file:text-white hover:file:from-purple-700 hover:file:to-indigo-700 transition-all duration-200"
                      />
                    </div>
                  </div>
                </div>
                {/* Inventory Table */}
                <div className="mb-6">
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl border border-indigo-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-bold text-white">Inventory Data</h4>
                        <div className="ml-auto bg-white bg-opacity-20 px-3 py-1 rounded-full">
                          <span className="text-white font-semibold text-sm">{inventory.length} vehicles</span>
                        </div>
                      </div>
                    </div>
                    <div className="overflow-x-auto max-h-[60vh] bg-white">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            {inventory[0] &&
                              Object.keys(inventory[0]).map((header) => (
                                <th key={header} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                                  {header}
                                </th>
                              ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {selectedDealership && inventory
                            .filter(vehicle => {
                              if (!inventorySearch) return true;
                              const s = inventorySearch.toLowerCase();
                              return (
                                (vehicle.model || '').toLowerCase().includes(s) ||
                                (vehicle.vin || '').toLowerCase().includes(s) ||
                                (vehicle.stockNumber || '').toLowerCase().includes(s)
                              );
                            })
                            .map(vehicle => (
                              <tr key={vehicle.vin} className={`hover:bg-gray-50 transition-colors duration-200 ${(!vehicle.imageOne || !vehicle.price) ? 'bg-yellow-50' : ''}`}>
                                {Object.entries(vehicle).map(([key, val], i) => (
                                  <td key={i} className="px-4 py-3 text-gray-900 text-sm whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <span className="truncate">{val}</span>
                                      {/* Show warning icons */}
                                      {key === 'imageOne' && !vehicle.imageOne && <span title="Missing photo" className="text-red-500 text-xs">⚠️</span>}
                                      {key === 'price' && !vehicle.price && <span title="Missing price" className="text-red-500 text-xs">⚠️</span>}
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4 justify-center">
                  <a
                    href={selectedDealership && selectedDealership._id ? `/api/dealerships/${selectedDealership._id}/csv` : '#'}
                    download
                    className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export Inventory CSV
                  </a>

                  <button
                    className="bg-gradient-to-r from-red-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:from-red-700 hover:to-pink-700 transition-all duration-200 transform hover:scale-105 flex items-center gap-2"
                    onClick={async () => {
                      await fetch(`/api/dealerships/${selectedDealership._id}/csv`, { method: 'DELETE' });
                      alert('CSV file deleted');
                      // Clear CSV content and inventory state, and close modal
                      setCsvContent('');
                      setInventory([]);
                      setLastModified('');
                      setShowInventory(false);
                      setSelectedDealership(null);
                      // Then refetch dealerships:
                      const updatedDealerships = await fetchDealershipsWithVehicleCounts();
                      setDealerships(updatedDealerships);
                    }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete CSV
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showPacketModal && packetDealership && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 w-full max-w-7xl p-8 relative overflow-y-auto" style={{ maxHeight: '95vh' }}>
              <button
                className="absolute top-6 right-6 text-gray-400 hover:text-gray-700 text-3xl font-bold hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition-all duration-200"
                onClick={closePacketModal}
              >
                &times;
              </button>
              
              {/* Header Section */}
              <div className="mb-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900">{packetDealership.name}</h3>
                    <p className="text-lg text-gray-600 font-medium">{modalTitle} Collection</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span className="font-semibold">{vehicleCount}</span> vehicles available
                </div>
              </div>
              
              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-3 mb-8">
                <button
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg ${packetFilter === 'all' ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-blue-500/25' : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}
                  onClick={() => setPacketFilter('all')}
                >
                  All Vehicles
                </button>
                <button
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg ${packetFilter === 'used' ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-blue-500/25' : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}
                  onClick={() => setPacketFilter('used')}
                >
                  Used Vehicles
                </button>
                <button
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg ${packetFilter === 'new' ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-blue-500/25' : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}
                  onClick={() => setPacketFilter('new')}
                >
                  New Vehicles
                </button>
              </div>
              
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Left: Search/Filters */}
                <div className="lg:w-1/4 w-full">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 sticky top-0">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Search & Filter</h4>
                    <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by Stock, VIN, Model..."
                    value={packetSearch}
                    onChange={e => setPacketSearch(e.target.value)}
                        className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  />
                      <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
                {/* Right: Vehicle Cards */}
                <div className="lg:w-3/4 w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
                  {packetDealership && filteredPacketVehicles.map((v, idx) => {
                    const id = v.vin || v.stockNumber;

                    
                    // Check for price in multiple possible field names from CSV
                    let priceRaw = v.internetPrice || v.price || v.retail || v.cost || v.wholesalePrice || 
                                  v.Price || v.msrp || v.MSRP || v.InternetPrice || v.Retail || v.Cost ||
                                  v['Internet Price'] || v['Retail'] || v['Cost'] || v['MSRP'] || v['Price'];
                    let priceDisplay = 'Call for Price';
                    
                    // Convert to number and validate
                    const priceNum = Number(String(priceRaw || '').replace(/[^0-9.-]/g, ''));
                    if (!isNaN(priceNum) && priceNum > 0) {
                      priceDisplay = `$${priceNum.toLocaleString()}`;
                    }
                    return (
                      <div key={id || idx} className="group bg-white border border-gray-200 rounded-xl md:rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 p-0 flex flex-col overflow-hidden hover:scale-[1.02] md:hover:scale-105 transform">
                        {/* Image Section */}
                        <div className="relative overflow-hidden bg-gray-100 rounded-t-xl md:rounded-t-2xl h-40 sm:h-48 md:h-52 flex items-center justify-center">
                        <img
                            src={v.imageOne || v.imageUrls?.split(',')[0] || v['Image One'] || v['imageUrls']?.split(',')[0] || 
                                 v.ImageOne || v.ImageUrls?.split(',')[0] || v.image1 || v.Image1 || 
                                 'https://via.placeholder.com/400x225?text=No+Image'}
                          alt={getVehicleName(v)}
                            className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                          onError={e => (e.target.src = 'https://via.placeholder.com/400x225?text=No+Image')}
                        />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          
                          {/* Price Badge */}
                          <div className="absolute top-4 right-4 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1 rounded-full shadow-lg font-bold text-sm">
                            {priceDisplay}
                          </div>
                        </div>
                        
                        {/* Content Section */}
                        <div className="p-3 sm:p-4 md:p-6 flex flex-col h-full">
                          {/* Vehicle Name */}
                          <div
                            className="font-bold text-lg sm:text-xl mb-2 md:mb-3 text-gray-900 cursor-pointer hover:text-blue-600 transition-colors duration-200 group-hover:text-blue-600"
                          onClick={() => {
                            if (packetDealership && packetDealership._id) {
                              window.location.href = `/admin/vehicle/${packetDealership._id}/${v.vin}`;
                            } else {
                              alert('No dealership selected!');
                            }
                          }}
                        >
                          {getVehicleName(v)}
                        </div>
                          
                          {/* Vehicle Details Grid */}
                          <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4 mb-3 md:mb-4 text-xs sm:text-sm">
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                              <div className="min-w-0 flex-1">
                                <span className="text-gray-600 font-medium">Stock:</span>
                                <div className="text-gray-900 font-semibold break-all">{v.stockNumber || v.stocknumber || v['Stock Number'] || v.StockNumber || v.vin || 'N/A'}</div>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></div>
                              <div className="min-w-0 flex-1">
                                <span className="text-gray-600 font-medium">Miles:</span>
                                <div className="text-gray-900 font-semibold">{v.mileage || v.Mileage || v.miles || v.Miles || 'N/A'}</div>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0"></div>
                              <div className="min-w-0 flex-1">
                                <span className="text-gray-600 font-medium">Trim:</span>
                                <div className="text-gray-900 font-semibold break-words">{v.trim || v.Trim || 'N/A'}</div>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 flex-shrink-0"></div>
                              <div className="min-w-0 flex-1">
                                <span className="text-gray-600 font-medium">Drive:</span>
                                <div className="text-gray-900 font-semibold break-words">{v.drivetrain || v.drive || v.Drivetrain || v.Drive || 'N/A'}</div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Colors Section */}
                          <div className="bg-gray-50 rounded-lg md:rounded-xl p-2 sm:p-3 md:p-4 mb-3 md:mb-4">
                            <div className="space-y-3 text-sm">
                              <div className="flex items-start gap-3">
                                <div className="flex items-center gap-2 text-gray-600 font-medium flex-shrink-0">
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM7 21h10a2 2 0 002-2v-4a2 2 0 00-2-2H7" />
                                  </svg>
                                  Exterior:
                                </div>
                                <span className="text-gray-900 font-semibold break-words">{v.exteriorColor || v['Exterior Color'] || v.ExteriorColor || 'N/A'}</span>
                              </div>
                              <div className="flex items-start gap-3">
                                <div className="flex items-center gap-2 text-gray-600 font-medium flex-shrink-0">
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Interior:
                                </div>
                                <span className="text-gray-900 font-semibold break-words">{v.interiorColor || v['Interior Color'] || v.InteriorColor || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* VIN Section */}
                          <div className="mt-auto">
                            <div className="text-xs text-gray-500 font-mono bg-gray-100 px-3 py-2 rounded-lg">
                              VIN: {v.vin || v.VIN || 'N/A'}
                            </div>
                          </div>
                          
                          {/* Action Button */}
                          <button
                            className="mt-3 md:mt-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg md:rounded-xl transition-all duration-200 text-sm sm:text-base shadow-md hover:shadow-lg"
                            onClick={() => {
                              if (packetDealership && packetDealership._id) {
                                window.location.href = `/admin/vehicle/${packetDealership._id}/${v.vin}`;
                              } else {
                                alert('No dealership selected!');
                              }
                            }}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {vehicleCount === 0 && (
                    <div className="col-span-full text-center text-gray-400">No {modalTitle} found.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedPacketVehicle && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-70">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl p-6 relative overflow-y-auto" style={{ maxHeight: '95vh' }}>
              <button
                className="absolute top-2 left-2 text-gray-700 hover:text-black text-lg font-bold px-4 py-2 border rounded"
                onClick={() => setSelectedPacketVehicle(null)}
              >
                &larr; Back
              </button>
              <div className="flex flex-col md:flex-row gap-6 mt-8">
                <div className="md:w-1/2 w-full flex flex-col items-center">
                  <img
                    src={selectedPacketVehicle.imageOne || selectedPacketVehicle.imageUrls?.split(',')[0] || 
                         selectedPacketVehicle['Image One'] || selectedPacketVehicle['imageUrls']?.split(',')[0] || 
                         selectedPacketVehicle.ImageOne || selectedPacketVehicle.ImageUrls?.split(',')[0] || 
                         selectedPacketVehicle.image1 || selectedPacketVehicle.Image1 || 
                         'https://via.placeholder.com/400x225?text=No+Image'}
                    alt={getVehicleName(selectedPacketVehicle)}
                    className="w-full max-h-72 object-contain rounded mb-4 bg-gray-100"
                    onError={e => (e.target.src = 'https://via.placeholder.com/400x225?text=No+Image')}
                  />
                </div>
                <div className="md:w-1/2 w-full flex flex-col gap-2">
                  <div className="text-2xl font-bold text-black mb-2">{getVehicleName(selectedPacketVehicle)}</div>
                  <div className="text-lg text-red-600 font-bold mb-2">
                    {(() => {
                      const priceRaw = selectedPacketVehicle.internetPrice || selectedPacketVehicle.price || 
                                       selectedPacketVehicle.retail || selectedPacketVehicle.cost || 
                                       selectedPacketVehicle.wholesalePrice || selectedPacketVehicle.Price || 
                                       selectedPacketVehicle.msrp || selectedPacketVehicle.MSRP ||
                                       selectedPacketVehicle['Internet Price'] || selectedPacketVehicle['Retail'] || 
                                       selectedPacketVehicle['Cost'] || selectedPacketVehicle['MSRP'] || selectedPacketVehicle['Price'];
                      const priceNum = Number(String(priceRaw || '').replace(/[^0-9.-]/g, ''));
                      if (!isNaN(priceNum) && priceNum > 0) {
                        return `$${priceNum.toLocaleString()}`;
                      }
                      return 'Call for Price';
                    })()}
                  </div>
                  <div className="text-base text-gray-700 mb-1">Stock #: {selectedPacketVehicle.stockNumber || selectedPacketVehicle.stocknumber || selectedPacketVehicle['Stock Number'] || selectedPacketVehicle.StockNumber || selectedPacketVehicle.vin || 'N/A'}</div>
                  <div className="text-base text-gray-700 mb-1">VIN: {selectedPacketVehicle.vin || selectedPacketVehicle.VIN || 'N/A'}</div>
                  <div className="text-base text-gray-700 mb-1">Miles: {selectedPacketVehicle.mileage || selectedPacketVehicle.Mileage || selectedPacketVehicle.miles || selectedPacketVehicle.Miles || 'N/A'}</div>
                  <div className="text-base text-gray-700 mb-1">Trim: {selectedPacketVehicle.trim || selectedPacketVehicle.Trim || 'N/A'}</div>
                  <div className="text-base text-gray-700 mb-1">Exterior: {selectedPacketVehicle.exteriorColor || selectedPacketVehicle['Exterior Color'] || selectedPacketVehicle.ExteriorColor || 'N/A'}</div>
                  <div className="text-base text-gray-700 mb-1">Interior: {selectedPacketVehicle.interiorColor || selectedPacketVehicle['Interior Color'] || selectedPacketVehicle.InteriorColor || 'N/A'}</div>
                  <div className="text-base text-gray-700 mb-1">Drivetrain: {selectedPacketVehicle.drivetrain || selectedPacketVehicle.drive || selectedPacketVehicle.Drivetrain || selectedPacketVehicle.Drive || 'N/A'}</div>
                  {/* Add more fields as needed */}
                </div>
              </div>
            </div>
          </div>
        )}

        {showLeadsModal && leadsDealership && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-2 sm:p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-[95vw] md:max-w-5xl lg:max-w-6xl p-3 sm:p-6 relative overflow-y-auto max-h-screen sm:max-h-[95vh]">
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-3xl font-bold p-2 transition-colors duration-200 hover:bg-gray-100 rounded-full"
                onClick={() => {
                  setShowLeadsModal(false);
                  setLeadsActiveTab('leads');
                  setTradeInEvaluations([]);
                  setSelectedTradeInEvaluation(null);
                  setShowTradeInDetailModal(false);
                  setLeadSearch(''); // Clear search input when modal closes
                  // Close photo lightbox if open
                  if (showPhotoLightbox) {
                    closePhotoLightbox();
                  }
                }}
                style={{ minWidth: 44, minHeight: 44 }}
              >
                &times;
              </button>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 pr-12 sm:pr-8">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1 break-words">{leadsDealership.name}</h2>
                  <p className="text-sm text-gray-500 font-medium">Customer Leads Management</p>
              </div>
              </div>
              
              {/* Tab Navigation */}
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  className={`px-4 py-2 font-medium text-sm ${
                    leadsActiveTab === 'leads'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => {
                    setLeadsActiveTab('leads');
                    // Reset trade-in evaluations to show all when switching back
                    if (leadsDealership) {
                      fetchTradeInEvaluations(leadsDealership._id);
                    }
                  }}
                >
                  Customer Leads ({leads.length})
                </button>
                <button
                  className={`px-4 py-2 font-medium text-sm ml-4 ${
                    leadsActiveTab === 'trade-in'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => {
                    setLeadsActiveTab('trade-in');
                    // Refresh trade-in evaluations when switching to tab
                    if (leadsDealership) {
                      fetchTradeInEvaluations(leadsDealership._id);
                    }
                  }}
                >
                  Trade-In Evaluations ({tradeInEvaluations.length})
                </button>
              </div>
              {/* Tab Content */}
              {leadsActiveTab === 'leads' && (
                <>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
              <input
                type="text"
                  placeholder="Search leads by name, email, phone, or vehicle..."
                value={leadSearch}
                onChange={e => setLeadSearch(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base transition-colors duration-200 text-gray-900 placeholder-gray-500"
              />
                  </div>
                  <div className="relative filter-dropdown">
                    <button
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                      className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm sm:text-base font-medium text-gray-700 whitespace-nowrap"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                      Filter by
                      <svg className={`w-4 h-4 transition-transform ${showFilterDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {showFilterDropdown && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                        <div className="p-2">
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-3 py-1">Sort by</div>
                          {[
                            { value: 'recent-lead', label: 'Most Recent Lead', icon: '🕐' },
                            { value: 'recent-session', label: 'Most Recent Click Stream', icon: '🔄' },
                            { value: 'recent-calculator', label: 'Most Recent Calculator Log', icon: '🧮' },
                            { value: 'most-clicks', label: 'Most Click Streams', icon: '👆' }
                          ].map((filter) => (
                            <button
                              key={filter.value}
                              onClick={() => {
                                setLeadsFilter(filter.value);
                                setShowFilterDropdown(false);
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left hover:bg-gray-50 transition-colors duration-150 ${
                                leadsFilter === filter.value ? 'bg-blue-50 text-blue-700 border-l-2 border-blue-500' : 'text-gray-700'
                              }`}
                            >
                              <span className="text-lg">{filter.icon}</span>
                              <span className="text-sm font-medium">{filter.label}</span>
                              {leadsFilter === filter.value && (
                                <svg className="w-4 h-4 ml-auto text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          ))}
                          
                          {/* Date Filter Section */}
                          <div className="border-t border-gray-200 mt-2 pt-2">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-3 py-1">Filter by Date</div>
                            {[
                              { key: 'all', label: 'All Time', icon: '🌍' },
                              { key: 'today', label: 'Today', icon: '📅' },
                              { key: 'threeDays', label: 'Last 3 Days', icon: '📊' },
                              { key: 'sevenDays', label: 'Last 7 Days', icon: '📈' }
                            ].map((dateFilter) => (
                              <label
                                key={dateFilter.key}
                                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-left hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={dateFilters[dateFilter.key]}
                                  onChange={(e) => {
                                    const newFilters = { today: false, threeDays: false, sevenDays: false, all: false };
                                    newFilters[dateFilter.key] = e.target.checked;
                                    setDateFilters(newFilters);
                                  }}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-lg">{dateFilter.icon}</span>
                                <span className="text-sm font-medium text-gray-700">{dateFilter.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-full">
                    <span className="font-bold text-lg">{filteredLeads.length}</span>
                    <span className="text-sm ml-1">Total Leads</span>
                  </div>
                  {leadSearch && (
                    <div className="text-gray-600 bg-gray-100 px-3 py-1 rounded-full text-sm">
                      Showing {filteredLeads.length} of {leads.length}
                    </div>
                  )}
                  <div className="text-gray-600 bg-blue-50 px-3 py-1 rounded-full text-sm border border-blue-200">
                    <span className="text-blue-700 font-medium">
                      {(() => {
                        const activeFilter = [
                          { value: 'recent-lead', label: 'Most Recent Lead' },
                          { value: 'recent-session', label: 'Most Recent Click Stream' },
                          { value: 'recent-calculator', label: 'Most Recent Calculator Log' },
                          { value: 'most-clicks', label: 'Most Click Streams' }
                        ].find(f => f.value === leadsFilter);
                        return activeFilter ? activeFilter.label : 'Most Recent Lead';
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Modern Card Layout */}
              <div className="space-y-4">
                  {leads.length === 0 ? (
                  <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No leads yet</h3>
                    <p className="text-gray-500">Customer leads will appear here once they start submitting inquiries</p>
                  </div>
                ) : (
                  <>
                    {/* Calculate pagination for leads */}
                    {(() => {
                      const totalLeads = filteredLeads.length;
                      const totalPages = Math.ceil(totalLeads / leadsPerPage);
                      const startIndex = (leadsCurrentPage - 1) * leadsPerPage;
                      const endIndex = startIndex + leadsPerPage;
                      const paginatedLeads = filteredLeads.slice(startIndex, endIndex);

                      return (
                        <>
                          {paginatedLeads.map((lead, index) => (
                    <div key={lead._id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300 overflow-hidden">
                      {/* Mobile Layout */}
                      <div className="block sm:hidden p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {((lead.firstName || '') + ' ' + (lead.lastName || '')).trim().charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {(lead.firstName || '') + ' ' + (lead.lastName || '') || 'Unknown Name'}
                              </h3>
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                leadSubmissionStatus[lead.email] === 'Yes' 
                                  ? 'bg-green-100 text-green-800' 
                                  : leadSubmissionStatus[lead.email] === 'No'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {leadSubmissionStatus[lead.email] === 'Yes' ? '✓ Submitted' : leadSubmissionStatus[lead.email] === 'No' ? '✗ Not Submitted' : '⏳ Checking...'}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-2 mb-3">
                          <a href={`mailto:${lead.email}`} className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {lead.email}
                          </a>
                          {lead.phone && (
                            <a href={`tel:${lead.phone}`} className="text-green-600 hover:text-green-800 font-medium text-sm flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              {lead.phone}
                            </a>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="bg-gray-50 rounded-lg p-2">
                            <div className="text-xs text-gray-600">Vehicle</div>
                            <div className="text-sm font-medium text-gray-900">
                              {lead.vehicle ? `${lead.vehicle.year} ${lead.vehicle.make} ${lead.vehicle.model}` : 'Not specified'}
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2">
                            <div className="text-xs text-gray-600">Submitted</div>
                            <div className="text-sm font-medium text-gray-900">
                              {lead.date ? new Date(lead.date).toLocaleDateString() : 'Unknown'}
                            </div>
                          </div>
                        </div>
                        
                        {lead.assignedTo && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
                            <div className="text-xs text-blue-800">
                              Assigned to: {lead.assignedTo.name}
                            </div>
                          </div>
                        )}
                        
                        <div className="flex flex-col gap-2">
                          <button
                            className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 shadow-sm w-full"
                            onClick={async () => {
                              setSelectedLead(lead);
                              setViewLeadModal(true);
                              setLeadPacketLogsLoading(true);
                              const res = await fetch(`/api/dealerships/api/packet-logs?dealershipId=${leadsDealership._id}&email=${encodeURIComponent(lead.email)}`);
                              const logs = await res.json();
                              setLeadPacketLogs(logs);
                              setLeadPacketLogsLoading(false);
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span>View Details</span>
                          </button>
                          
                          <div className="flex gap-2">
                            <button
                              className="flex items-center justify-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg font-medium transition-colors duration-200 shadow-sm flex-1"
                              onClick={async () => {
                                setLeadsActiveTab('trade-in');
                                try {
                                  let searchParams = '';
                                  if (lead.phone) {
                                    searchParams = `customerPhone=${encodeURIComponent(lead.phone)}`;
                                  } else if (lead.email) {
                                    searchParams = `customerEmail=${encodeURIComponent(lead.email)}`;
                                  }
                                  setTradeInLoading(true);
                                  const response = await fetch(`/api/trade-in-evaluations/${leadsDealership._id}?${searchParams}`);
                                  if (response.ok) {
                                    const data = await response.json();
                                    setTradeInEvaluations(data.evaluations || []);
                                  } else {
                                    setTradeInEvaluations([]);
                                  }
                                } catch (error) {
                                  setTradeInEvaluations([]);
                                } finally {
                                  setTradeInLoading(false);
                                }
                              }}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                              <span>Trade-In</span>
                            </button>
                            
                            {(localStorage.getItem('adminRole') === 'ADMIN' || localStorage.getItem('adminRole') === 'OWNER') && (
                              <button
                                className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg font-medium transition-colors duration-200 shadow-sm flex-1"
                                onClick={() => openAssignModal(lead)}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <span>Assign</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Desktop Layout */}
                      <div className="hidden sm:block p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-lg">
                                {((lead.firstName || '') + ' ' + (lead.lastName || '')).trim().charAt(0).toUpperCase() || '?'}
                              </span>
                          </div>
                            <div>
                              <h3 className="text-xl font-semibold text-gray-900">
                                {(lead.firstName || '') + ' ' + (lead.lastName || '') || 'Unknown Name'}
                              </h3>
                              <div className="flex items-center space-x-4 mt-1">
                                <a href={`mailto:${lead.email}`} className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  {lead.email}
                                </a>
                                {lead.phone && (
                                  <a href={`tel:${lead.phone}`} className="text-green-600 hover:text-green-800 font-medium text-sm flex items-center">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    {lead.phone}
                                  </a>
                                )}
                          </div>
                        </div>
                        </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                              leadSubmissionStatus[lead.email] === 'Yes' 
                                ? 'bg-green-100 text-green-800' 
                                : leadSubmissionStatus[lead.email] === 'No'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {leadSubmissionStatus[lead.email] === 'Yes' ? '✓ Submitted' : leadSubmissionStatus[lead.email] === 'No' ? '✗ Not Submitted' : '⏳ Checking...'}
                            </span>
                        </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center space-x-2 mb-2">
                              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <h4 className="text-sm font-semibold text-gray-700">Vehicle</h4>
                        </div>
                            <div className="text-gray-900 font-medium">
                              {lead.vehicle ? `${lead.vehicle.year} ${lead.vehicle.make} ${lead.vehicle.model}` : 'Not specified'}
                        </div>
                            {lead.vehicle?.stockNumber && (
                              <div className="text-sm text-gray-500 mt-1">Stock: #{lead.vehicle.stockNumber}</div>
                            )}
                          </div>

                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center space-x-2 mb-2">
                              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4m-8 0h8m-8 0a1 1 0 00-1 1v8a1 1 0 001 1h8a1 1 0 001-1V8a1 1 0 00-1-1" />
                              </svg>
                              <h4 className="text-sm font-semibold text-gray-700">Submitted</h4>
                            </div>
                            <div className="text-gray-900 font-medium">
                              {lead.date ? new Date(lead.date).toLocaleDateString() : 'Unknown'}
                            </div>
                            {lead.date && (
                              <div className="text-sm text-gray-500 mt-1">
                                {new Date(lead.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </div>
                            )}
                          </div>

                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center space-x-2 mb-2">
                              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <h4 className="text-sm font-semibold text-gray-700">Created By</h4>
                            </div>
                            <div className="text-gray-900 font-medium">
                              {lead.createdBy ? lead.createdBy.name : 'Legacy'}
                            </div>
                            {lead.createdBy?.role && (
                              <div className="text-sm text-gray-500 mt-1 capitalize">
                                {lead.createdBy.role.toLowerCase()}
                              </div>
                            )}
                          </div>

                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center space-x-2 mb-2">
                              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <h4 className="text-sm font-semibold text-gray-700">Last Opened</h4>
                            </div>
                            <div className="text-gray-900 font-medium">
                              {leadLastOpened[lead.email] ? leadLastOpened[lead.email].toLocaleDateString() : 'Never'}
                            </div>
                            {leadLastOpened[lead.email] && (
                              <div className="text-sm text-gray-500 mt-1">
                                {leadLastOpened[lead.email].toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </div>
                            )}
                          </div>
                        </div>

                        {lead.assignedTo && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                            <div className="flex items-center space-x-2">
                              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-sm font-medium text-blue-800">
                                Assigned to: {lead.assignedTo.name} ({lead.assignedTo.role?.toLowerCase() || 'unknown'})
                              </span>
                            </div>
                          </div>
                        )}

                        <div className="flex flex-wrap gap-3">
                          <button
                            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 shadow-sm"
                            onClick={async () => {
                              setSelectedLead(lead);
                              setViewLeadModal(true);
                              setLeadPacketLogsLoading(true);
                              const res = await fetch(`/api/dealerships/api/packet-logs?dealershipId=${leadsDealership._id}&email=${encodeURIComponent(lead.email)}`);
                              const logs = await res.json();
                              setLeadPacketLogs(logs);
                              setLeadPacketLogsLoading(false);
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span>View Details</span>
                          </button>

                          <button
                            className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 shadow-sm"
                            onClick={async () => {
                              setLeadsActiveTab('trade-in');
                              try {
                                let searchParams = '';
                                if (lead.phone) {
                                  searchParams = `customerPhone=${encodeURIComponent(lead.phone)}`;
                                } else if (lead.email) {
                                  searchParams = `customerEmail=${encodeURIComponent(lead.email)}`;
                                }
                                setTradeInLoading(true);
                                const response = await fetch(`/api/trade-in-evaluations/${leadsDealership._id}?${searchParams}`);
                                if (response.ok) {
                                  const data = await response.json();
                                  setTradeInEvaluations(data.evaluations || []);
                                } else {
                                  setTradeInEvaluations([]);
                                }
                              } catch (error) {
                                setTradeInEvaluations([]);
                              } finally {
                                setTradeInLoading(false);
                              }
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                            <span>Trade-In</span>
                          </button>

                          {(localStorage.getItem('adminRole') === 'ADMIN' || localStorage.getItem('adminRole') === 'OWNER') && (
                            <button
                              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 shadow-sm"
                              onClick={() => openAssignModal(lead)}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span>Assign</span>
                            </button>
                          )}

                          <button
                            className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 shadow-sm disabled:opacity-50"
                            onClick={() => handleDeleteLead(lead)}
                            disabled={deletingLeadId === lead._id}
                          >
                            {deletingLeadId === lead._id ? (
                              <>
                                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Deleting...</span>
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <span>Delete</span>
                              </>
                            )}
                          </button>
                        </div>
                        </div>
                      </div>
                            ))}

                            {/* Enhanced Pagination Controls for Leads */}
                            {totalPages > 1 && (
                              <div className="mt-8 px-4">
                                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4">
                                    <div className="flex items-center justify-center gap-2">
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-14.5L21 3m-3.5 3.5L21 3m-3.5 3.5L21 10" />
                                      </svg>
                                      <span className="font-semibold">Customer Leads</span>
                                    </div>
                                  </div>
                                  <div className="p-6">
                                    <div className="flex flex-wrap items-center justify-center gap-2">
                                      {/* Previous Button */}
                                      <button
                                        onClick={() => setLeadsCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={leadsCurrentPage === 1}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all shadow-sm ${
                                          leadsCurrentPage === 1
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 hover:scale-105 transform'
                                        }`}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        Previous
                                      </button>

                                      {/* Page Numbers */}
                                      <div className="flex gap-2">
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                                          <button
                                            key={pageNum}
                                            onClick={() => setLeadsCurrentPage(pageNum)}
                                            className={`w-12 h-12 rounded-xl font-bold transition-all transform hover:scale-110 shadow-sm ${
                                              leadsCurrentPage === pageNum
                                                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                                                : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                            }`}
                                          >
                                            {pageNum}
                                          </button>
                                        ))}
                                      </div>

                                      {/* Next Button */}
                                      <button
                                        onClick={() => setLeadsCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={leadsCurrentPage === totalPages}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all shadow-sm ${
                                          leadsCurrentPage === totalPages
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 hover:scale-105 transform'
                                        }`}
                                      >
                                        Next
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Enhanced Pagination Info */}
                            <div className="mt-6 text-center">
                              <div className="inline-flex items-center gap-3 bg-blue-50 text-blue-800 px-6 py-3 rounded-full border border-blue-200 shadow-sm">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="font-medium">
                                  Showing <span className="font-bold">{Math.min(endIndex, totalLeads)}</span> of <span className="font-bold">{totalLeads}</span> leads
                                </span>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>

              </>
              )}

              {/* Trade-In Evaluations Tab Content */}
              {leadsActiveTab === 'trade-in' && (
                <>
                  {/* Trade-In Search and Filter System */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Search evaluations by customer name, email, phone, or vehicle..."
                          value={tradeInSearch}
                          onChange={e => setTradeInSearch(e.target.value)}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base transition-colors duration-200 text-gray-900 placeholder-gray-500"
                        />
                      </div>
                      <div className="relative filter-dropdown">
                        <button
                          onClick={() => setShowTradeInFilterDropdown(!showTradeInFilterDropdown)}
                          className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200 text-sm sm:text-base font-medium text-gray-700 whitespace-nowrap"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                          </svg>
                          Filter by
                          <svg className={`w-4 h-4 transition-transform ${showTradeInFilterDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {showTradeInFilterDropdown && (
                          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                            <div className="p-2">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-3 py-1">Sort by</div>
                                                             {[
                                 { value: 'recent-submission', label: 'Most Recent Submission', icon: '🕐' },
                                 { value: 'vehicle-year', label: 'Vehicle Year (Newest)', icon: '🚗' }
                               ].map((filter) => (
                                <button
                                  key={filter.value}
                                  onClick={() => {
                                    setTradeInFilter(filter.value);
                                    setShowTradeInFilterDropdown(false);
                                  }}
                                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left hover:bg-gray-50 transition-colors duration-150 ${
                                    tradeInFilter === filter.value ? 'bg-orange-50 text-orange-700 border-l-2 border-orange-500' : 'text-gray-700'
                                  }`}
                                >
                                  <span className="text-lg">{filter.icon}</span>
                                  <span className="text-sm font-medium">{filter.label}</span>
                                  {tradeInFilter === filter.value && (
                                    <svg className="w-4 h-4 ml-auto text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </button>
                              ))}
                              
                              {/* Status Filter Section */}
                              <div className="border-t border-gray-200 mt-2 pt-2">
                                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-3 py-1">Filter by Status</div>
                                                                 {[
                                   { key: 'all', label: 'All Status', icon: '🔄' },
                                   { key: 'pending', label: 'Need Review', icon: '⏳' },
                                   { key: 'approved', label: 'Approved', icon: '✅' }
                                 ].map((statusFilter) => (
                                  <label
                                    key={statusFilter.key}
                                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-left hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={tradeInStatusFilters[statusFilter.key]}
                                      onChange={(e) => {
                                        const newFilters = { all: false, pending: false, approved: false };
                                        newFilters[statusFilter.key] = e.target.checked;
                                        setTradeInStatusFilters(newFilters);
                                      }}
                                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                    />
                                    <span className="text-lg">{statusFilter.icon}</span>
                                    <span className="text-sm font-medium text-gray-700">{statusFilter.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Trade-In Count and Status Badges */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-full">
                        <span className="font-bold text-lg">{tradeInEvaluations.length}</span>
                        <span className="text-sm ml-1">Total Evaluations</span>
                      </div>
                      {tradeInSearch && (
                        <div className="text-gray-600 bg-gray-100 px-3 py-1 rounded-full text-sm">
                          Showing {(() => {
                            const filtered = tradeInEvaluations.filter(evaluation => {
                              if (!tradeInSearch.trim()) return true;
                              const searchTerm = tradeInSearch.toLowerCase().trim();
                              const customerInfo = evaluation.customerInfo || {};
                              const vehicleInfo = evaluation.vehicleInfo || {};
                              const customerName = `${customerInfo.firstName || ''} ${customerInfo.lastName || ''}`.toLowerCase();
                              const customerEmail = (customerInfo.email || '').toLowerCase();
                              const customerPhone = (customerInfo.phone || '').toLowerCase();
                              const vehicleName = `${vehicleInfo.year || ''} ${vehicleInfo.make || ''} ${vehicleInfo.model || ''}`.toLowerCase();
                              const vehicleVin = (vehicleInfo.vin || '').toLowerCase();
                              return customerName.includes(searchTerm) || customerEmail.includes(searchTerm) || 
                                     customerPhone.includes(searchTerm) || vehicleName.includes(searchTerm) || vehicleVin.includes(searchTerm);
                            });
                            return filtered.length;
                          })()} of {tradeInEvaluations.length}
                        </div>
                      )}
                      <div className="text-gray-600 bg-orange-50 px-3 py-1 rounded-full text-sm border border-orange-200">
                        {tradeInFilter === 'recent-submission' && 'Most Recent Submission'}
                        {tradeInFilter === 'vehicle-year' && 'Vehicle Year (Newest)'}
                      </div>
                    </div>
                  </div>

                  {tradeInLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                  ) : tradeInEvaluations.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      <p className="text-lg">No trade-in evaluations found</p>
                      <p className="text-sm mt-2">Customer submissions will appear here</p>
                    </div>
                  ) : (
                    <>
                      {/* Mobile Layout - Trade-in Evaluations */}
                      <div className="block sm:hidden space-y-4">
                        {(() => {
                          // Filter and sort trade-in evaluations
                          const filteredEvaluations = tradeInEvaluations.filter(evaluation => {
                            // Search filter
                            if (tradeInSearch.trim()) {
                              const searchTerm = tradeInSearch.toLowerCase().trim();
                              const customerInfo = evaluation.customerInfo || {};
                              const vehicleInfo = evaluation.vehicleInfo || {};
                              
                              const customerName = `${customerInfo.firstName || ''} ${customerInfo.lastName || ''}`.toLowerCase();
                              const customerEmail = (customerInfo.email || '').toLowerCase();
                              const customerPhone = (customerInfo.phone || '').toLowerCase();
                              const customerZip = (customerInfo.zipCode || '').toLowerCase();
                              const vehicleName = `${vehicleInfo.year || ''} ${vehicleInfo.make || ''} ${vehicleInfo.model || ''}`.toLowerCase();
                              const vehicleVin = (vehicleInfo.vin || '').toLowerCase();
                              const vehicleColor = (vehicleInfo.vehicleColor || '').toLowerCase();
                              const vehicleTrim = (vehicleInfo.trim || '').toLowerCase();
                              
                              const matchesSearch = (
                                customerName.includes(searchTerm) ||
                                customerEmail.includes(searchTerm) ||
                                customerPhone.includes(searchTerm) ||
                                customerZip.includes(searchTerm) ||
                                vehicleName.includes(searchTerm) ||
                                vehicleVin.includes(searchTerm) ||
                                vehicleColor.includes(searchTerm) ||
                                vehicleTrim.includes(searchTerm)
                              );
                              
                              if (!matchesSearch) return false;
                            }
                            
                            // Status filter
                            if (!tradeInStatusFilters.all) {
                              const status = evaluation.status || 'need_reviewed';
                              if (tradeInStatusFilters.pending && (status === 'need_reviewed' || status === 'pending' || !status)) {
                                return true;
                              }
                              if (tradeInStatusFilters.approved && status === 'approved') {
                                return true;
                              }
                              return false;
                            }
                            
                            return true;
                          }).sort((a, b) => {
                            // Apply sorting
                            switch (tradeInFilter) {
                              case 'recent-submission':
                                return new Date(b.submittedAt || b.createdAt || 0) - new Date(a.submittedAt || a.createdAt || 0);
                              case 'vehicle-year':
                                const yearA = parseInt(a.vehicleInfo?.year || 0);
                                const yearB = parseInt(b.vehicleInfo?.year || 0);
                                return yearB - yearA;
                              default:
                                return 0;
                            }
                          });

                          const totalEvaluations = filteredEvaluations.length;
                          const totalPages = Math.ceil(totalEvaluations / tradeInPerPage);
                          const startIndex = (tradeInCurrentPage - 1) * tradeInPerPage;
                          const endIndex = startIndex + tradeInPerPage;
                          const paginatedEvaluations = filteredEvaluations.slice(startIndex, endIndex);

                          return (
                            <>
                              {paginatedEvaluations.map((evaluation, index) => (
                          <div key={evaluation._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            {/* Header with customer info and status */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                  <span className="text-white font-bold text-sm">
                                    {evaluation.customerInfo?.firstName?.charAt(0) || '?'}
                                  </span>
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-900">
                                    {evaluation.customerInfo?.firstName && evaluation.customerInfo?.lastName 
                                      ? `${evaluation.customerInfo.firstName} ${evaluation.customerInfo.lastName}`
                                      : 'Unknown Customer'}
                                  </div>
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    (evaluation.status === 'need_reviewed' || evaluation.status === 'pending' || !evaluation.status)
                                      ? 'bg-yellow-100 text-yellow-800' 
                                      : evaluation.status === 'approved'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {(evaluation.status === 'need_reviewed' || evaluation.status === 'pending' || !evaluation.status) ? 'Need Reviewed' : 
                                     evaluation.status === 'approved' ? 'Approved' : 
                                     evaluation.status}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Vehicle Information */}
                            <div className="mb-3">
                              <div className="font-medium text-gray-900 mb-1">
                                {evaluation.vehicleInfo?.year} {evaluation.vehicleInfo?.make} {evaluation.vehicleInfo?.model}
                              </div>
                              <div className="text-xs text-gray-500 font-mono">
                                VIN: {evaluation.vehicleInfo?.vin ? evaluation.vehicleInfo.vin.substring(0, 8) + '...' : 'N/A'}
                              </div>
                              {evaluation.vehicleInfo?.vehicleColor && (
                                <div className="text-xs text-green-600 font-medium">Color: {evaluation.vehicleInfo.vehicleColor}</div>
                              )}
                              {evaluation.vehicleInfo?.trim && (
                                <div className="text-xs text-blue-600 font-medium">{evaluation.vehicleInfo.trim}</div>
                              )}
                            </div>

                            {/* Contact Information */}
                            <div className="mb-3 space-y-1">
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                <span className="text-sm text-gray-700">{evaluation.customerInfo?.phone || 'N/A'}</span>
                              </div>
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                                </svg>
                                <span className="text-sm text-gray-700">{evaluation.customerInfo?.email || 'No email'}</span>
                              </div>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                <span>{evaluation.vehicleInfo?.currentMiles ? `${evaluation.vehicleInfo.currentMiles.toLocaleString()} miles` : 'N/A'}</span>
                              </div>
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span>{evaluation.customerInfo?.zipCode || 'N/A'}</span>
                              </div>
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>{evaluation.photos?.length || 0} photos</span>
                              </div>
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{evaluation.submissionDate ? new Date(evaluation.submissionDate).toLocaleDateString() : 'N/A'}</span>
                              </div>
                            </div>

                            {/* Value Information */}
                            {(evaluation.evaluation?.estimatedValue || evaluation.evaluation?.tradeInValue) && (
                              <div className="mb-3 flex gap-2 text-xs">
                                {evaluation.evaluation?.estimatedValue && (
                                  <div className="bg-green-50 px-2 py-1 rounded text-green-700 font-bold">
                                    Est: ${evaluation.evaluation.estimatedValue.toLocaleString()}
                                  </div>
                                )}
                                {evaluation.evaluation?.tradeInValue && (
                                  <div className="bg-blue-50 px-2 py-1 rounded text-blue-700 font-bold">
                                    Trade: ${evaluation.evaluation.tradeInValue.toLocaleString()}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-2">
                              <button
                                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-150 font-medium text-sm flex items-center justify-center"
                                onClick={() => viewTradeInDetails(evaluation._id)}
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View Details
                              </button>
                              
                              {/* Photo Upload Link Button - Available for all customers */}
                                <button
                                  className="bg-orange-600 text-white px-3 py-2 rounded-lg hover:bg-orange-700 transition-colors duration-150 font-medium text-sm flex items-center"
                                  onClick={() => generatePhotoUploadLink(evaluation)}
                                  disabled={generatingPhotoLink === evaluation._id}
                                  title="Generate photo upload link for customer"
                                >
                                  {generatingPhotoLink === evaluation._id ? (
                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  )}
                                </button>
                              
                              <button
                                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-150 font-medium text-sm flex items-center"
                                onClick={() => handleDeleteTradeIn(evaluation)}
                                disabled={deletingTradeInId === evaluation._id}
                              >
                                {deletingTradeInId === evaluation._id ? (
                                  <>
                                    <svg className="animate-spin w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Deleting...
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Delete
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                                ))}

                                {/* Enhanced Pagination Controls for Trade-in Evaluations - Mobile */}
                                {totalPages > 1 && (
                                  <div className="mt-8 px-4">
                                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                                      <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-4">
                                        <div className="flex items-center justify-center gap-2">
                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                          </svg>
                                          <span className="font-semibold">Trade-In Evaluations</span>
                                        </div>
                                      </div>
                                      <div className="p-6">
                                        <div className="flex flex-wrap items-center justify-center gap-2">
                                          {/* Previous Button */}
                                          <button
                                            onClick={() => setTradeInCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={tradeInCurrentPage === 1}
                                            className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-xl font-semibold transition-all shadow-sm text-sm sm:text-base ${
                                              tradeInCurrentPage === 1
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700 hover:scale-105 transform'
                                            }`}
                                          >
                                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                            <span className="hidden sm:inline">Previous</span>
                                            <span className="sm:hidden">Prev</span>
                                          </button>

                                          {/* Page Numbers - Mobile optimized */}
                                          <div className="flex gap-1 sm:gap-2">
                                            {(() => {
                                              const maxVisiblePages = 5; // Show max 5 pages on mobile
                                              let startPage = Math.max(1, tradeInCurrentPage - Math.floor(maxVisiblePages / 2));
                                              let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                                              
                                              // Adjust if we're near the end
                                              if (endPage - startPage + 1 < maxVisiblePages) {
                                                startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                              }
                                              
                                              const pages = [];
                                              for (let i = startPage; i <= endPage; i++) {
                                                pages.push(i);
                                              }
                                              
                                              return (
                                                <>
                                                  {startPage > 1 && (
                                                    <>
                                                      <button
                                                        onClick={() => setTradeInCurrentPage(1)}
                                                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl font-bold transition-all transform hover:scale-110 shadow-sm bg-white text-gray-700 border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50"
                                                      >
                                                        1
                                                      </button>
                                                      {startPage > 2 && <span className="text-gray-400 self-center">...</span>}
                                                    </>
                                                  )}
                                                  
                                                  {pages.map(pageNum => (
                                                    <button
                                                      key={pageNum}
                                                      onClick={() => setTradeInCurrentPage(pageNum)}
                                                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl font-bold transition-all transform hover:scale-110 shadow-sm ${
                                                        tradeInCurrentPage === pageNum
                                                          ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg'
                                                          : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                                                      }`}
                                                    >
                                                      {pageNum}
                                                    </button>
                                                  ))}
                                                  
                                                  {endPage < totalPages && (
                                                    <>
                                                      {endPage < totalPages - 1 && <span className="text-gray-400 self-center">...</span>}
                                                      <button
                                                        onClick={() => setTradeInCurrentPage(totalPages)}
                                                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl font-bold transition-all transform hover:scale-110 shadow-sm bg-white text-gray-700 border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50"
                                                      >
                                                        {totalPages}
                                                      </button>
                                                    </>
                                                  )}
                                                </>
                                              );
                                            })()}
                                          </div>

                                          {/* Next Button */}
                                          <button
                                            onClick={() => setTradeInCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={tradeInCurrentPage === totalPages}
                                            className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-xl font-semibold transition-all shadow-sm text-sm sm:text-base ${
                                              tradeInCurrentPage === totalPages
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700 hover:scale-105 transform'
                                            }`}
                                          >
                                            <span className="hidden sm:inline">Next</span>
                                            <span className="sm:hidden">Next</span>
                                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Enhanced Pagination Info - Mobile */}
                                <div className="mt-6 text-center">
                                  <div className="inline-flex items-center gap-3 bg-orange-50 text-orange-800 px-6 py-3 rounded-full border border-orange-200 shadow-sm">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="font-medium">
                                      Showing <span className="font-bold">{Math.min(endIndex, totalEvaluations)}</span> of <span className="font-bold">{totalEvaluations}</span> evaluations
                                    </span>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </div>

                      {/* Desktop Layout - Trade-in Evaluations (unchanged) */}
                      <div className="hidden sm:block overflow-x-auto">
                        <table className="min-w-full border rounded-xl overflow-hidden text-sm">
                          <thead>
                            <tr className="bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800">
                              <th className="px-4 py-3 text-left font-semibold">Customer</th>
                              <th className="px-4 py-3 text-left font-semibold">Contact</th>
                              <th className="px-4 py-3 text-left font-semibold">Vehicle</th>
                              <th className="px-4 py-3 text-left font-semibold">Details</th>
                              <th className="px-4 py-3 text-left font-semibold">Status</th>
                              <th className="px-4 py-3 text-left font-semibold">Submitted</th>
                              <th className="px-4 py-3 text-center font-semibold">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              // Filter and sort trade-in evaluations (Desktop)
                              const filteredEvaluations = tradeInEvaluations.filter(evaluation => {
                                // Search filter
                                if (tradeInSearch.trim()) {
                                  const searchTerm = tradeInSearch.toLowerCase().trim();
                                  const customerInfo = evaluation.customerInfo || {};
                                  const vehicleInfo = evaluation.vehicleInfo || {};
                                  
                                  const customerName = `${customerInfo.firstName || ''} ${customerInfo.lastName || ''}`.toLowerCase();
                                  const customerEmail = (customerInfo.email || '').toLowerCase();
                                  const customerPhone = (customerInfo.phone || '').toLowerCase();
                                  const customerZip = (customerInfo.zipCode || '').toLowerCase();
                                  const vehicleName = `${vehicleInfo.year || ''} ${vehicleInfo.make || ''} ${vehicleInfo.model || ''}`.toLowerCase();
                                  const vehicleVin = (vehicleInfo.vin || '').toLowerCase();
                                  const vehicleColor = (vehicleInfo.vehicleColor || '').toLowerCase();
                                  const vehicleTrim = (vehicleInfo.trim || '').toLowerCase();
                                  
                                  const matchesSearch = (
                                    customerName.includes(searchTerm) ||
                                    customerEmail.includes(searchTerm) ||
                                    customerPhone.includes(searchTerm) ||
                                    customerZip.includes(searchTerm) ||
                                    vehicleName.includes(searchTerm) ||
                                    vehicleVin.includes(searchTerm) ||
                                    vehicleColor.includes(searchTerm) ||
                                    vehicleTrim.includes(searchTerm)
                                  );
                                  
                                  if (!matchesSearch) return false;
                                }
                                
                                                                 // Status filter
                                 if (!tradeInStatusFilters.all) {
                                   const status = evaluation.status || 'need_reviewed';
                                   if (tradeInStatusFilters.pending && (status === 'need_reviewed' || status === 'pending' || !status)) {
                                     return true;
                                   }
                                   if (tradeInStatusFilters.approved && status === 'approved') {
                                     return true;
                                   }
                                   return false;
                                 }
                                 
                                 return true;
                               }).sort((a, b) => {
                                 // Apply sorting
                                 switch (tradeInFilter) {
                                   case 'recent-submission':
                                     return new Date(b.submittedAt || b.createdAt || 0) - new Date(a.submittedAt || a.createdAt || 0);
                                   case 'vehicle-year':
                                     const yearA = parseInt(a.vehicleInfo?.year || 0);
                                     const yearB = parseInt(b.vehicleInfo?.year || 0);
                                     return yearB - yearA;
                                   default:
                                     return 0;
                                 }
                              });

                              const totalEvaluations = filteredEvaluations.length;
                              const totalPages = Math.ceil(totalEvaluations / tradeInPerPage);
                              const startIndex = (tradeInCurrentPage - 1) * tradeInPerPage;
                              const endIndex = startIndex + tradeInPerPage;
                              const paginatedEvaluations = filteredEvaluations.slice(startIndex, endIndex);

                              return (
                                <>
                                  {paginatedEvaluations.map((evaluation, index) => (
                              <tr key={evaluation._id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                                <td className="px-4 py-3 text-gray-900 font-medium">
                                  <div className="flex items-center">
                                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mr-3">
                                      <span className="text-white font-bold text-xs">
                                        {evaluation.customerInfo?.firstName?.charAt(0) || '?'}
                                      </span>
                                    </div>
                                    <div>
                                      <div className="font-semibold">
                                        {evaluation.customerInfo?.firstName && evaluation.customerInfo?.lastName 
                                          ? `${evaluation.customerInfo.firstName} ${evaluation.customerInfo.lastName}`
                                          : 'Unknown Customer'}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {evaluation.customerInfo?.email || 'No email'}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-700">
                                  <div className="text-sm">
                                    <div className="flex items-center">
                                      <svg className="w-4 h-4 mr-1 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                      </svg>
                                      {evaluation.customerInfo?.phone || 'N/A'}
                                    </div>
                                    <div className="flex items-center mt-1">
                                      <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                      </svg>
                                      <span className="text-xs text-gray-500">{evaluation.customerInfo?.zipCode || 'N/A'}</span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-700">
                                  <div className="text-sm">
                                    <div className="font-medium text-gray-900">
                                      {evaluation.vehicleInfo?.year} {evaluation.vehicleInfo?.make} {evaluation.vehicleInfo?.model}
                                    </div>
                                    <div className="text-xs text-gray-500 font-mono">
                                      VIN: {evaluation.vehicleInfo?.vin ? evaluation.vehicleInfo.vin.substring(0, 8) + '...' : 'N/A'}
                                    </div>
                                    {evaluation.vehicleInfo?.vehicleColor && (
                                      <div className="text-xs text-green-600 font-medium">Color: {evaluation.vehicleInfo.vehicleColor}</div>
                                    )}
                                    {evaluation.vehicleInfo?.trim && (
                                      <div className="text-xs text-blue-600 font-medium">{evaluation.vehicleInfo.trim}</div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-700">
                                  <div className="text-sm space-y-1">
                                    <div className="flex items-center">
                                      <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                      </svg>
                                      <span className="text-xs">
                                        {evaluation.vehicleInfo?.currentMiles ? `${evaluation.vehicleInfo.currentMiles.toLocaleString()} miles` : 'N/A'}
                                      </span>
                                    </div>
                                    <div className="flex items-center">
                                      <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                      </svg>
                                      <span className="text-xs capitalize">
                                        {evaluation.condition?.vehicleDrivability || 'N/A'}
                                      </span>
                                    </div>
                                    <div className="flex items-center">
                                      <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      <span className="text-xs">
                                        {evaluation.photos?.length || 0} photos
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    (evaluation.status === 'need_reviewed' || evaluation.status === 'pending' || !evaluation.status)
                                      ? 'bg-yellow-100 text-yellow-800' 
                                      : evaluation.status === 'approved'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {(evaluation.status === 'need_reviewed' || evaluation.status === 'pending' || !evaluation.status) ? 'Need Reviewed' : 
                                     evaluation.status === 'approved' ? 'Approved' : 
                                     evaluation.status}
                                  </span>
                                  {evaluation.evaluation?.estimatedValue && (
                                    <div className="text-xs text-green-600 font-bold mt-1">
                                      ${evaluation.evaluation.estimatedValue.toLocaleString()}
                                    </div>
                                  )}
                                  {evaluation.evaluation?.tradeInValue && (
                                    <div className="text-xs text-blue-600 font-bold mt-1">
                                      Trade: ${evaluation.evaluation.tradeInValue.toLocaleString()}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-gray-700 text-xs">
                                  <div className="text-sm">
                                    {evaluation.submissionDate ? new Date(evaluation.submissionDate).toLocaleDateString() : 'N/A'}
                                  </div>
                                  {evaluation.submissionDate && (
                                      <div className="text-xs text-gray-500">
                                      {new Date(evaluation.submissionDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                      </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <div className="flex items-center justify-center space-x-2">
                                    <button
                                      className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-150 font-medium text-xs flex items-center"
                                      onClick={() => viewTradeInDetails(evaluation._id)}
                                    >
                                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                      View
                                    </button>
                                    
                                    {/* Photo Upload Link Button - Desktop - Available for all customers */}
                                      <button
                                        className="bg-orange-600 text-white px-3 py-2 rounded-lg hover:bg-orange-700 transition-colors duration-150 font-medium text-xs flex items-center"
                                        onClick={() => generatePhotoUploadLink(evaluation)}
                                        disabled={generatingPhotoLink === evaluation._id}
                                        title="Generate photo upload link for customer"
                                      >
                                        {generatingPhotoLink === evaluation._id ? (
                                          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                          </svg>
                                        ) : (
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                          </svg>
                                        )}
                                      </button>
                                    
                                    <button
                                      className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition-colors duration-150 font-medium text-xs flex items-center"
                                      onClick={() => handleDeleteTradeIn(evaluation)}
                                      disabled={deletingTradeInId === evaluation._id}
                                    >
                                      {deletingTradeInId === evaluation._id ? (
                                        <>
                                          <svg className="animate-spin w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                          </svg>
                                          Deleting...
                                        </>
                                      ) : (
                                        <>
                                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                          Delete
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                                    ))}
                                  </>
                                );
                              })()}
                            </tbody>
                          </table>

                          {/* Enhanced Pagination Controls for Trade-in Evaluations - Desktop */}
                          {(() => {
                            const totalEvaluations = tradeInEvaluations.length;
                            const totalPages = Math.ceil(totalEvaluations / tradeInPerPage);
                            const startIndex = (tradeInCurrentPage - 1) * tradeInPerPage;
                            const endIndex = startIndex + tradeInPerPage;

                            return totalPages > 1 ? (
                              <div className="mt-8 px-4">
                                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                                  <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-4">
                                    <div className="flex items-center justify-center gap-2">
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                      </svg>
                                      <span className="font-semibold">Trade-In Evaluations</span>
                                    </div>
                                  </div>
                                  <div className="p-6">
                                    <div className="flex flex-wrap items-center justify-center gap-2">
                                      {/* Previous Button */}
                                      <button
                                        onClick={() => setTradeInCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={tradeInCurrentPage === 1}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all shadow-sm ${
                                          tradeInCurrentPage === 1
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700 hover:scale-105 transform'
                                        }`}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        Previous
                                      </button>

                                      {/* Page Numbers */}
                                      <div className="flex gap-2">
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                                          <button
                                            key={pageNum}
                                            onClick={() => setTradeInCurrentPage(pageNum)}
                                            className={`w-12 h-12 rounded-xl font-bold transition-all transform hover:scale-110 shadow-sm ${
                                              tradeInCurrentPage === pageNum
                                                ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg'
                                                : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                                            }`}
                                          >
                                            {pageNum}
                                          </button>
                                        ))}
                                      </div>

                                      {/* Next Button */}
                                      <button
                                        onClick={() => setTradeInCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={tradeInCurrentPage === totalPages}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all shadow-sm ${
                                          tradeInCurrentPage === totalPages
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700 hover:scale-105 transform'
                                        }`}
                                      >
                                        Next
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : null;
                          })()}

                          {/* Enhanced Pagination Info - Desktop */}
                          {(() => {
                            const totalEvaluations = tradeInEvaluations.length;
                            const startIndex = (tradeInCurrentPage - 1) * tradeInPerPage;
                            const endIndex = startIndex + tradeInPerPage;

                            return (
                              <div className="mt-6 text-center">
                                <div className="inline-flex items-center gap-3 bg-orange-50 text-orange-800 px-6 py-3 rounded-full border border-orange-200 shadow-sm">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className="font-medium">
                                    Showing <span className="font-bold">{Math.min(endIndex, totalEvaluations)}</span> of <span className="font-bold">{totalEvaluations}</span> evaluations
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {showSettingsModal && settingsDealership && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
            onClick={() => setShowSettingsModal(false)}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-full sm:max-w-2xl md:max-w-3xl p-2 sm:p-6 md:p-10 relative overflow-y-auto overflow-x-hidden" 
              style={{ maxHeight: '95vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl font-bold"
                onClick={() => setShowSettingsModal(false)}
              >
                &times;
              </button>
              <h3 className="text-2xl font-bold text-black mb-4">{settingsDealership.name} Auto Settings</h3>
              
              {/* PDF Uploader Interface - Moved to top */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border-2 border-blue-200 p-8 shadow-inner mb-6">
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center mb-3">
                    <svg className="w-10 h-10 text-blue-600 mr-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <h3 className="text-2xl font-bold text-blue-900">PDF Uploader</h3>
                  </div>
                  <p className="text-blue-700 font-medium">Upload vehicle documents and media files</p>
                </div>

                {/* Upload Instructions */}
                <div className="bg-white rounded-2xl p-6 mb-6 border border-blue-200 shadow-sm">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Quick Upload Guide:
                  </h4>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">•</span>
                      <span>Files must be named with the vehicle's <strong className="text-blue-600">VIN</strong> or <strong className="text-blue-600">Stock Number</strong></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">•</span>
                      <span>Supported formats: <strong className="text-green-600">{/Mobi|Android/i.test(navigator.userAgent) ? "PDF, MP4" : "PDF, JPG, PNG, MP4"}</strong></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 font-bold mr-2">•</span>
                      <span className="text-gray-500">Example: <code className="bg-gray-100 px-2 py-1 rounded text-xs">1HGCV1F39KA123456_inspection.pdf</code></span>
                    </li>
                  </ul>
                </div>

                {/* Upload Actions */}
                <div className="bg-white rounded-2xl p-6 border-2 border-dashed border-blue-300 hover:border-blue-400 transition-colors">
                  <div className="text-center">
                    <svg className="w-16 h-16 text-blue-400 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3-3m0 0l-3 3m3-3v6m-1.5-15H5.25a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    </svg>
                    
                    <div className="space-y-4">
                      <input
                        type="file"
                        ref={fileInputRef}
                        multiple
                        accept={/Mobi|Android/i.test(navigator.userAgent) ? ".pdf,.mp4" : ".pdf,.jpg,.jpeg,.png,.mp4"}
                        className="hidden"
                        onChange={handleFileSelection}
                      />
                      <button 
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-8 py-3 rounded-xl font-bold shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all transform hover:scale-105 flex items-center mx-auto"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                        Choose Files
                      </button>
                      
                      <p className="text-gray-500 text-sm">or drag and drop files here</p>
                      
                      <div className="flex gap-3 justify-center">
                        <button 
                          onClick={handleStartUpload}
                          disabled={selectedFiles.length === 0 || uploading}
                          className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          {uploading ? 'Uploading...' : 'Start Upload'}
                        </button>
                        
                        <button 
                          onClick={handleClearFiles}
                          className="bg-gray-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-gray-600 transition flex items-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Clear All
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Selected Files Display */}
                {selectedFiles.length > 0 && (
                  <div className="mt-6 bg-white rounded-2xl p-6 border border-blue-200">
                    <h5 className="font-bold text-gray-800 mb-3">Selected Files ({selectedFiles.length})</h5>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center space-x-3">
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div>
                              <div className="font-medium text-gray-900">{file.name}</div>
                              <div className="text-sm text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Status */}
                <div className="mt-6 text-center"> 
                  <div className="inline-flex items-center px-4 py-2 bg-white rounded-full border border-gray-200 shadow-sm">
                    <div className={`w-3 h-3 rounded-full mr-2 ${uploading ? 'bg-blue-400 animate-pulse' : selectedFiles.length > 0 ? 'bg-yellow-400' : 'bg-green-400'}`}></div>
                    <span className="text-sm font-medium text-gray-700">
                      {uploading ? 'Uploading files...' : selectedFiles.length > 0 ? `${selectedFiles.length} files selected` : 'Ready to upload'}
                    </span>
                  </div>
                </div>

                {/* Upload Messages */}
                {uploadMessage && (
                  <div className={`mt-4 p-4 rounded-lg text-center font-medium ${uploadMessage.includes('Error') || uploadMessage.includes('Failed') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {uploadMessage}
                  </div>
                )}
              </div>

              {/* Vehicle List Section */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-blue-900 mb-4">All Vehicles</h4>
                {/* Search Bar */}
                <div className="mb-4 w-full flex justify-center">
                  <input
                    type="text"
                    className="w-full max-w-md border border-gray-300 rounded-lg px-4 py-2 text-base text-black shadow-sm"
                    placeholder="Search by VIN, vehicle name, or model..."
                    value={settingsVehicleSearch}
                    onChange={e => setSettingsVehicleSearch(e.target.value)}
                  />
                </div>
                
                {/* Missing Info Filters */}
                <div className="mb-6 bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl border-2 border-orange-200 p-6 shadow-sm">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-orange-600 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <h5 className="text-lg font-bold text-orange-900">Filter Missing Information</h5>
                  </div>
                  <p className="text-center text-orange-700 mb-4 text-sm">Show only vehicles that are missing specific documents or media</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <label className="flex items-center bg-white rounded-lg p-3 border border-orange-200 hover:bg-orange-50 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-orange-600 rounded mr-3"
                        checked={filterMissingVideo}
                        onChange={e => setFilterMissingVideo(e.target.checked)}
                      />
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">Missing Video</span>
                      </div>
                    </label>
                    
                    <label className="flex items-center bg-white rounded-lg p-3 border border-orange-200 hover:bg-orange-50 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-orange-600 rounded mr-3"
                        checked={filterMissingAutoCheck}
                        onChange={e => setFilterMissingAutoCheck(e.target.checked)}
                      />
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">Missing AutoCheck</span>
                      </div>
                    </label>
                    
                    <label className="flex items-center bg-white rounded-lg p-3 border border-orange-200 hover:bg-orange-50 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-orange-600 rounded mr-3"
                        checked={filterMissingMsrp}
                        onChange={e => setFilterMissingMsrp(e.target.checked)}
                      />
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">Missing MSRP</span>
                      </div>
                    </label>
                    
                    <label className="flex items-center bg-white rounded-lg p-3 border border-orange-200 hover:bg-orange-50 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-orange-600 rounded mr-3"
                        checked={filterMissingInspection}
                        onChange={e => setFilterMissingInspection(e.target.checked)}
                      />
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">Missing Inspection</span>
                      </div>
                    </label>
                  </div>
                  
                  {/* Clear All Filters Button */}
                  {(filterMissingVideo || filterMissingAutoCheck || filterMissingMsrp || filterMissingInspection) && (
                    <div className="flex justify-center mt-4">
                      <button
                        onClick={() => {
                          setFilterMissingVideo(false);
                          setFilterMissingAutoCheck(false);
                          setFilterMissingMsrp(false);
                          setFilterMissingInspection(false);
                        }}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Clear All Filters
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Filter Status */}
                {(filterMissingVideo || filterMissingAutoCheck || filterMissingMsrp || filterMissingInspection) && (
                  <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                        <span className="text-blue-800 font-medium">
                          Showing vehicles missing: {[
                            filterMissingVideo && 'Video',
                            filterMissingAutoCheck && 'AutoCheck',
                            filterMissingMsrp && 'MSRP',
                            filterMissingInspection && 'Inspection'
                          ].filter(Boolean).join(', ')}
                        </span>
                      </div>
                      <span className="text-blue-600 text-sm font-medium">
                        {(() => {
                          const filtered = (dealershipSettings?.vehicles || []).filter(vehicle => {
                            if (!settingsVehicleSearch.trim()) return true;
                            const s = settingsVehicleSearch.trim().toLowerCase();
                            const vin = (vehicle.vin || vehicle.VIN || '').toLowerCase();
                            const model = (vehicle.model || '').toLowerCase();
                            const stockNumber = (vehicle.stockNumber || vehicle.stocknumber || vehicle['Stock Number'] || vehicle.StockNumber || '').toLowerCase();
                            const name = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ').toLowerCase();
                            return vin.includes(s) || model.includes(s) || name.includes(s) || stockNumber.includes(s);
                          }).filter(vehicle => {
                            const vinSafe = (vehicle.vin || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                            const hasInspection = !!dealershipSettings?.inspectionPdfs?.get?.(vinSafe) || !!dealershipSettings?.inspectionPdfs?.[vinSafe];
                            const hasAutoCheck = !!dealershipSettings?.autoCheckPdfs?.get?.(vinSafe) || !!dealershipSettings?.autoCheckPdfs?.[vinSafe];
                            const hasMsrp = !!dealershipSettings?.msrpPdfs?.get?.(vinSafe) || !!dealershipSettings?.msrpPdfs?.[vinSafe];
                            const hasVideo = !!dealershipSettings?.vehicleVideos?.get?.(vinSafe) || !!dealershipSettings?.vehicleVideos?.[vinSafe];
                            
                            let matchesFilter = false;
                            if (filterMissingVideo && !hasVideo) matchesFilter = true;
                            if (filterMissingAutoCheck && !hasAutoCheck) matchesFilter = true;
                            if (filterMissingMsrp && !hasMsrp) matchesFilter = true;
                            if (filterMissingInspection && !hasInspection) matchesFilter = true;
                            
                            return matchesFilter;
                          });
                          return `${filtered.length} vehicles`;
                        })()}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col gap-4">
                  {(dealershipSettings?.vehicles || [])
                    .filter(vehicle => {
                      // Search filter
                      if (!settingsVehicleSearch.trim()) return true;
                      const s = settingsVehicleSearch.trim().toLowerCase();
                      const vin = (vehicle.vin || vehicle.VIN || '').toLowerCase();
                      const model = (vehicle.model || '').toLowerCase();
                      const stockNumber = (vehicle.stockNumber || vehicle.stocknumber || vehicle['Stock Number'] || vehicle.StockNumber || '').toLowerCase();
                      const name = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ').toLowerCase();
                      return vin.includes(s) || model.includes(s) || name.includes(s) || stockNumber.includes(s);
                    })
                    .filter(vehicle => {
                      // Missing info filters
                      if (!filterMissingVideo && !filterMissingAutoCheck && !filterMissingMsrp && !filterMissingInspection) {
                        return true; // No filters active, show all vehicles
                      }
                      
                      const vinSafe = (vehicle.vin || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                      const hasInspection = !!dealershipSettings?.inspectionPdfs?.get?.(vinSafe) || !!dealershipSettings?.inspectionPdfs?.[vinSafe];
                      const hasAutoCheck = !!dealershipSettings?.autoCheckPdfs?.get?.(vinSafe) || !!dealershipSettings?.autoCheckPdfs?.[vinSafe];
                      const hasMsrp = !!dealershipSettings?.msrpPdfs?.get?.(vinSafe) || !!dealershipSettings?.msrpPdfs?.[vinSafe];
                      const hasVideo = !!dealershipSettings?.vehicleVideos?.get?.(vinSafe) || !!dealershipSettings?.vehicleVideos?.[vinSafe];
                      
                      // Check if vehicle matches any of the active filters
                      let matchesFilter = false;
                      
                      if (filterMissingVideo && !hasVideo) matchesFilter = true;
                      if (filterMissingAutoCheck && !hasAutoCheck) matchesFilter = true;
                      if (filterMissingMsrp && !hasMsrp) matchesFilter = true;
                      if (filterMissingInspection && !hasInspection) matchesFilter = true;
                      
                      return matchesFilter;
                    })
                    .map((vehicle, idx) => {
                    const vinSafe = (vehicle.vin || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                    const hasInspection = !!dealershipSettings?.inspectionPdfs?.get?.(vinSafe) || !!dealershipSettings?.inspectionPdfs?.[vinSafe];
                    const hasAutoCheck = !!dealershipSettings?.autoCheckPdfs?.get?.(vinSafe) || !!dealershipSettings?.autoCheckPdfs?.[vinSafe];
                    const hasMsrp = !!dealershipSettings?.msrpPdfs?.get?.(vinSafe) || !!dealershipSettings?.msrpPdfs?.[vinSafe];
                    const hasVideo = !!dealershipSettings?.vehicleVideos?.get?.(vinSafe) || !!dealershipSettings?.vehicleVideos?.[vinSafe];
                    return (
                      <div
                        key={vehicle.vin || vehicle.VIN || vehicle.stockNumber || vehicle.stocknumber || vehicle['Stock Number'] || vehicle.StockNumber || idx}
                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between rounded-xl border border-gray-200 bg-white px-3 sm:px-4 md:px-6 py-4 shadow-sm gap-3 sm:gap-4 hover:shadow-lg transition w-full max-w-full"
                        style={{ boxShadow: '0 2px 8px 0 rgba(31, 38, 135, 0.07)' }}
                      >
                        <div className="flex flex-col min-w-0 flex-1">
                          <div className="font-bold text-lg text-gray-900 mb-1 truncate">{vehicle.year} {vehicle.make} {vehicle.model}</div>
                          <div className="flex flex-wrap gap-2 text-base text-gray-700 mb-1">
                            <span className="font-semibold">Stock #:</span> <span className="font-mono">{vehicle.stockNumber || vehicle.stocknumber || vehicle['Stock Number'] || vehicle.StockNumber || 'N/A'}</span>
                                                          <span className="font-semibold ml-4">VIN:</span> <span className="font-mono">{vehicle.vin || vehicle.VIN || 'N/A'}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 md:gap-3 items-center flex-shrink-0 w-full sm:w-auto">
                          <span className={`flex items-center gap-1 px-3 py-1 rounded text-sm font-semibold ${hasVideo ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>{hasVideo ? (<><span className='text-lg'>✔</span> Video</>) : (<><span className='text-lg'>✖</span> Video</>)} </span>
                          <span className={`flex items-center gap-1 px-3 py-1 rounded text-sm font-semibold ${hasInspection ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>{hasInspection ? (<><span className='text-lg'>✔</span> 172 / Multi</>) : (<><span className='text-lg'>✖</span> 172 / Multi</>)} </span>
                          <span className={`flex items-center gap-1 px-3 py-1 rounded text-sm font-semibold ${hasAutoCheck ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>{hasAutoCheck ? (<><span className='text-lg'>✔</span> AutoCheck</>) : (<><span className='text-lg'>✖</span> AutoCheck</>)} </span>
                          <span className={`flex items-center gap-1 px-3 py-1 rounded text-sm font-semibold ${hasMsrp ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>{hasMsrp ? (<><span className='text-lg'>✔</span> MSRP</>) : (<><span className='text-lg'>✖</span> MSRP</>)} </span>
                          <button
                            className="px-3 py-1 rounded bg-blue-600 text-white font-semibold text-sm shadow hover:bg-blue-700 transition"
                            onClick={() => setEditingVehicle(vehicle)}
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {(() => {
                    const allVehicles = dealershipSettings?.vehicles || [];
                    const filteredVehicles = allVehicles.filter(vehicle => {
                      // Search filter
                      if (!settingsVehicleSearch.trim()) return true;
                      const s = settingsVehicleSearch.trim().toLowerCase();
                      const vin = (vehicle.vin || vehicle.VIN || '').toLowerCase();
                      const model = (vehicle.model || '').toLowerCase();
                      const stockNumber = (vehicle.stockNumber || vehicle.stocknumber || vehicle['Stock Number'] || vehicle.StockNumber || '').toLowerCase();
                      const name = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ').toLowerCase();
                      return vin.includes(s) || model.includes(s) || name.includes(s) || stockNumber.includes(s);
                    }).filter(vehicle => {
                      // Missing info filters
                      if (!filterMissingVideo && !filterMissingAutoCheck && !filterMissingMsrp && !filterMissingInspection) {
                        return true; // No filters active, show all vehicles
                      }
                      
                      const vinSafe = (vehicle.vin || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                      const hasInspection = !!dealershipSettings?.inspectionPdfs?.get?.(vinSafe) || !!dealershipSettings?.inspectionPdfs?.[vinSafe];
                      const hasAutoCheck = !!dealershipSettings?.autoCheckPdfs?.get?.(vinSafe) || !!dealershipSettings?.autoCheckPdfs?.[vinSafe];
                      const hasMsrp = !!dealershipSettings?.msrpPdfs?.get?.(vinSafe) || !!dealershipSettings?.msrpPdfs?.[vinSafe];
                      const hasVideo = !!dealershipSettings?.vehicleVideos?.get?.(vinSafe) || !!dealershipSettings?.vehicleVideos?.[vinSafe];
                      
                      // Check if vehicle matches any of the active filters
                      let matchesFilter = false;
                      
                      if (filterMissingVideo && !hasVideo) matchesFilter = true;
                      if (filterMissingAutoCheck && !hasAutoCheck) matchesFilter = true;
                      if (filterMissingMsrp && !hasMsrp) matchesFilter = true;
                      if (filterMissingInspection && !hasInspection) matchesFilter = true;
                      
                      return matchesFilter;
                    });
                    
                    if (allVehicles.length === 0) {
                      return (
                        <div className="text-gray-400 text-center py-6">
                          <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          No vehicles found in inventory.
                        </div>
                      );
                    }
                    
                    if (filteredVehicles.length === 0) {
                      const hasFilters = filterMissingVideo || filterMissingAutoCheck || filterMissingMsrp || filterMissingInspection;
                      const hasSearch = settingsVehicleSearch.trim();
                      
                      if (hasFilters && hasSearch) {
                        return (
                          <div className="text-gray-400 text-center py-8">
                            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <div className="text-lg font-medium mb-2">No vehicles match your search and filters</div>
                            <div className="text-sm">Try adjusting your search terms or clearing some filters</div>
                          </div>
                        );
                      } else if (hasFilters) {
                        return (
                          <div className="text-gray-400 text-center py-8">
                            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <div className="text-lg font-medium mb-2">Great news! 🎉</div>
                            <div className="text-sm">No vehicles are missing the selected information</div>
                          </div>
                        );
                      } else if (hasSearch) {
                        return (
                          <div className="text-gray-400 text-center py-8">
                            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <div className="text-lg font-medium mb-2">No vehicles match your search</div>
                            <div className="text-sm">Try different search terms</div>
                          </div>
                        );
                      }
                    }
                    
                    return null;
                  })()}
                </div>
              </div>
              {/* Edit Vehicle Modal */}
              {editingVehicle && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm">
                  <div className="bg-gradient-to-br from-white via-gray-50 to-blue-50 rounded-3xl shadow-2xl border border-gray-200 w-full max-w-2xl mx-4 relative overflow-hidden">
                    {/* Header Section */}
                    <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 px-8 py-6 relative">
                      <button
                        className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors duration-200 p-2 rounded-full hover:bg-white/20"
                        onClick={() => setEditingVehicle(null)}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <div className="text-center">
                        <h2 className="text-3xl font-bold text-white mb-2">✏️ Edit Vehicle</h2>
                        <div className="text-xl font-semibold text-white/90 mb-1">
                          {editingVehicle.year} {editingVehicle.make} {editingVehicle.model}
                        </div>
                        <div className="flex justify-center gap-6 text-white/80 text-sm">
                          <span>Stock #: <span className="font-mono font-bold">{editingVehicle.stockNumber || editingVehicle.stocknumber || editingVehicle['Stock Number'] || editingVehicle.StockNumber || 'N/A'}</span></span>
                          <span>VIN: <span className="font-mono font-bold">{editingVehicle.vin || editingVehicle.VIN || 'N/A'}</span></span>
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-8 max-h-[80vh] overflow-y-auto">
                      <div className="space-y-6">
                        {/* Video URL Section */}
                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-800">Seller Video URL</h3>
                              <p className="text-sm text-gray-600">Add YouTube, Vimeo, or direct video link</p>
                            </div>
                          </div>
                          <input
                            type="text"
                            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-900 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                            placeholder="Paste YouTube, Vimeo, or direct video URL"
                            value={editVideoUrl}
                            onChange={handleEditChange(e => setEditVideoUrl(e.target.value))}
                            disabled={editLoading}
                          />
                          {(() => {
                            const vinSafe = (editingVehicle.vin || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                            const current = dealershipSettings?.vehicleVideos?.get?.(vinSafe) || dealershipSettings?.vehicleVideos?.[vinSafe];
                            return current ? (
                              <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center gap-2 text-green-700 text-sm">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  <span className="font-medium">Current: {current}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-2 text-gray-500 text-sm">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                  <span>No video uploaded</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* Inspection Forms Section */}
                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-800">Inspection Forms PDF</h3>
                              <p className="text-sm text-gray-600">Upload vehicle inspection documentation</p>
                            </div>
                          </div>
                          {(() => {
                            const vinSafe = (editingVehicle.vin || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                            const current = dealershipSettings?.inspectionPdfs?.get?.(vinSafe) || dealershipSettings?.inspectionPdfs?.[vinSafe];
                            if (current && !edit172File) {
                              return (
                                <div className="space-y-3">
                                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                    <div className="flex items-center gap-2 text-green-700 text-sm">
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                      <span className="font-medium">Already uploaded: </span>
                                      <a href={current} target="_blank" rel="noopener noreferrer" className="underline text-blue-600 hover:text-blue-800 font-medium">View PDF</a>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 rounded-xl font-semibold hover:from-yellow-600 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                                    onClick={() => setEdit172File('REPLACE')}
                                    disabled={editLoading}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Replace File
                                  </button>
                                </div>
                              );
                            }
                            return (
                              <div className="space-y-3">
                                <div className="relative">
                                  <input
                                    type="file"
                                    accept="application/pdf"
                                    className="w-full border-2 border-dashed border-gray-300 rounded-xl px-4 py-6 text-gray-900 bg-gray-50 hover:bg-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 cursor-pointer"
                                    onChange={handleEditChange(e => setEdit172File(e.target.files[0]))}
                                    disabled={editLoading}
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="text-center">
                                      <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                      </svg>
                                      <p className="text-sm text-gray-500">Click to upload PDF</p>
                                    </div>
                                  </div>
                                </div>
                                {edit172File && edit172File !== 'REPLACE' && (
                                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                    <div className="flex items-center gap-2 text-blue-700 text-sm">
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                      </svg>
                                      <span className="font-medium">Selected: {edit172File.name}</span>
                                    </div>
                                  </div>
                                )}
                                {!current && !edit172File && (
                                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      <span>No PDF uploaded</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* AutoCheck Section */}
                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <div>
                                <h3 className="text-lg font-bold text-gray-800">AutoCheck PDF</h3>
                                <p className="text-sm text-gray-600">Vehicle history report</p>
                              </div>
                            </div>
                            <button
                              onClick={handleFetchAutoCheck}
                              disabled={fetchingAutoCheck || editLoading}
                              className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
                            >
                              {fetchingAutoCheck ? (
                                <>
                                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Fetching...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                  </svg>
                                  Fetch AutoCheck
                                </>
                              )}
                            </button>
                          </div>
                          <div className="relative">
                            <input
                              type="file"
                              accept="application/pdf"
                              className="w-full border-2 border-dashed border-gray-300 rounded-xl px-4 py-6 text-gray-900 bg-gray-50 hover:bg-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 cursor-pointer"
                              onChange={handleEditChange(e => setEditAutoCheckFile(e.target.files[0]))}
                              disabled={editLoading}
                            />
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="text-center">
                                <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p className="text-sm text-gray-500">Click to upload PDF</p>
                              </div>
                            </div>
                          </div>
                          {editAutoCheckFile ? (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="flex items-center gap-2 text-blue-700 text-sm">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                <span className="font-medium">Selected: {editAutoCheckFile.name}</span>
                              </div>
                            </div>
                          ) : (() => {
                            const vinSafe = (editingVehicle.vin || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                            const current = dealershipSettings?.autoCheckPdfs?.get?.(vinSafe) || dealershipSettings?.autoCheckPdfs?.[vinSafe];
                            return current ? (
                              <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center gap-2 text-green-700 text-sm">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  <span className="font-medium">Already uploaded</span>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-2 text-gray-500 text-sm">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  <span>No PDF uploaded</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* MSRP Section */}
                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-800">Original MSRP PDF</h3>
                              <p className="text-sm text-gray-600">Manufacturer's suggested retail price document</p>
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div className="relative">
                              <input
                                type="file"
                                accept="application/pdf"
                                className="w-full border-2 border-dashed border-gray-300 rounded-xl px-4 py-6 text-gray-900 bg-gray-50 hover:bg-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 cursor-pointer"
                                onChange={handleEditChange(e => setEditMsrpFile(e.target.files[0]))}
                                disabled={editLoading}
                              />
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="text-center">
                                  <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                  </svg>
                                  <p className="text-sm text-gray-500">Click to upload PDF</p>
                                </div>
                              </div>
                            </div>
                            {editMsrpFile ? (
                              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center gap-2 text-blue-700 text-sm">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                  <span className="font-medium">Selected: {editMsrpFile.name}</span>
                                </div>
                              </div>
                            ) : (() => {
                              const vinSafe = (editingVehicle.vin || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                              const current = dealershipSettings?.msrpPdfs?.get?.(vinSafe) || dealershipSettings?.msrpPdfs?.[vinSafe];
                              return current ? (
                                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                  <div className="flex items-center gap-2 text-green-700 text-sm">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-medium">Already uploaded</span>
                                  </div>
                                </div>
                              ) : (
                                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <span>No PDF uploaded</span>
                                  </div>
                                </div>
                              );
                            })()}
                            
                            {/* Window Sticker Button */}
                            <button
                              onClick={handleFetchWindowSticker}
                              disabled={fetchingWindowSticker || editLoading}
                              className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                            >
                              {fetchingWindowSticker ? (
                                <>
                                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Fetching...
                                </>
                              ) : (
                                <>
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Get Window Sticker (Auto-Detect)
                                </>
                              )}
                            </button>
                            <p className="text-xs text-gray-500 text-center px-2">
                              Automatically detects the correct window sticker API based on vehicle make
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-8 flex gap-4">
                        <button
                          className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-200 hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                          onClick={handleUpdateVehicle}
                          disabled={editLoading}
                        >
                          {editLoading ? (
                            <>
                              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Updating...
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Update Vehicle
                            </>
                          )}
                        </button>
                      </div>

                      {/* Status Messages */}
                      {editError && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                          <div className="flex items-center gap-2 text-red-700">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium">{editError}</span>
                          </div>
                        </div>
                      )}
                      {editSuccess && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                          <div className="flex items-center gap-2 text-green-700">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium text-lg">Vehicle has been updated successfully! 🎉</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

                </div>
          </div>
        )}

        {viewLeadModal && selectedLead && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-2 sm:p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-3xl p-4 sm:p-6 relative overflow-y-auto max-h-screen sm:max-h-[95vh]">
              <button
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-3xl sm:text-2xl font-bold p-2 sm:p-1"
                onClick={() => setViewLeadModal(false)}
                style={{ minWidth: 44, minHeight: 44 }}
              >
                &times;
              </button>
              
              <div className="pr-12 sm:pr-8 mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 break-words">
                  Packet Links for {selectedLead.firstName} {selectedLead.lastName}
                </h2>
                <p className="text-sm text-gray-600">Manage customer packet links and view submission status</p>
              </div>

              {selectedPacketLogIds.length > 0 && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <button
                    className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete Selected ({selectedPacketLogIds.length})
                  </button>
                </div>
              )}

              {leadPacketLogsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Loading packet links...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {leadPacketLogs.length === 0 && (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-gray-500 text-lg font-medium">No packet links found</div>
                      <div className="text-gray-400 text-sm mt-1">This lead doesn't have any packet links yet.</div>
                    </div>
                  )}
                  {leadPacketLogs.map(log => {
                    // Check if customer has submitted on this log - check multiple possible fields
                    const submittedOptions = log.submittedOptions || log.submitted_options || log.options;
                    const hasSubmitted = submittedOptions && Object.keys(submittedOptions).length > 0;
                    
                    return (
                      <div 
                        key={log._id} 
                        className={`border rounded-xl p-4 transition-all duration-200 cursor-pointer ${
                        hasSubmitted 
                            ? 'bg-green-50 border-green-200 shadow-md' 
                            : 'bg-gray-50 border-gray-200 hover:shadow-sm'
                        } ${selectedPacketLogIds.includes(log._id) ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}`}
                        onClick={() => togglePacketLogSelection(log._id)}
                      >
                        {hasSubmitted && (
                          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-green-200">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="text-green-700 font-semibold text-sm">Customer Submitted Information</span>
                          </div>
                        )}
                        
                        <div className="flex items-start gap-4">
                        <input
                          type="checkbox"
                          checked={selectedPacketLogIds.includes(log._id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              togglePacketLogSelection(log._id);
                            }}
                            className="mt-1 flex-shrink-0 w-4 h-4 text-blue-600 rounded"
                          />
                          
                          <div className="flex-1 min-w-0 space-y-3">
                            {/* Link Display */}
                            <div className="bg-white rounded-lg p-3 border border-gray-200">
                              <div className="text-xs text-gray-500 mb-1">Packet Link:</div>
                              <div 
                                className="text-sm text-blue-700 hover:text-blue-900 cursor-pointer break-all font-mono"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(log.link, '_blank');
                                }}
                              >
                              {log.link}
                            </div>
                            </div>
                            
                            {/* Status Information */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500 font-medium">Created:</span>
                                <span className="text-gray-700">
                                  {log.createdAt ? new Date(log.createdAt).toLocaleString() : 'Unknown'}
                              </span>
                            </div>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500 font-medium">Status:</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  log.lastActiveAt && (now - new Date(log.lastActiveAt).getTime() < 10000) 
                                    ? "bg-green-100 text-green-700" 
                                    : "bg-gray-100 text-gray-600"
                                }`}>
                                  {log.lastActiveAt && (now - new Date(log.lastActiveAt).getTime() < 10000) 
                                    ? "● Active now" 
                                    : "Offline"}
                                </span>
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex flex-wrap gap-2 pt-2">
                        <button
                                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                            navigator.clipboard.writeText('https://buymycarnwi.com' + log.link);
                            setCopiedLogId(log._id);
                            setTimeout(() => setCopiedLogId(null), 2000);
                          }}
                        >
                                {copiedLogId === log._id ? 'Copied!' : 'Copy Link'}
                        </button>
                        <button
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCustomerInfoLog(log);
                                  setShowCustomerInfoModal(true);
                                }}
                        >
                                View Submitted Deal
                        </button>
                        <button
                                className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                onClick={async (e) => {
                                  e.stopPropagation();
                            // Fetch the latest log from the backend before showing the modal
                            const res = await fetch(`/api/dealerships/api/packet-logs/${log._id}`);
                            const latestLog = await res.json();
                            setCustomerLogsLog(latestLog);
                            // Set the session activity customer and link for the Session Activity button
                            const customerEmail = latestLog.customer?.email;
                            console.log('Setting sessionActivityCustomer to:', customerEmail);
                            console.log('Full customer object:', latestLog.customer);
                            setSessionActivityCustomer(customerEmail);
                            setSessionActivityLink(latestLog.link);
                            setShowCustomerLogsModal(true);
                          }}
                        >
                                Calculator Logs
                              </button>
                              <button
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  // Fetch the latest log from the backend before showing the modal
                                  const res = await fetch(`/api/dealerships/api/packet-logs/${log._id}`);
                                  const latestLog = await res.json();
                                  const customerEmail = latestLog.customer?.email;
                                  const dealershipId = latestLog.dealershipId;
                                  console.log('Session Activity button clicked');
                                  console.log('customerEmail:', customerEmail);
                                  console.log('dealershipId:', dealershipId);
                                  console.log('link:', latestLog.link);
                                  
                                  if (dealershipId && customerEmail) {
                                    console.log('Opening session activity modal...');
                                    openSessionActivityModal(customerEmail, dealershipId, latestLog.link);
                                  } else {
                                    console.log('Missing required data - dealershipId:', dealershipId, 'customerEmail:', customerEmail);
                                  }
                                }}
                              >
                                Session Activity
                              </button>
                              <button
                                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  // Fetch the latest log from the backend before showing the modal
                                  const res = await fetch(`/api/dealerships/api/packet-logs/${log._id}`);
                                  const latestLog = await res.json();
                                  const customerEmail = latestLog.customer?.email;
                                  const dealershipId = latestLog.dealershipId;
                                  console.log('Click Stream Log button clicked');
                                  console.log('customerEmail:', customerEmail);
                                  console.log('dealershipId:', dealershipId);
                                  console.log('link:', latestLog.link);
                                  
                                  if (dealershipId && customerEmail) {
                                    console.log('Opening click stream log modal...');
                                    openClickStreamLogModal(customerEmail, dealershipId, latestLog.link);
                                  } else {
                                    console.log('Missing required data - dealershipId:', dealershipId, 'customerEmail:', customerEmail);
                                  }
                                }}
                              >
                                Click Stream Log
                              </button>
                              <button
                                className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  // Fetch the latest log from the backend before showing the modal
                                  const res = await fetch(`/api/dealerships/api/packet-logs/${log._id}`);
                                  const latestLog = await res.json();
                                  setEmailTemplateLog(latestLog);
                                  setEmailCustomMessage('');
                                  setShowEmailTemplateModal(true);
                                }}
                              >
                                Email Template
                        </button>
                      </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Delete confirmation dialog */}
              {showDeleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                  <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full text-center">
                    <div className="text-lg font-semibold mb-4 text-gray-900">Are you sure you want to delete the selected packet link(s)?</div>
                    <div className="flex justify-center gap-4">
                      <button
                        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-semibold"
                        onClick={handleDeleteSelectedLogs}
                      >
                        Yes, Delete
                      </button>
                      <button
                        className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400 font-semibold"
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Customer Info Modal */}
        {showCustomerInfoModal && customerInfoLog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-2 sm:p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-3 sm:p-6 w-full max-w-full sm:max-w-md relative overflow-y-auto max-h-screen sm:max-h-[95vh]">
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-3xl sm:text-2xl font-bold p-2 sm:p-1"
                onClick={() => setShowCustomerInfoModal(false)}
                style={{ minWidth: 44, minHeight: 44 }}
              >
                &times;
              </button>
              <div className="text-lg sm:text-xl font-bold mb-4 text-blue-900 pr-12 sm:pr-8">Submitted Customer Info</div>
              {(() => {
                const submittedOptions =
                  customerInfoLog.submittedOptions ||
                  customerInfoLog.submitted_options ||
                  customerInfoLog.options;
                const vehicle = customerInfoLog.vehicle || {};
                // Try to get a valid price from multiple fields
                let price = vehicle.price || vehicle.internetPrice || vehicle.msrp || vehicle.MSRP;
                price = Number(price) || 0;
                // Calculate monthly payment if possible
                let monthlyPayment = null;
                if (price > 0 && submittedOptions && submittedOptions.Term && submittedOptions.APR !== undefined) {
                  const down = Number(submittedOptions.calcDownPayment) || 0;
                  const trade = submittedOptions.showTradeIn ? (Number(submittedOptions.tradeInValue) || 0) : 0;
                  const payoff = submittedOptions.hasTradePayoff ? (Number(submittedOptions.tradePayoff) || 0) : 0;
                  const principal = price - down - trade + payoff;
                  const apr = Number(submittedOptions.APR) / 100;
                  const n = Number(submittedOptions.Term);
                  if (principal > 0 && n > 0) {
                    const r = apr / 12;
                    monthlyPayment = r > 0
                      ? (principal * r) / (1 - Math.pow(1 + r, -n))
                      : principal / n;
                  }
                }
                if (monthlyPayment === null) {
                  console.log('Monthly payment not shown. Price:', price, 'Options:', submittedOptions);
                }
                return submittedOptions && Object.keys(submittedOptions).length > 0 ? (
                  <ul className="text-sm text-gray-800 list-disc list-inside bg-blue-50 border border-blue-200 rounded p-3">
                    {price > 0 && (
                      <li><b>Vehicle Price:</b> ${price.toLocaleString()}</li>
                    )}
                    {submittedOptions.monthlyPayment !== undefined && submittedOptions.monthlyPayment !== null && (
                      <li><b>Monthly Payment (Customer Selected):</b> ${Number(submittedOptions.monthlyPayment).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</li>
                    )}
                    {monthlyPayment !== null && submittedOptions.monthlyPayment === undefined && (
                      <li><b>Monthly Payment (Calculated):</b> ${monthlyPayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</li>
                    )}
                    <li><b>Payment Type:</b> {submittedOptions.paymentType}</li>
                    <li><b>Down Payment:</b> ${submittedOptions.calcDownPayment}</li>
                    <li><b>Term:</b> {submittedOptions.Term} months</li>
                    <li><b>Credit Score:</b> {submittedOptions.calcCreditScore}</li>
                    <li><b>APR:</b> {submittedOptions.APR}%</li>
                    <li><b>Include Taxes:</b> {submittedOptions.includeTaxes ? 'Yes' : 'No'}</li>
                    <li><b>Zip Code:</b> {submittedOptions.zipCode}</li>
                    <li><b>Tax Rate:</b> {submittedOptions.taxRate}%</li>
                    <li><b>Trade-In:</b> {submittedOptions.showTradeIn ? 'Yes' : 'No'}</li>
                    {submittedOptions.showTradeIn && (
                      <>
                        <li><b>Trade-In Value:</b> ${submittedOptions.tradeInValue}</li>
                        <li><b>Loan Payoff:</b> {submittedOptions.hasTradePayoff ? `$${submittedOptions.tradePayoff}` : 'No'}</li>
                      </>
                    )}
                  </ul>
                ) : (
                  <div className="text-gray-500 text-center py-6">No options have been submitted for this packet yet.</div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Customer Logs Modal */}
        {showCustomerLogsModal && customerLogsLog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-2 sm:p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-full sm:max-w-4xl relative overflow-hidden max-h-screen sm:max-h-[95vh]">
              
              {/* Modern Header with Gradient */}
              <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 px-6 py-6 relative">
              <button
                  className="absolute top-4 right-4 text-white hover:text-gray-200 text-2xl font-bold w-10 h-10 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center transition-all duration-200"
                onClick={() => setShowCustomerLogsModal(false)}
              >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
              </button>
                
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pr-14">
                  <div className="text-white mb-4 sm:mb-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c0 .621.504 1.125 1.125 1.125H18a2.25 2.25 0 002.25-2.25M6 7.5h3v3H6v-3z" />
                        </svg>
                      </div>
                      <h2 className="text-2xl font-bold">Customer Input Logs</h2>
                    </div>
                    <p className="text-blue-100 text-sm">Track customer input changes and interactions</p>
                  </div>
                  
                  <button
                    onClick={() => {
                      if (customerLogsLog?._id) {
                        fetchCustomerLogs(customerLogsLog._id);
                      }
                    }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-xl font-medium shadow-lg transition-all duration-200 hover:shadow-xl flex items-center gap-2 text-sm backdrop-blur-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 104.582 9m15.356 2V9m0 0h-3" />
                    </svg>
                    Refresh
                  </button>
                </div>
              </div>
              
              {/* Content Area */}
              <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 140px)' }}>
              {customerLogsLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="relative">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-indigo-600"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-6 h-6 bg-indigo-600 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <span className="ml-4 text-gray-600 font-medium">Loading customer logs...</span>
                </div>
              ) : customerLogsLog?.customerLogs && customerLogsLog.customerLogs.length > 0 ? (() => {
                // Get all unique fields, excluding user_interaction since it's shown in click stream
                const uniqueFields = Array.from(new Set(customerLogsLog.customerLogs.map(log => log.field))).filter(field => field !== 'user_interaction');
                // Set default tab if not set
                if (!activeLogTab && uniqueFields.length > 0) setActiveLogTab(uniqueFields[0]);
                // Filter logs for active tab
                const filteredLogs = customerLogsLog.customerLogs.filter(log => log.field === activeLogTab);
                const sortedLogs = [...filteredLogs].sort((a, b) => b.timestamp - a.timestamp); // Most recent first
                return (
                  <>
                      {/* Modern Tab Navigation */}
                      <div className="mb-8">
                        <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-hide">
                      {uniqueFields.map(field => (
                        <button
                          key={field}
                              className={`px-6 py-3 rounded-xl font-semibold text-sm focus:outline-none transition-all duration-300 whitespace-nowrap relative ${
                                activeLogTab === field 
                                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                              }`}
                          onClick={() => setActiveLogTab(field)}
                        >
                          {logFieldLabels[field] || field}
                              {activeLogTab === field && (
                                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-white rounded-full"></div>
                              )}
                        </button>
                      ))}
                    </div>
                      </div>
                      
                      {/* Modern Content Area */}
                      <div className="bg-gray-50 rounded-2xl p-6" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                      {/* Mobile Card View */}
                        <div className="block sm:hidden space-y-4">
                        {sortedLogs.map((log, idx) => (
                            <div key={idx} className={`rounded-2xl p-5 border-2 shadow-sm transition-all duration-200 hover:shadow-md ${
                              idx === 0 
                                ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 shadow-amber-100' 
                                : 'bg-white border-gray-200'
                            }`}>
                              {idx === 0 && (
                                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-amber-200">
                                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                                  <span className="text-amber-700 font-semibold text-xs uppercase tracking-wider">Most Recent</span>
                                </div>
                              )}
                              
                              <div className="flex items-center gap-2 mb-4">
                                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-indigo-600">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <div className="text-sm text-gray-500 font-medium">
                              {new Date(log.timestamp).toLocaleString()}
                            </div>
                              </div>
                              
                              <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3 text-red-600">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
                                    </svg>
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Previous Value</div>
                                    <div className="text-sm font-medium text-gray-800">{String(log.oldValue)}</div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-3">
                                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3 text-green-600">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                    </svg>
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">New Value</div>
                                    <div className="text-sm font-medium text-gray-800">{String(log.newValue)}</div>
                                  </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Desktop Table View */}
                        <div className="hidden sm:block">
                          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                              <div className="grid grid-cols-3 gap-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                  <span className="font-semibold text-gray-700 text-sm">Previous Value</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  <span className="font-semibold text-gray-700 text-sm">New Value</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  <span className="font-semibold text-gray-700 text-sm">Timestamp</span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="divide-y divide-gray-100">
                          {sortedLogs.map((log, idx) => (
                                <div key={idx} className={`grid grid-cols-3 gap-4 px-6 py-4 transition-all duration-200 hover:bg-gray-50 ${
                                  idx === 0 
                                    ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400' 
                                    : 'bg-white'
                                }`}>
                                  {idx === 0 && (
                                    <div className="col-span-3 flex items-center gap-2 mb-2 pb-2 border-b border-amber-200">
                                      <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                                      <span className="text-amber-700 font-semibold text-xs uppercase tracking-wider">Most Recent Change</span>
                                    </div>
                                  )}
                                  
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-red-600">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
                                      </svg>
                                    </div>
                                    <span className="text-gray-800 font-medium">{String(log.oldValue)}</span>
                                  </div>
                                  
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-600">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                      </svg>
                                    </div>
                                    <span className="text-gray-800 font-medium">{String(log.newValue)}</span>
                                  </div>
                                  
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-blue-600">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    </div>
                                    <span className="text-gray-600 font-medium text-sm">
                                      {new Date(log.timestamp).toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                    </div>
                  </>
                );
              })() : (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10 text-gray-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c0 .621.504 1.125 1.125 1.125H18a2.25 2.25 0 002.25-2.25M6 7.5h3v3H6v-3z" />
                      </svg>
                    </div>
                    <div className="text-xl font-semibold text-gray-700 mb-2">No Input Logs Found</div>
                    <div className="text-gray-500">This customer hasn't made any input changes yet.</div>
                </div>
              )}
              </div>
            </div>
          </div>
        )}

        {/* Session Activity Modal */}
        {showSessionActivityModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-2 sm:p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-full sm:max-w-5xl relative overflow-hidden max-h-screen sm:max-h-[95vh]">
              
              {/* Modern Header with Gradient */}
              <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 px-6 py-6 relative">
              <button
                  className="absolute top-4 right-4 text-white hover:text-gray-200 text-2xl font-bold w-10 h-10 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center transition-all duration-200"
                onClick={() => {
                  setShowSessionActivityModal(false);
                  setIsClickStreamMode(false); // Reset mode when closing
                }}
              >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
              </button>
                
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pr-14">
                  <div className="text-white mb-4 sm:mb-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold">Session Activity</h2>
                        <div className="text-sm text-white break-all">
                          Customer: {sessionActivityCustomer}
                        </div>
                      </div>
                    </div>
                  {sessionActivityLink && sessionActivityLink.length > 0 && (
                      <div className="text-sm text-teal-100 bg-white bg-opacity-10 rounded-lg px-3 py-2 inline-block">
                        <span className="font-medium">Link:</span> ...{sessionActivityLink.slice(-30)}
                    </div>
                  )}
                </div>
                  
                <button
                  onClick={() => {
                    if (sessionActivityDealershipId && sessionActivityCustomer) {
                      setSessionActivityLoading(true);
                      
                      // Check if we're in click stream mode
                      if (isClickStreamMode) {
                        // This is click stream data - call the click stream API
                        fetch(`/api/dealerships/${sessionActivityDealershipId}/click-stream-log/${encodeURIComponent(sessionActivityCustomer)}?link=${encodeURIComponent(sessionActivityLink || '')}&_t=${Date.now()}`, {
                          headers: {
                            'Cache-Control': 'no-cache, no-store, must-revalidate',
                            'Pragma': 'no-cache',
                            'Expires': '0'
                          }
                        })
                          .then(res => res.json())
                          .then(data => {
                            setSessionActivityData(data);
                            setSessionActivityLoading(false);
                          })
                          .catch(err => {
                            console.error('Error fetching click stream data:', err);
                            setSessionActivityLoading(false);
                          });
                      } else {
                        // This is regular session activity data
                      fetchSessionActivity(sessionActivityCustomer, sessionActivityDealershipId, sessionActivityLink);
                      }
                    }
                  }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-xl font-medium shadow-lg transition-all duration-200 hover:shadow-xl flex items-center gap-2 text-sm backdrop-blur-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 104.582 9m15.356 2V9m0 0h-3" />
                  </svg>
                  Refresh
                </button>
                </div>
              </div>
              
              {/* Content Area */}
              <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 140px)' }}>
              {sessionActivityLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="relative">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-emerald-600"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-6 h-6 bg-emerald-600 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <span className="ml-4 text-gray-600 font-medium">Loading session activity...</span>
                </div>
              ) : sessionActivityData.length > 0 ? (
                  <div className="bg-gray-50 rounded-2xl p-6" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {/* Check if this is click stream data */}
                  {sessionActivityData[0]?.type ? (() => {
                    // Get all unique interaction types, excluding photos_available
                    const uniqueTypes = Array.from(new Set(sessionActivityData.map(interaction => interaction.type))).filter(type => type !== 'photos_available');
                    // Set default tab if not set
                    if (!activeClickStreamTab && uniqueTypes.length > 0) setActiveClickStreamTab(uniqueTypes[0]);
                    // Filter interactions for active tab (excluding photos_available)
                    const filteredInteractions = sessionActivityData.filter(interaction => interaction.type === activeClickStreamTab && interaction.type !== 'photos_available');
                    const sortedInteractions = [...filteredInteractions].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // Most recent first
                    
                    return (
                      <>
                          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-purple-600">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                                </svg>
                              </div>
                              <h3 className="text-lg font-semibold text-purple-900">Click Stream Interactions</h3>
                            </div>
                            <p className="text-sm text-purple-700">Customer interaction tracking and behavior analysis</p>
                          </div>
                          
                          {/* Modern Tab Navigation */}
                          <div className="mb-8">
                            <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-hide">
                          {uniqueTypes.map(type => (
                            <button
                              key={type}
                                  className={`px-6 py-3 rounded-xl font-semibold text-sm focus:outline-none transition-all duration-300 whitespace-nowrap relative ${
                                    activeClickStreamTab === type 
                                      ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg transform scale-105' 
                                      : 'bg-white text-gray-700 hover:bg-gray-100 hover:shadow-md'
                                  }`}
                              onClick={() => setActiveClickStreamTab(type)}
                            >
                              {clickStreamLabels[type] || type} ({sessionActivityData.filter(i => i.type === type).length})
                                  {activeClickStreamTab === type && (
                                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-white rounded-full"></div>
                                  )}
                            </button>
                          ))}
                            </div>
                        </div>
                        
                          {/* Modern Content Area */}
                          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                          {/* Mobile Card View */}
                            <div className="block sm:hidden p-4">
                              <div className="space-y-4">
                            {sortedInteractions.map((interaction, idx) => (
                                  <div key={idx} className={`rounded-2xl p-4 border-2 shadow-sm transition-all duration-200 hover:shadow-md ${
                                    idx === 0 
                                      ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200' 
                                      : 'bg-white border-gray-200'
                                  }`}>
                                    {idx === 0 && (
                                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-purple-200">
                                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                                        <span className="text-purple-700 font-semibold text-xs uppercase tracking-wider">Most Recent</span>
                                  </div>
                                    )}
                                    
                                    <div className="flex items-center gap-2 mb-3">
                                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                        <span className="text-purple-600 font-bold text-xs">#{idx + 1}</span>
                                      </div>
                                      <div className="text-sm text-gray-500 font-medium">
                                    {new Date(interaction.timestamp).toLocaleString()}
                                  </div>
                                </div>
                                    
                                <div className="text-sm text-gray-700">
                                  {interaction.data && typeof interaction.data === 'object' ? (
                                        <div className="space-y-2">
                                      {Object.entries(interaction.data).map(([key, value]) => (
                                            <div key={key} className="flex items-center gap-3">
                                              <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                                                <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                                              </div>
                                              <div className="flex-1">
                                                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            {key === 'totalPhotos' ? 'Total Photos Available' : 
                                             key === 'direction' ? 'Direction' : 
                                             key === 'type' ? 'Action Type' : 
                                             key === 'platform' ? 'Video Platform' :
                                                   key === 'url' ? 'Video URL' : key}
                                                </div>
                                                <div className="text-sm font-medium text-gray-800">
                                               {key === 'url' && String(value).length > 40 ? 
                                                 `...${String(value).slice(-30)}` : 
                                                 String(value)}
                                                </div>
                                              </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                        <div className="text-center py-3 text-gray-500">
                                          {interaction.data || 'No additional data'}
                                        </div>
                                  )}
                                </div>
                              </div>
                            ))}
                              </div>
                          </div>
                          
                          {/* Desktop Table View */}
                            <div className="hidden sm:block">
                              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                    <span className="font-semibold text-gray-700 text-sm">Timestamp</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                    <span className="font-semibold text-gray-700 text-sm">Interaction Data</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="divide-y divide-gray-100">
                              {sortedInteractions.map((interaction, idx) => (
                                  <div key={idx} className={`grid grid-cols-2 gap-4 px-6 py-4 transition-all duration-200 hover:bg-gray-50 ${
                                    idx === 0 
                                      ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-400' 
                                      : 'bg-white'
                                  }`}>
                                    {idx === 0 && (
                                      <div className="col-span-2 flex items-center gap-2 mb-2 pb-2 border-b border-purple-200">
                                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                                        <span className="text-purple-700 font-semibold text-xs uppercase tracking-wider">Most Recent Interaction</span>
                                      </div>
                                    )}
                                    
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-purple-600">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                      </div>
                                      <span className="text-gray-800 font-medium text-sm">
                                    {new Date(interaction.timestamp).toLocaleString()}
                                      </span>
                                    </div>
                                    
                                    <div className="text-sm text-gray-700">
                                    {interaction.data && typeof interaction.data === 'object' ? (
                                        <div className="space-y-2">
                                        {Object.entries(interaction.data).map(([key, value]) => (
                                            <div key={key} className="flex items-center gap-3">
                                              <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                              </div>
                                              <div className="flex-1">
                                                <span className="font-medium text-gray-600 text-xs uppercase tracking-wider">
                                              {key === 'totalPhotos' ? 'Total Photos Available' : 
                                               key === 'direction' ? 'Direction' : 
                                               key === 'type' ? 'Action Type' : 
                                               key === 'platform' ? 'Video Platform' :
                                                   key === 'url' ? 'Video URL' : key}
                                            </span>
                                                <div className="text-gray-800 font-medium">
                                              {key === 'url' && String(value).length > 40 ? 
                                                `...${String(value).slice(-30)}` : 
                                                String(value)}
                                                </div>
                                              </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                        <div className="text-center py-3 text-gray-500">
                                          {interaction.data || 'No additional data'}
                                        </div>
                                    )}
                                    </div>
                                  </div>
                              ))}
                              </div>
                            </div>
                        </div>
                      </>
                    );
                  })() : (
                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                          <div className="grid grid-cols-7 gap-2 text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span className="font-semibold text-gray-700">Vehicle</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="font-semibold text-gray-700">Session</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                              <span className="font-semibold text-gray-700">Link</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                              <span className="font-semibold text-gray-700">Created</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                              <span className="font-semibold text-gray-700">Opened</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                              <span className="font-semibold text-gray-700">Closed</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                              <span className="font-semibold text-gray-700">Status</span>
                            </div>
                          </div>
                        </div>
                        
                                     {/* Mobile Card View */}
                        <div className="block sm:hidden p-4">
                          <div className="space-y-4">
                     {sessionActivityData.map((session, idx) => {
                       const sessionNumber = idx + 1;
                       
                       return (
                                <div key={idx} className="rounded-2xl p-4 border-2 shadow-sm transition-all duration-200 hover:shadow-md bg-white border-gray-200">
                                  <div className="flex justify-between items-start mb-4">
                               <div className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-blue-600">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0V8.25a1.5 1.5 0 013 0v10.5zm6-13.5a1.5 1.5 0 013 0v6.75a1.5 1.5 0 01-3 0V5.25z" />
                                        </svg>
                                      </div>
                                      <div>
                                 <div className="text-sm font-semibold text-gray-900">
                                   {session.vehicle?.year} {session.vehicle?.make} {session.vehicle?.model}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          VIN: {session.vehicle?.vin || 'N/A'}
                                        </div>
                                      </div>
                                 </div>
                                 {sessionActivityData.length > 1 && (
                                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                                     Session #{sessionNumber}
                                   </span>
                                 )}
                           </div>
                         
                                  <div className="text-xs text-blue-600 cursor-pointer hover:text-blue-800 truncate mb-4 p-2 bg-blue-50 rounded-lg" 
                              onClick={() => window.open('https://buymycarnwi.com' + session.link, '_blank')}>
                                    <span className="font-medium">Link:</span> ...{session.link.slice(-30)}
                         </div>
                         
                                  <div className="grid grid-cols-2 gap-4 text-xs mb-4">
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 bg-orange-100 rounded-full flex items-center justify-center">
                                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                               </div>
                                        <span className="font-medium text-gray-700">Created</span>
                                 </div>
                                      <div className="text-gray-600 ml-6">
                                        {session.createdAt ? new Date(session.createdAt).toLocaleDateString() : 'Unknown'}
                                      </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 bg-cyan-100 rounded-full flex items-center justify-center">
                                          <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                                        </div>
                                        <span className="font-medium text-gray-700">Opened</span>
                                      </div>
                                      <div className="text-gray-600 ml-6">
                                        {session.openedAt ? new Date(session.openedAt).toLocaleDateString() : 'Never'}
                             </div>
                           </div>
                         </div>
                         
                                  <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                           <div className="flex items-center gap-2">
                                      <div className="w-4 h-4 bg-indigo-100 rounded-full flex items-center justify-center">
                                        <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                                      </div>
                             <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                               (session.submittedOptions && Object.keys(session.submittedOptions).length > 0)
                                 ? 'bg-green-100 text-green-800'
                                 : session.opened
                                 ? 'bg-blue-100 text-blue-800'
                                 : 'bg-gray-100 text-gray-800'
                             }`}>
                               {(session.submittedOptions && Object.keys(session.submittedOptions).length > 0)
                                 ? 'Submitted'
                                 : session.opened
                                 ? 'Opened'
                                 : 'Created'}
                             </span>
                           </div>
                           {session.lastClosedAt && (
                                      <div className="text-xs text-gray-600 text-right">
                               <div className="font-medium">Closed:</div>
                               <div>{new Date(session.lastClosedAt).toLocaleDateString()}</div>
                             </div>
                           )}
                         </div>
                       </div>
                       );
                     })}
                          </div>
                   </div>
                  
                  {/* Desktop Table View */}
                        <div className="hidden sm:block">
                          <div className="divide-y divide-gray-100">
                      {sessionActivityData.map((session, idx) => {
                        const sessionNumber = idx + 1;
                        
                        return (
                                <div key={idx} className="grid grid-cols-7 gap-4 px-6 py-4 transition-all duration-200 hover:bg-gray-50 bg-white">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-blue-600">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0V8.25a1.5 1.5 0 013 0v10.5zm6-13.5a1.5 1.5 0 013 0v6.75a1.5 1.5 0 01-3 0V5.25z" />
                                      </svg>
                                    </div>
                                    <div>
                              <div className="font-medium text-gray-900 text-sm">
                                {session.vehicle?.year} {session.vehicle?.make} {session.vehicle?.model}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                VIN: {session.vehicle?.vin || 'N/A'}
                              </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center justify-center">
                                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                                #{sessionNumber}
                              </span>
                                  </div>
                                  
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-purple-600">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                                      </svg>
                                    </div>
                              <div className="text-xs text-blue-600 cursor-pointer hover:text-blue-800 truncate" 
                                   onClick={() => window.open('https://buymycarnwi.com' + session.link, '_blank')}
                                   title={session.link}>
                                ...{session.link.slice(-15)}
                              </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-orange-600">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                      </svg>
                                    </div>
                                    <div className="text-xs">
                                      <div className="text-gray-800 font-medium">
                              {session.createdAt ? new Date(session.createdAt).toLocaleDateString() : 'Unknown'}
                            </div>
                                      <div className="text-gray-500">
                              {session.createdAt ? new Date(session.createdAt).toLocaleTimeString() : ''}
                            </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-cyan-600">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                      </svg>
                                    </div>
                                    <div className="text-xs">
                                      <div className="text-gray-800 font-medium">
                              {session.openedAt ? new Date(session.openedAt).toLocaleDateString() : 'Never'}
                            </div>
                                      <div className="text-gray-500">
                              {session.openedAt ? new Date(session.openedAt).toLocaleTimeString() : ''}
                            </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-red-600">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </div>
                                    <div className="text-xs">
                                      <div className="text-gray-800 font-medium">
                              {session.lastClosedAt ? new Date(session.lastClosedAt).toLocaleDateString() : 'N/A'}
                            </div>
                                      <div className="text-gray-500">
                              {session.lastClosedAt ? new Date(session.lastClosedAt).toLocaleTimeString() : ''}
                            </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-indigo-600">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                               (session.submittedOptions && Object.keys(session.submittedOptions).length > 0)
                                ? 'bg-green-100 text-green-800'
                                : session.opened
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                               {(session.submittedOptions && Object.keys(session.submittedOptions).length > 0)
                                ? 'Submitted'
                                : session.opened
                                ? 'Opened'
                                : 'Created'}
                            </span>
                                  </div>
                                </div>
                        );
                      })}
                          </div>
                        </div>
                      </div>
                  )}
                </div>
              ) : (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10 text-gray-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                      </svg>
                    </div>
                    <div className="text-xl font-semibold text-gray-700 mb-2">No Session Activity Found</div>
                    <div className="text-gray-500">This customer hasn't had any session activity yet.</div>
                </div>
              )}
              </div>
            </div>
          </div>
        )}

        {showSendPacketModal && selectedVehicleForPacket && packetDealership && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <div className="send-packet-modal bg-white rounded-2xl shadow-2xl p-6 w-full max-w-6xl relative overflow-y-auto max-h-screen" style={{ position: 'relative' }}>
              {/* Close Button */}
              <button
                className="absolute top-3 right-3 text-black bg-white rounded-full text-2xl font-bold z-20 p-1.5 shadow focus:outline-none focus:ring-2 focus:ring-blue-400"
                style={{ minWidth: 36, minHeight: 36 }}
                onClick={() => {
                  setShowSendPacketModal(false);
                  setPastedCustomerText('');
                  setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
                  setSelectedCustomer(null);
                  setCustomerSearch('');
                  setCustomerResults([]);
                  setLastGeneratedLink('');
                }}
                aria-label="Close"
              >
                &times;
              </button>

              {/* Vehicle Package Checklist */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 mb-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Vehicle Package Status
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {/* Photos */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        selectedVehicleForPacket.imageOne || selectedVehicleForPacket['Image One'] || 
                        selectedVehicleForPacket.ImageOne || selectedVehicleForPacket.image1 || 
                        selectedVehicleForPacket.Image1 ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-sm font-medium text-slate-700">Photos</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedVehicleForPacket.imageOne || selectedVehicleForPacket['Image One'] || 
                      selectedVehicleForPacket.ImageOne || selectedVehicleForPacket.image1 || 
                      selectedVehicleForPacket.Image1 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedVehicleForPacket.imageOne || selectedVehicleForPacket['Image One'] || 
                       selectedVehicleForPacket.ImageOne || selectedVehicleForPacket.image1 || 
                       selectedVehicleForPacket.Image1 ? '✓' : '✗'}
                    </div>
                  </div>

                  {/* Video */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        selectedVehicleForPacket.videoUrl ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-sm font-medium text-slate-700">Video</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedVehicleForPacket.videoUrl 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedVehicleForPacket.videoUrl ? '✓' : '✗'}
                    </div>
                  </div>

                  {/* AutoCheck */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        selectedVehicleForPacket.autoCheckPdfUrl ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-sm font-medium text-slate-700">AutoCheck</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedVehicleForPacket.autoCheckPdfUrl 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedVehicleForPacket.autoCheckPdfUrl ? '✓' : '✗'}
                    </div>
                  </div>

                  {/* Inspection */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        selectedVehicleForPacket.inspectionPdfUrl ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-sm font-medium text-slate-700">Inspection</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedVehicleForPacket.inspectionPdfUrl 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedVehicleForPacket.inspectionPdfUrl ? '✓' : '✗'}
                    </div>
                  </div>

                  {/* MSRP */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        selectedVehicleForPacket.msrpPdfUrl ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-sm font-medium text-slate-700">MSRP</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedVehicleForPacket.msrpPdfUrl 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedVehicleForPacket.msrpPdfUrl ? '✓' : '✗'}
                    </div>
                  </div>

                  {/* Calculator */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-sm font-medium text-slate-700">Calculator</span>
                    </div>
                    <div className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-sm font-medium text-blue-800 mb-1">Package Completeness</div>
                  <div className="text-xs text-blue-600">
                    {(() => {
                      const items = [
                        selectedVehicleForPacket.imageOne || selectedVehicleForPacket['Image One'] || 
                        selectedVehicleForPacket.ImageOne || selectedVehicleForPacket.image1 || 
                        selectedVehicleForPacket.Image1,
                        selectedVehicleForPacket.videoUrl,
                        selectedVehicleForPacket.autoCheckPdfUrl,
                        selectedVehicleForPacket.inspectionPdfUrl,
                        selectedVehicleForPacket.msrpPdfUrl,
                        true // Calculator always available
                      ];
                      const available = items.filter(Boolean).length;
                      const total = items.length;
                      const percentage = Math.round((available / total) * 100);
                      return `${available}/${total} items available (${percentage}%)`;
                    })()}
                  </div>
                </div>
              </div>

              {/* Vehicle Summary */}
              <div className="flex gap-4 mb-4">
                <img 
                  src={selectedVehicleForPacket.imageOne || selectedVehicleForPacket['Image One'] || selectedVehicleForPacket.ImageOne || selectedVehicleForPacket.image1 || selectedVehicleForPacket.Image1 || 'https://via.placeholder.com/150x100?text=No+Image'} 
                  alt={`${selectedVehicleForPacket.year} ${selectedVehicleForPacket.make} ${selectedVehicleForPacket.model}`} 
                  className="w-32 h-24 object-cover rounded-lg border" 
                />
                <div>
                  <div className="text-xl font-bold">
                    {selectedVehicleForPacket.year} {selectedVehicleForPacket.make} {selectedVehicleForPacket.model} {selectedVehicleForPacket.trim}
                  </div>
                  <div className="text-green-700 font-bold text-lg">
                    {typeof selectedVehicleForPacket.price === 'number' ? `$${selectedVehicleForPacket.price.toLocaleString()}` : selectedVehicleForPacket.price || 'Call for Price'}
                  </div>
                  <div className="text-xs text-gray-500">VIN: <span className="font-mono">{selectedVehicleForPacket.vin}</span></div>
                  <div className="text-xs text-gray-500">Stock #: {selectedVehicleForPacket.stockNumber}</div>
                  <div className="text-xs text-gray-500">Miles: <span className="font-bold">{selectedVehicleForPacket.mileage ? Number(selectedVehicleForPacket.mileage).toLocaleString() : 'N/A'}</span></div>
                </div>
              </div>

              {/* Shareable Web Link Only */}
              <div className="mb-4">
                <div className="text-lg font-bold text-gray-800 mb-2">Generate Shareable Web Link</div>
                <div className="flex gap-2 items-center">
                  <div className="flex-1 bg-gray-50 rounded-xl border border-gray-200 shadow-sm p-4">
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base bg-gray-100 text-gray-900"
                      value={lastGeneratedLink}
                      readOnly
                      placeholder="Select a customer and click 'Generate Link' to create a shareable URL"
                      onFocus={e => e.target.select()}
                    />
                    {lastGeneratedLink && <div className="text-xs text-gray-500 mt-1">Copy and share this link with your customer.</div>}
                  </div>
                  <button
                    className="ml-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold shadow hover:bg-blue-700 transition"
                    onClick={handleGenerateNewLink}
                    type="button"
                    disabled={!selectedCustomer}
                  >
                    Generate Link
                  </button>
                </div>
                {lastGeneratedLink && (
                  <div className="mt-2 flex gap-2">
                    <button
                      className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold shadow hover:bg-green-700 transition"
                      onClick={async () => {
                        await navigator.clipboard.writeText(lastGeneratedLink);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      type="button"
                    >
                      {copied ? 'Copied!' : 'Copy Link'}
                    </button>
                  </div>
                )}
              </div>

              {/* Customer Search/Add Section */}
              <div className="mb-4">
                <div className="text-lg font-bold text-gray-800 mb-2">Search or Add Customer</div>
                {customerAdded && (
                  <div className="w-full bg-green-500 text-white text-center py-2 rounded mb-2 font-semibold shadow">Customer added!</div>
                )}
                {!selectedCustomer ? (
                  <>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 text-black text-sm sm:text-base"
                      placeholder="Search by name, email, or phone..."
                      value={customerSearch}
                      onChange={e => setCustomerSearch(e.target.value)}
                    />
                    {customerSearchLoading && <div className="text-xs text-gray-500">Searching...</div>}
                    {customerResults.length > 0 && (
                      <div className="bg-white border rounded shadow max-h-40 overflow-y-auto mb-2 w-full text-sm sm:text-base">
                        {customerResults.map(c => (
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-3 sm:px-4 py-2 hover:bg-blue-100 cursor-pointer gap-1 sm:gap-0" key={c._id || c.email}>
                            <span className="text-sm sm:text-base" onClick={() => { setSelectedCustomer(c); setCustomerSearch(''); setCustomerResults([]); }}>
                              {c.firstName || ''} {c.lastName || ''} ({c.email || c.phone || 'No contact'})
                            </span>
                            <button
                                                          onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this customer?')) {
                                let res;
                                
                                // Get user info for logging
                                const userRole = localStorage.getItem('adminRole');
                                const userEmail = localStorage.getItem('adminEmail');
                                const userName = localStorage.getItem('adminName');
                                const userId = localStorage.getItem('adminId');
                                const userParams = new URLSearchParams({
                                  userRole: userRole || 'admin',
                                  userEmail: userEmail || 'admin@system.com',
                                  userName: userName || 'Admin',
                                  userId: userId || 'system'
                                });
                                
                                if (c._id && packetDealership._id) {
                                  res = await fetch(`/api/dealerships/${packetDealership._id}/customers/${c._id}?${userParams}`, { method: 'DELETE' });
                                  if (res.status === 404 && c.leadId) {
                                    res = await fetch(`/api/dealerships/${packetDealership._id}/leads/${c.leadId}`, { method: 'DELETE' });
                                  }
                                } else if (c.leadId && packetDealership._id) {
                                  res = await fetch(`/api/dealerships/${packetDealership._id}/leads/${c.leadId}`, { method: 'DELETE' });
                                }
                                if (res && res.ok) {
                                  setCustomerResults(customerResults.filter(customer => customer._id !== c._id && customer.leadId !== c.leadId));
                                } else {
                                  alert('Failed to delete customer.');
                                }
                              }
                            }}
                              className="text-red-500 hover:text-red-700 font-bold text-lg sm:ml-2 self-end sm:self-auto"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="font-bold text-green-700 mb-2 mt-4 sm:mt-0">Add New Customer</div>
                    
                    {/* Auto-Parse Customer Info Section */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 mb-4">
                      <div className="font-semibold text-blue-800 mb-2">📋 Quick Paste & Parse</div>
                      <div className="text-xs text-blue-600 mb-2">Paste customer info (name, phone, email) and it will auto-fill the form below</div>
                      <textarea
                        className="w-full border border-blue-300 rounded-lg px-2 py-2 text-sm"
                        placeholder={`Example:
John Smith
(Individual) 
C: (555) 123-4567

john.smith@email.com
Chicago, IL, 60601`}
                        rows="4"
                        value={pastedCustomerText}
                        onChange={(e) => {
                          setPastedCustomerText(e.target.value);
                          if (e.target.value.trim()) {
                            parseCustomerText(e.target.value);
                          }
                        }}
                      />
                      <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <button
                          type="button"
                          className="w-full sm:w-auto px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition"
                          onClick={() => parseCustomerText(pastedCustomerText)}
                        >
                          Parse Info
                        </button>
                        <button
                          type="button"
                          className="w-full sm:w-auto px-3 py-1 bg-gray-400 text-white rounded text-sm hover:bg-gray-500 transition"
                          onClick={() => {
                            setPastedCustomerText('');
                            setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
                          }}
                        >
                          Clear All
                        </button>
                      </div>
                    </div>

                    <form onSubmit={handleAddCustomer} className="bg-gray-50 border rounded-xl p-3 sm:p-4 mt-2">
                      <div className="flex flex-col sm:flex-row gap-2 mb-2">
                        <input
                          type="text"
                          className="w-full sm:flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base"
                          placeholder="First Name"
                          value={newCustomer.firstName}
                          onChange={e => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
                          required
                        />
                        <input
                          type="text"
                          className="w-full sm:flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base"
                          placeholder="Last Name"
                          value={newCustomer.lastName}
                          onChange={e => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
                          required
                        />
                      </div>
                      <input
                        type="email"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 text-sm sm:text-base"
                        placeholder="Email"
                        value={newCustomer.email}
                        onChange={e => setNewCustomer({ ...newCustomer, email: e.target.value })}
                      />
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 text-sm sm:text-base"
                        placeholder="Mobile Number"
                        value={newCustomer.phone}
                        onChange={e => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                      />
                      <button
                        type="submit"
                        className="w-full bg-green-600 text-white px-4 py-2 rounded-lg font-semibold mt-2 hover:bg-green-700 transition text-sm sm:text-base"
                      >
                        Add New Customer
                      </button>
                    </form>
                  </>
                ) : (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 bg-gray-50 border rounded-xl p-3 sm:p-4">
                    <div className="font-semibold text-gray-800 text-sm sm:text-base">{selectedCustomer.firstName} {selectedCustomer.lastName}</div>
                    <div className="text-gray-600 text-sm">{selectedCustomer.email} {selectedCustomer.phone && `| ${selectedCustomer.phone}`}</div>
                    <button
                      className="w-full sm:w-auto sm:ml-auto px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm sm:text-base"
                      onClick={() => setSelectedCustomer(null)}
                      type="button"
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>


            </div>
          </div>
        )}

        {/* Delete Lead Confirmation Modal */}
        {showDeleteLeadConfirm && leadToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md p-6 relative">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Delete Lead
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Are you sure you want to delete <span className="font-medium text-gray-900">{leadToDelete.firstName} {leadToDelete.lastName}</span>?
                <br />
                <br />
                This will permanently remove:
                <br />
                • The lead record
                <br />
                • All associated customer records
                <br />
                • All packet logs and activity
                <br />
                <br />
                <span className="text-red-600 font-medium">This action cannot be undone.</span>
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteLeadConfirm(false);
                    setLeadToDelete(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteLead}
                  disabled={deletingLeadId === leadToDelete._id}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium disabled:opacity-50"
                >
                  {deletingLeadId === leadToDelete._id ? (
                    <span className="inline-flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </span>
                  ) : (
                    'Delete Lead'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lead Assignment Modal */}
        {showAssignModal && leadToAssign && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md p-6 relative">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.121M7 20v-2c0-.656.126-1.283.356-1.857M16 4a3 3 0 11-6 0 3 3 0 016 0zm-3 7a6 6 0 00-6 6v2h12v-2a6 6 0 00-6-6z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Assign Lead
              </h3>
              <p className="text-sm text-gray-600 text-center mb-4">
                Assign <span className="font-medium text-gray-900">{leadToAssign.firstName} {leadToAssign.lastName}</span> to a team member
              </p>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign to:
                </label>
                <select
                  value={selectedAssignee}
                  onChange={(e) => setSelectedAssignee(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Unassigned</option>
                  {dealershipAdmins.length > 0 && (
                    <optgroup label="Admins">
                      {dealershipAdmins.map(admin => (
                        <option key={admin._id} value={admin.email}>
                          {admin.name} ({admin.email})
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {salesTeamMembers.length > 0 && (
                    <optgroup label="Sales Team">
                      {salesTeamMembers.map(member => (
                        <option key={member._id} value={member.email}>
                          {member.name} ({member.email})
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={closeAssignModal}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium"
                  disabled={assignmentLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignLead}
                  disabled={assignmentLoading}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium disabled:opacity-50"
                >
                  {assignmentLoading ? (
                    <span className="inline-flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {selectedAssignee === '' ? 'Unassigning...' : 'Assigning...'}
                    </span>
                  ) : (
                    selectedAssignee === '' ? 'Unassign Lead' : 'Assign Lead'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Owner Settings Modal */}
        {isOwner && showOwnerSettingsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-7xl max-h-[95vh] flex flex-col relative">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 sm:px-6 py-3 sm:py-4 rounded-t-2xl">
              <button
                  className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white hover:text-gray-200 text-xl sm:text-2xl font-bold bg-white bg-opacity-20 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center transition-all hover:bg-opacity-30"
                onClick={() => setShowOwnerSettingsModal(false)}
              >
                &times;
              </button>
                <div className="flex items-center pr-8 sm:pr-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                  </svg>
                </div>
                  <div className="min-w-0">
                    <h2 className="text-lg sm:text-2xl font-bold text-white">Owner Settings</h2>
                    <p className="text-purple-100 text-xs sm:text-sm hidden sm:block">Manage users, permissions, and system settings</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-8">

              {/* Admin Management Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 rounded-t-xl">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                      </svg>
                      <span className="hidden sm:inline">Admin User Management</span>
                      <span className="sm:hidden">👥 Admin Users</span>
                    </h3>
                  </div>
                  
                  <div className="p-6">
                {/* Create New Admin Form */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-6 mb-6">
                      <h4 className="text-md font-semibold text-purple-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Create New Admin User
                      </h4>
                  <form onSubmit={handleCreateAdmin} className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                        <input
                          type="text"
                          value={newAdmin.name}
                          onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                        <input
                          type="email"
                          value={newAdmin.email}
                          onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          required
                        />
                      </div>
                    </div>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                        <input
                          type="password"
                          value={newAdmin.password}
                          onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                        <select
                          value={newAdmin.role}
                          onChange={(e) => setNewAdmin({...newAdmin, role: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          required
                        >
                          <option value="ADMIN" className="text-gray-900">Admin</option>
                          <option value="SALES" className="text-gray-900">Sales Person</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Dealership</label>
                        <select
                          value={newAdmin.dealershipId}
                          onChange={(e) => setNewAdmin({...newAdmin, dealershipId: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          required
                        >
                          <option value="" className="text-gray-900">Select a dealership</option>
                          {dealerships.map(dealership => (
                            <option key={dealership._id} value={dealership._id} className="text-gray-900">
                              {dealership.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={adminLoading}
                            className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        {adminLoading ? 'Creating...' : 'Create User'}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Admin Message */}
                {adminMessage && (
                      <div className={`mb-6 p-4 rounded-lg ${adminMessage.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {adminMessage}
                  </div>
                )}

                    {/* Existing Admin Users - Card Layout */}
                                  <div>
                      <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Existing Admin Users
                      </h4>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {(() => {
                          const ownerName = localStorage.getItem('adminName') || 'Josh Talaski';
                          const ownerEmail = localStorage.getItem('adminEmail') || 'No email';
                          const currentUserRole = localStorage.getItem('adminRole');
                          
                          // Create combined list with owner first, then admins
                          const allUsers = [];
                          
                          // Add owner if current user is owner (so they can see their own account)
                          if (currentUserRole === 'OWNER') {
                            allUsers.push({
                              _id: 'owner',
                              name: ownerName,
                              email: ownerEmail,
                              role: 'OWNER',
                              dealership: { name: 'All Dealerships' },
                              createdAt: null,
                              emailNotifications: localStorage.getItem('ownerEmailNotifications') === 'true',
                              isOwner: true
                            });
                          }
                          
                          // Add admin users
                          allUsers.push(...adminUsers);
                          
                          if (allUsers.length === 0) {
                            return (
                              <div className="col-span-full bg-gray-50 rounded-lg p-8 text-center">
                                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                <p className="text-gray-500 text-sm">No admin users found. Create your first admin user above.</p>
                                  </div>
                            );
                          }
                          
                          return allUsers.map((admin) => (
                            <div key={admin._id} className={`border rounded-lg p-4 transition-all duration-200 hover:shadow-md ${
                              admin.isOwner 
                                ? 'border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50' 
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}>
                              <div className="flex items-start justify-between">
                                <div className="flex items-center flex-1">
                                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-3 ${
                                    admin.isOwner 
                                      ? 'bg-gradient-to-r from-yellow-500 to-orange-600' 
                                      : 'bg-gradient-to-r from-blue-500 to-purple-600'
                                  }`}>
                                    <span className="text-white font-bold text-lg">{admin.name.charAt(0).toUpperCase()}</span>
                                </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center space-x-2">
                                      <h5 className="font-semibold text-gray-900 truncate">{admin.name}</h5>
                                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                        admin.role === 'OWNER' 
                                          ? 'bg-yellow-100 text-yellow-800' 
                                          : admin.role === 'SALES'
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-blue-100 text-blue-800'
                                      }`}>
                                  {admin.role || 'ADMIN'}
                                </span>
                                    </div>
                                    <p className="text-sm text-gray-500 truncate">{admin.email}</p>
                                    <p className="text-xs text-gray-400 mt-1">
                                      {admin.dealership?.name || 'Not assigned'} • {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : 'N/A'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="mt-4 flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={admin.emailNotifications || false}
                                    onChange={() => admin.isOwner ? handleToggleOwnerNotifications(admin.emailNotifications) : handleToggleAdminNotifications(admin._id, admin.emailNotifications)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                  />
                                  <label className="text-sm text-gray-700 font-medium">
                                    Email Notifications
                                  </label>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  {admin.isOwner ? (
                                    <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">Protected</span>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => handlePasswordReset(admin, 'admin')}
                                        className="text-blue-600 hover:text-blue-900 transition-colors p-2 rounded-lg hover:bg-blue-50"
                                        title="Reset Password"
                                      >
                                        🔒
                                      </button>
                                <button
                                  onClick={() => handleDeleteAdmin(admin._id)}
                                        className="text-red-600 hover:text-red-900 transition-colors p-2 rounded-lg hover:bg-red-50"
                                        title="Delete Admin"
                                >
                                        🗑️
                                </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* View Salesperson Section */}
                <div className="mt-8">
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                      <h4 className="text-md font-semibold text-gray-900">Dealership Admins & Salespeople</h4>
                    </div>
                    <div className="p-6">
                      {dealershipSalespeople.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">Loading dealership data...</div>
                      ) : (
                        <div className="space-y-6">
                          {dealershipSalespeople.map((dealershipData, idx) => (
                            <div key={dealershipData.dealership._id} className="border border-gray-200 rounded-lg p-4">
                              <h5 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-600 rounded-full flex items-center justify-center mr-3">
                                  <span className="text-white font-bold text-sm">{dealershipData.dealership.name.charAt(0).toUpperCase()}</span>
                                </div>
                                {dealershipData.dealership.name}
                                <span className="ml-2 text-sm text-gray-500">
                                  ({dealershipData.salesTeam.length + dealershipData.admins.length} total)
                                </span>
                              </h5>
                              
                              {/* Combined list of admins and salespeople */}
                              <div className="space-y-2">
                                                                 {/* Show admins first (highlighted) */}
                                 {dealershipData.admins.map((admin) => (
                                   <div key={`admin-${admin._id}`} className="flex flex-col sm:flex-row sm:items-center p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2 sm:space-y-0">
                                     <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mr-3">
                                       <span className="text-white font-bold text-sm">{admin.name.charAt(0).toUpperCase()}</span>
                                     </div>
                                     <div className="flex-1">
                                       <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                                       <div className="text-xs text-gray-500">{admin.email}</div>
                                     </div>
                                     <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
                                       <div>
                                         <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                           {admin.role || 'ADMIN'}
                                         </span>
                                         <div className="text-xs text-gray-500 mt-1">
                                           {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : 'N/A'}
                                         </div>
                                       </div>
                                       <div className="flex items-center space-x-2">
                                         <input
                                           type="checkbox"
                                           checked={admin.emailNotifications || false}
                                           onChange={() => handleToggleAdminNotifications(admin._id, admin.emailNotifications)}
                                           className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                         />
                                         <label className="text-xs text-gray-700 font-medium">
                                           Opt In
                                         </label>
                                       </div>
                                       <button
                                         onClick={() => handlePasswordReset(admin, 'admin')}
                                         className="text-blue-600 hover:text-blue-900 transition-colors p-1 rounded hover:bg-blue-50"
                                         title="Reset Password"
                                       >
                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                         </svg>
                                       </button>
                                       <button
                                         onClick={() => handleDeleteAdminFromView(admin._id)}
                                         className="text-red-600 hover:text-red-900 transition-colors p-1 rounded hover:bg-red-50"
                                         title="Delete Admin"
                                       >
                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                         </svg>
                                       </button>
                                     </div>
                                   </div>
                                 ))}
                                
                                                                 {/* Show salespeople */}
                                 {dealershipData.salesTeam.map((salesperson) => (
                                   <div key={`sales-${salesperson._id}`} className="flex flex-col sm:flex-row sm:items-center p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2 sm:space-y-0">
                                     <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-teal-600 rounded-full flex items-center justify-center mr-3">
                                       <span className="text-white font-bold text-sm">{salesperson.name.charAt(0).toUpperCase()}</span>
                                     </div>
                                     <div className="flex-1">
                                       <div className="text-sm font-medium text-gray-900">{salesperson.name}</div>
                                       <div className="text-xs text-gray-500">{salesperson.email}</div>
                                     </div>
                                     <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
                                       <div>
                                         <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                           SALES
                                         </span>
                                         <div className="text-xs text-gray-500 mt-1">
                                           {salesperson.lastLogin ? 'Active' : 'Never Logged In'}
                                         </div>
                                       </div>
                                       <div className="flex items-center space-x-2">
                                         <input
                                           type="checkbox"
                                           checked={salesperson.emailNotifications || false}
                                           onChange={() => handleToggleSalesTeamNotifications(salesperson._id, salesperson.emailNotifications, dealershipData.dealership._id)}
                                           className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                         />
                                         <label className="text-xs text-gray-700 font-medium">
                                           Opt In
                                         </label>
                                       </div>
                                       <button
                                         onClick={() => handlePasswordReset({ ...salesperson, dealership: dealershipData.dealership._id }, 'sales')}
                                         className="text-blue-600 hover:text-blue-900 transition-colors p-1 rounded hover:bg-blue-50"
                                         title="Reset Password"
                                       >
                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                         </svg>
                                       </button>
                                       <button
                                         onClick={() => handleDeleteSalespersonFromView(dealershipData.dealership._id, salesperson._id)}
                                         className="text-red-600 hover:text-red-900 transition-colors p-1 rounded hover:bg-red-50"
                                         title="Delete Salesperson"
                                       >
                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                         </svg>
                                       </button>
                                     </div>
                                   </div>
                                 ))}
                                
                                {/* Show message if no users */}
                                {dealershipData.admins.length === 0 && dealershipData.salesTeam.length === 0 && (
                                  <div className="text-center text-gray-500 py-4">
                                    No users assigned to this dealership
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* URL Activity Section */}
                <div className="mt-8">
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                      <h4 className="text-md font-semibold text-gray-900">URL Creation Activity</h4>
                      <button
                        onClick={fetchUrlActivities}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Refresh
                      </button>
                    </div>
                    <div className="p-6">
                      {urlActivitiesLoading ? (
                        <div className="text-center text-gray-500 py-8">Loading URL activities...</div>
                      ) : urlActivities.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">No URL activities found</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Created By</th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Customer</th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Vehicle</th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Dealership</th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Created</th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">URL</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {urlActivities.map((activity, idx) => (
                                <tr key={activity._id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="px-3 py-3 text-xs">
                                    <div className="flex items-center">
                                      <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                                        <span className="text-white font-bold text-xs">{activity.createdBy.name.charAt(0).toUpperCase()}</span>
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <div className="text-xs font-medium text-gray-900 truncate">{activity.createdBy.name}</div>
                                        <div className="text-xs text-gray-500 truncate">{activity.createdBy.email}</div>
                                        <span className={`inline-flex px-1 py-0.5 text-xs font-semibold rounded-full mt-1 ${
                                          activity.createdBy.role === 'OWNER' ? 'bg-purple-100 text-purple-800' :
                                          activity.createdBy.role === 'ADMIN' ? 'bg-blue-100 text-blue-800' :
                                          'bg-green-100 text-green-800'
                                        }`}>
                                          {activity.createdBy.role}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 text-xs">
                                    <div className="text-xs font-medium text-gray-900 truncate">
                                      {activity.customer.firstName} {activity.customer.lastName}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">{activity.customer.email}</div>
                                    {activity.customer.phone && (
                                      <div className="text-xs text-gray-500 truncate">{activity.customer.phone}</div>
                                    )}
                                  </td>
                                  <td className="px-3 py-3 text-xs">
                                    <div className="text-xs font-medium text-gray-900 truncate">
                                      {activity.vehicle.year} {activity.vehicle.make} {activity.vehicle.model}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">VIN: {activity.vehicle.vin.slice(-8)}</div>
                                    {activity.vehicle.stockNumber && (
                                      <div className="text-xs text-gray-500 truncate">Stock: {activity.vehicle.stockNumber}</div>
                                    )}
                                  </td>
                                  <td className="px-3 py-3 text-xs">
                                    <div className="text-xs text-gray-900 truncate">{activity.dealershipName}</div>
                                  </td>
                                  <td className="px-3 py-3 text-xs">
                                    <div className="text-xs text-gray-900">
                                      {new Date(activity.createdAt).toLocaleDateString()}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {new Date(activity.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 text-xs">
                                    <button
                                      onClick={() => window.open(activity.customUrl, '_blank')}
                                      className="text-blue-600 hover:text-blue-800 text-xs font-medium bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded"
                                      title={activity.customUrl}
                                    >
                                      View URL
                                    </button>
                                    <div className="text-xs text-gray-500 truncate mt-1">
                                      ID: {activity.linkId}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* TODO: Add other sections here like Dealership Admins & Salespeople and URL Activity */}
                
              </div>
            </div>
          </div>
        )}

      {/* Sales Team Modal */}
      {showSalesTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-200 w-full max-w-full sm:max-w-4xl relative overflow-hidden max-h-[95vh] overflow-y-auto">
            {/* Header with gradient background */}
            <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 px-4 py-4 sm:px-8 sm:py-6 relative">
              <div className="absolute inset-0 bg-black bg-opacity-10"></div>
              <button
                className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white hover:text-gray-200 text-xl sm:text-2xl font-bold bg-white bg-opacity-20 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center transition-all hover:bg-opacity-30"
                onClick={() => setShowSalesTeamModal(false)}
              >
                &times;
              </button>
              <div className="relative z-10">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="bg-white bg-opacity-20 rounded-xl sm:rounded-2xl p-2 sm:p-3">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.121M7 20v-2c0-.656.126-1.283.356-1.857M16 4a3 3 0 11-6 0 3 3 0 016 0zm-3 7a6 6 0 00-6 6v2h12v-2a6 6 0 00-6-6z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-3xl font-bold text-white">Sales Team Management</h2>
                    <p className="text-blue-100 mt-1 text-sm sm:text-base">Add and manage your sales team members</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-8">
              {/* Add Sales Person Form */}
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 border border-blue-100">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                  <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg sm:rounded-xl p-1.5 sm:p-2">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">Add New Team Member</h3>
                    <p className="text-gray-600 text-sm sm:text-base">Create a new sales team member account</p>
                  </div>
                </div>

                <form onSubmit={handleAddSalesTeamMember} className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span>Full Name</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Enter full name"
                          value={salesTeamForm.name}
                          onChange={e => setSalesTeamForm({...salesTeamForm, name: e.target.value})}
                          className="w-full border-2 border-gray-200 rounded-lg sm:rounded-xl px-3 py-2 sm:px-4 sm:py-3 text-gray-900 bg-white focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 shadow-sm hover:shadow-md text-sm sm:text-base"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                        <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span>Email Address</span>
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          placeholder="Enter email address"
                          value={salesTeamForm.email}
                          onChange={e => setSalesTeamForm({...salesTeamForm, email: e.target.value})}
                          className="w-full border-2 border-gray-200 rounded-lg sm:rounded-xl px-3 py-2 sm:px-4 sm:py-3 text-gray-900 bg-white focus:ring-4 focus:ring-purple-100 focus:border-purple-500 transition-all duration-200 shadow-sm hover:shadow-md text-sm sm:text-base"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                        <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <span>Password</span>
                      </label>
                      <div className="relative">
                        <input
                          type="password"
                          placeholder="Enter secure password"
                          value={salesTeamForm.password}
                          onChange={e => setSalesTeamForm({...salesTeamForm, password: e.target.value})}
                          className="w-full border-2 border-gray-200 rounded-lg sm:rounded-xl px-3 py-2 sm:px-4 sm:py-3 text-gray-900 bg-white focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200 shadow-sm hover:shadow-md text-sm sm:text-base"
                          required
                          minLength="8"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-4 border-t border-gray-200 gap-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg">
                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Password must be at least 8 characters</span>
                    </div>
                    <button
                      type="submit"
                      className="w-full sm:w-auto bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white px-6 sm:px-8 py-3 rounded-lg sm:rounded-xl font-semibold hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 sm:gap-3"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                      <span className="text-sm sm:text-base">Add Sales Team Member</span>
                    </button>
                  </div>
                </form>
              </div>

              {/* Success/Error Message */}
              {salesTeamMessage && (
                <div className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl sm:rounded-2xl border-l-4 ${salesTeamMessage.includes('Error') || salesTeamMessage.includes('Failed') 
                  ? 'bg-gradient-to-r from-red-50 to-pink-50 border-red-400 text-red-800' 
                  : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-400 text-green-800'
                }`}>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    {salesTeamMessage.includes('Error') || salesTeamMessage.includes('Failed') ? (
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    <span className="font-semibold text-sm sm:text-base">{salesTeamMessage}</span>
                  </div>
                </div>
              )}

              {/* Sales Team List */}
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl sm:rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-4 py-3 sm:px-6 sm:py-4">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="bg-white bg-opacity-20 rounded-lg p-1.5 sm:p-2">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.121M7 20v-2c0-.656.126-1.283.356-1.857M16 4a3 3 0 11-6 0 3 3 0 016 0zm-3 7a6 6 0 00-6 6v2h12v-2a6 6 0 00-6-6z" />
                      </svg>
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-white">Current Team Members</h3>
                  </div>
                </div>

                {salesTeamMembers.length === 0 ? (
                  <div className="text-center py-12 sm:py-16">
                    <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-full w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"> 
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.121M7 20v-2c0-.656.126-1.283.356-1.857M16 4a3 3 0 11-6 0 3 3 0 016 0zm-3 7a6 6 0 00-6 6v2h12v-2a6 6 0 00-6-6z" />
                      </svg>
                    </div>
                    <h4 className="text-base sm:text-lg font-semibold text-gray-700 mb-2">No team members yet</h4>
                    <p className="text-gray-500 text-sm sm:text-base">Add your first sales team member using the form above</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile Card View */}
                    <div className="block sm:hidden">
                      <div className="divide-y divide-gray-200">
                        {salesTeamMembers.map((person, idx) => (
                          <div key={person._id} className="p-4 bg-white hover:bg-gray-50 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                                  <span className="text-white font-bold text-sm">{person.name.charAt(0).toUpperCase()}</span>
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm font-bold text-gray-900">{person.name}</div>
                                  <div className="text-xs text-gray-500 truncate">{person.email}</div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      person.lastLogin 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {person.lastLogin ? 'Active' : 'Never Logged In'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-col items-end space-y-2">
                                <div className="flex items-center space-x-2">
                                  <label className="text-xs text-gray-600">Opt In</label>
                                  <input
                                    type="checkbox"
                                    checked={person.emailNotifications || false}
                                    onChange={() => handleToggleSalesTeamNotifications(person._id, person.emailNotifications)}
                                    className="w-4 h-4 text-blue-600 rounded"
                                  />
                                </div>
                                <button
                                  onClick={() => handleDeleteSalesTeamMember(person._id)}
                                  className="bg-red-100 text-red-700 px-3 py-1 rounded-lg hover:bg-red-200 transition-colors duration-200 font-semibold text-xs"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden sm:block overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Opt In</th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {salesTeamMembers.map((person, idx) => (
                          <tr key={person._id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors duration-150`}>
                            <td className="px-6 py-5 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                                  <span className="text-white font-bold text-lg">{person.name.charAt(0).toUpperCase()}</span>
                                </div>
                                <div>
                                  <div className="text-sm font-bold text-gray-900">{person.name}</div>
                                  <div className="text-xs text-gray-500">Sales Team Member</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap">
                              <div className="text-sm text-gray-700 font-medium">{person.email}</div>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap">
                              <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full ${
                                person.lastLogin 
                                  ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200' 
                                  : 'bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 border border-yellow-200'
                              }`}>
                                {person.lastLogin ? 'Active' : 'Never Logged In'}
                              </span>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={person.emailNotifications || false}
                                  onChange={() => handleToggleSalesTeamNotifications(person._id, person.emailNotifications)}
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                />
                                <label className="text-sm text-gray-700 font-medium">
                                  Email Notifications
                                </label>
                              </div>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-3">
                                <button
                                  onClick={() => handleDeleteSalesTeamMember(person._id)}
                                  className="bg-red-100 text-red-700 px-3 py-2 rounded-lg hover:bg-red-200 transition-colors duration-200 font-semibold text-xs"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}
              </div>
              
              {/* Activity Analytics Button */}
              <div className="mt-4 sm:mt-6 text-center">
                <button
                  onClick={handleShowActivityAnalytics}
                  className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  📊 View Team Activity Analytics
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Analytics Modal */}
      {showActivityAnalytics && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-200 w-full max-w-full sm:max-w-6xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-700 px-4 py-4 sm:px-8 sm:py-6 relative">
              <div className="absolute inset-0 bg-black bg-opacity-10"></div>
              <button
                className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white hover:text-gray-200 text-xl sm:text-2xl font-bold bg-white bg-opacity-20 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center transition-all hover:bg-opacity-30"
                onClick={() => setShowActivityAnalytics(false)}
              >
                &times;
              </button>
              <div className="relative z-10">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="bg-white bg-opacity-20 rounded-xl sm:rounded-2xl p-2 sm:p-3">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-3xl font-bold text-white">Team Activity Analytics</h2>
                    <p className="text-indigo-100 mt-1 text-sm sm:text-base">Monitor sales team and admin activities</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-8">
              {/* Stats Summary */}
              {activityStats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Total Activities</p>
                        <p className="text-2xl sm:text-3xl font-bold text-blue-900">{activityStats.totalActivities}</p>
                        <p className="text-xs text-blue-500 mt-1">Last {activityPeriod} days</p>
                      </div>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6 border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">Active Users</p>
                        <p className="text-2xl sm:text-3xl font-bold text-green-900">{activityStats.userStats?.length || 0}</p>
                        <p className="text-xs text-green-500 mt-1">Team members</p>
                      </div>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 sm:p-6 border border-purple-200 sm:col-span-2 md:col-span-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Most Active</p>
                        <p className="text-lg font-bold text-purple-900">
                          {activityStats.userStats && activityStats.userStats.length > 0 
                            ? activityStats.userStats[0]._id.userName 
                            : 'N/A'}
                        </p>
                        <p className="text-xs text-purple-500 mt-1">
                          {activityStats.userStats && activityStats.userStats.length > 0 
                            ? `${activityStats.userStats[0].count} actions` 
                            : 'No activity'}
                        </p>
                      </div>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                    <label className="text-sm font-medium text-gray-700">Filter by Action:</label>
                    <select
                      value={activityFilter}
                      onChange={(e) => handleActivityFilterChange(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white text-gray-900 shadow-sm"
                    >
                      <option value="all" className="text-gray-900 bg-white">All Actions</option>
                      <option value="create" className="text-gray-900 bg-white">Create</option>
                      <option value="update" className="text-gray-900 bg-white">Update</option>
                      <option value="delete" className="text-gray-900 bg-white">Delete</option>
                      <option value="view" className="text-gray-900 bg-white">View</option>
                      <option value="login" className="text-gray-900 bg-white">Login</option>
                    </select>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                    <label className="text-sm font-medium text-gray-700">Period:</label>
                    <select
                      value={activityPeriod}
                      onChange={(e) => handleActivityPeriodChange(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white text-gray-900 shadow-sm"
                    >
                      <option value="7" className="text-gray-900 bg-white">Last 7 days</option>
                      <option value="30" className="text-gray-900 bg-white">Last 30 days</option>
                      <option value="90" className="text-gray-900 bg-white">Last 90 days</option>
                    </select>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                    <button
                      onClick={() => {
                        if (salesTeamDealership) {
                          fetchActivityLogs(salesTeamDealership._id);
                          fetchActivityStats(salesTeamDealership._id);
                        }
                      }}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                    >
                      🔄 Refresh
                    </button>
                    <button
                      onClick={() => handleClearAllLogs(activityFilter)}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                    >
                      🗑️ Clear {activityFilter === 'all' ? 'All' : activityFilter.charAt(0).toUpperCase() + activityFilter.slice(1)} Logs
                    </button>
                  </div>
                </div>
              </div>

              {/* Activity Logs */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Recent Activity Logs
                  </h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {activityLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 border-3 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-gray-600 font-medium">Loading activities...</span>
                      </div>
                    </div>
                  ) : activityLogs.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-medium text-gray-700 mb-2">No activities found</h4>
                      <p className="text-gray-500">No activities match your current filters</p>
                    </div>
                  ) : (
                    <>
                      {/* Mobile Card View */}
                      <div className="block sm:hidden divide-y divide-gray-100">
                        {activityLogs.map((log) => (
                          <div key={log._id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start space-x-3">
                              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-bold text-xs">{log.userName.charAt(0).toUpperCase()}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-semibold text-gray-900">{log.userName}</span>
                                  <span className="text-xs text-gray-500">{formatTimestamp(log.timestamp)}</span>
                                </div>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getActionTypeBadgeColor(log.actionType)}`}>
                                    {log.actionType}
                                  </span>
                                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                    {log.entityType.replace('_', ' ')}
                                  </span>
                                  <span className="text-xs text-gray-400">{log.userType}</span>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">{log.action}</p>
                                
                                {/* Details for mobile */}
                                {log.details && Object.keys(log.details).length > 0 && (
                                  <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                                    <div className="font-medium text-gray-700 mb-1">Details:</div>
                                    {Object.entries(log.details).map(([key, value]) => {
                                      if (value === null || value === undefined) return null;
                                      
                                      const readableKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                                      
                                      let displayValue;
                                      if (typeof value === 'object' && value !== null) {
                                        if (key === 'vehicleInfo' && value.year && value.make && value.model) {
                                          displayValue = `${value.year} ${value.make} ${value.model}${value.vin ? ` (${value.vin})` : ''}`;
                                        } else {
                                          displayValue = JSON.stringify(value, null, 2);
                                        }
                                      } else {
                                        displayValue = String(value);
                                      }
                                      
                                      return (
                                        <div key={key} className="mb-1">
                                          <span className="font-medium text-gray-600">{readableKey}:</span>
                                          <span className="text-gray-800 ml-1 break-all">{displayValue}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Desktop View */}
                      <div className="hidden sm:block divide-y divide-gray-100">
                        {activityLogs.map((log) => (
                          <div key={log._id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-center space-x-4 flex-1">
                                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-white font-bold text-sm">{log.userName.charAt(0).toUpperCase()}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-3 flex-wrap">
                                    <span className="text-sm font-semibold text-gray-900">{log.userName}</span>
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getActionTypeBadgeColor(log.actionType)}`}>
                                      {log.actionType}
                                    </span>
                                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                      {log.entityType.replace('_', ' ')}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">{log.action}</p>
                                </div>
                              </div>
                              
                              <div className="flex items-start space-x-4">
                                {/* Details Box - Right Side */}
                                {log.details && Object.keys(log.details).length > 0 && (
                                  <div className="text-xs text-gray-500 bg-gray-50 rounded p-3 max-w-sm">
                                    <div className="font-medium text-gray-700 mb-2">Details:</div>
                                    {Object.entries(log.details).map(([key, value]) => {
                                      // Skip null/undefined values
                                      if (value === null || value === undefined) return null;
                                      
                                      // Format key names to be more readable
                                      const readableKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                                      
                                      // Handle different value types
                                      let displayValue;
                                      if (typeof value === 'object' && value !== null) {
                                        // For objects, try to format them nicely
                                        if (key === 'vehicleInfo' && value.year && value.make && value.model) {
                                          displayValue = `${value.year} ${value.make} ${value.model}${value.vin ? ` (${value.vin})` : ''}`;
                                        } else {
                                          displayValue = JSON.stringify(value, null, 2);
                                        }
                                      } else {
                                        displayValue = String(value);
                                      }
                                      
                                      return (
                                        <div key={key} className="mb-1 flex">
                                          <span className="font-medium text-gray-600 mr-2">{readableKey}:</span>
                                          <span className="text-gray-800 break-all">{displayValue}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                                
                                {/* Timestamp - Far Right */}
                                <div className="text-xs text-gray-500 text-right flex-shrink-0">
                                  <div>{formatTimestamp(log.timestamp)}</div>
                                  <div className="text-xs text-gray-400 mt-1">{log.userType}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trade-In Detail Modal */}
      {showTradeInDetailModal && selectedTradeInEvaluation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Trade-In Evaluation Details</h2>
                <button
                  className="text-gray-400 hover:text-gray-700 text-2xl font-bold"
                  onClick={() => {
                    setShowTradeInDetailModal(false);
                    // Close photo lightbox if open
                    if (showPhotoLightbox) {
                      closePhotoLightbox();
                    }
                  }}
                >
                  &times;
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Customer Information */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Customer Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-600">Name:</span>
                      <p className="text-gray-900 font-medium">
                        {selectedTradeInEvaluation.customerInfo?.firstName && selectedTradeInEvaluation.customerInfo?.lastName 
                          ? `${selectedTradeInEvaluation.customerInfo.firstName} ${selectedTradeInEvaluation.customerInfo.lastName}`
                          : 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Email:</span>
                      <p className="text-gray-900 font-medium text-blue-600">
                        {selectedTradeInEvaluation.customerInfo?.email || 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Phone:</span>
                      <p className="text-gray-900 font-medium">
                        {selectedTradeInEvaluation.customerInfo?.phone || 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">ZIP Code:</span>
                      <p className="text-gray-900">
                        {selectedTradeInEvaluation.customerInfo?.zipCode || 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Selling Option:</span>
                      <p className="text-gray-900 capitalize">
                        {selectedTradeInEvaluation.customerInfo?.sellingOption || 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Financing Status:</span>
                      <p className="text-gray-900 capitalize">
                        {selectedTradeInEvaluation.customerInfo?.financingStatus || 'Not provided'}
                      </p>
                    </div>
                    {selectedTradeInEvaluation.customerInfo?.payoffAmount > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Payoff Amount:</span>
                        <p className="text-gray-900 font-bold text-red-600">
                          ${selectedTradeInEvaluation.customerInfo.payoffAmount.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {selectedTradeInEvaluation.customerInfo?.loanCompany && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Loan Company:</span>
                        <p className="text-gray-900 font-semibold">
                          {selectedTradeInEvaluation.customerInfo.loanCompany}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Vehicle Information */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Vehicle Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-600">Vehicle:</span>
                      <p className="text-gray-900 font-medium">
                        {selectedTradeInEvaluation.vehicleInfo?.year} {selectedTradeInEvaluation.vehicleInfo?.make} {selectedTradeInEvaluation.vehicleInfo?.model}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">VIN:</span>
                      <p className="text-gray-900 font-mono text-sm">
                        {selectedTradeInEvaluation.vehicleInfo?.vin || 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Current Miles:</span>
                      <p className="text-gray-900 font-medium">
                        {selectedTradeInEvaluation.vehicleInfo?.currentMiles ? selectedTradeInEvaluation.vehicleInfo.currentMiles.toLocaleString() : 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Trim:</span>
                      <p className="text-gray-900">
                        {selectedTradeInEvaluation.vehicleInfo?.trim || 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Body Class:</span>
                      <p className="text-gray-900">
                        {selectedTradeInEvaluation.vehicleInfo?.bodyClass || 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Engine:</span>
                      <p className="text-gray-900">
                        {selectedTradeInEvaluation.vehicleInfo?.engineCylinders ? `${selectedTradeInEvaluation.vehicleInfo.engineCylinders} cylinders` : 'Not provided'}
                        {selectedTradeInEvaluation.vehicleInfo?.engineHP && ` - ${selectedTradeInEvaluation.vehicleInfo.engineHP} HP`}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Fuel Type:</span>
                      <p className="text-gray-900">
                        {selectedTradeInEvaluation.vehicleInfo?.fuelType || 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Drive Type:</span>
                      <p className="text-gray-900">
                        {selectedTradeInEvaluation.vehicleInfo?.driveType || 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Transmission:</span>
                      <p className="text-gray-900">
                        {selectedTradeInEvaluation.vehicleInfo?.transmission || 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Color:</span>
                      <p className="text-gray-900">
                        {selectedTradeInEvaluation.vehicleInfo?.vehicleColor || 'Not provided'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Features & Options */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.586-3.414l-2.828-2.828a2 2 0 00-2.828 0l-8.586 8.586a2 2 0 00-.586 1.414V18a2 2 0 002 2h2.586a2 2 0 001.414-.586l8.586-8.586a2 2 0 000-2.828z" />
                    </svg>
                    Features & Options
                  </h3>
                  <div className="grid grid-cols-1 gap-2">
                    {/* Comfort & Convenience */}
                    <div className="mb-2">
                      <h4 className="text-sm font-semibold text-purple-800 mb-1">Comfort & Convenience</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedTradeInEvaluation.features?.keylessEntry && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Keyless Entry
                          </span>
                        )}
                        {selectedTradeInEvaluation.features?.powerMirrors && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Power Mirrors
                          </span>
                        )}
                        {selectedTradeInEvaluation.features?.cruiseControl && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Cruise Control
                          </span>
                        )}
                        {selectedTradeInEvaluation.features?.remoteStart && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Remote Start
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Entertainment */}
                    <div className="mb-2">
                      <h4 className="text-sm font-semibold text-purple-800 mb-1">Entertainment</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedTradeInEvaluation.features?.androidAppleCarplay && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Android/Apple CarPlay
                          </span>
                        )}
                        {selectedTradeInEvaluation.features?.premiumSoundSystem && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Premium Sound System
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Exterior */}
                    <div>
                      <h4 className="text-sm font-semibold text-purple-800 mb-1">Exterior</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedTradeInEvaluation.features?.premiumWheels && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Premium Wheels
                          </span>
                        )}
                        {selectedTradeInEvaluation.features?.aftermarketWheels && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Aftermarket Wheels
                          </span>
                        )}
                        {selectedTradeInEvaluation.features?.tintedWindows && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Tinted Windows
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Evaluation Status */}
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="text-lg font-semibold text-indigo-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Evaluation Status
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-600">Status:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${
                        (selectedTradeInEvaluation.status === 'need_reviewed' || selectedTradeInEvaluation.status === 'pending' || !selectedTradeInEvaluation.status)
                          ? 'bg-yellow-100 text-yellow-800' 
                          : selectedTradeInEvaluation.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {(selectedTradeInEvaluation.status === 'need_reviewed' || selectedTradeInEvaluation.status === 'pending' || !selectedTradeInEvaluation.status) ? 'Need Reviewed' : 
                         selectedTradeInEvaluation.status === 'approved' ? 'Approved' : 
                         selectedTradeInEvaluation.status}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Submitted:</span>
                      <p className="text-gray-900 font-medium">
                        {selectedTradeInEvaluation.submissionDate ? new Date(selectedTradeInEvaluation.submissionDate).toLocaleDateString() : 'Not available'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Evaluation ID:</span>
                      <p className="text-gray-900 font-mono text-sm">
                        {selectedTradeInEvaluation._id}
                      </p>
                    </div>
                    {selectedTradeInEvaluation.evaluation?.estimatedValue && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Estimated Value:</span>
                        <p className="text-gray-900 font-bold text-lg text-green-600">
                          ${selectedTradeInEvaluation.evaluation.estimatedValue.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {selectedTradeInEvaluation.evaluation?.tradeInValue && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Trade-In Value:</span>
                        <p className="text-gray-900 font-bold text-lg text-blue-600">
                          ${selectedTradeInEvaluation.evaluation.tradeInValue.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Vehicle Condition & History - Full Width */}
              <div className="mt-6 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
                <h3 className="text-lg font-semibold text-orange-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Vehicle Condition & History (Customer Submitted Information)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedTradeInEvaluation.condition?.exteriorDamage && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Exterior Damage:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.exteriorDamage === 'none' ? 'No damage' : 
                         selectedTradeInEvaluation.condition.exteriorDamage === 'minor' ? 'Minor scratches/dings' : 
                         selectedTradeInEvaluation.condition.exteriorDamage === 'major-scratches' ? 'Major Scratches' : 
                         selectedTradeInEvaluation.condition.exteriorDamage === 'moderate' ? 'Moderate damage' : 
                         selectedTradeInEvaluation.condition.exteriorDamage === 'major' ? 'Major damage' : 
                         selectedTradeInEvaluation.condition.exteriorDamage}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.interiorDamage && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Interior Damage:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.interiorDamage === 'yes' ? 'Yes' : 
                         selectedTradeInEvaluation.condition.interiorDamage === 'no' ? 'No' : 
                         selectedTradeInEvaluation.condition.interiorDamage}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.windshieldCondition && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Windshield Condition:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.windshieldCondition}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.moonroofOperation && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Moonroof Operation:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.moonroofOperation === 'no-moonroof' ? 'No moonroof' : 
                         selectedTradeInEvaluation.condition.moonroofOperation === 'works-perfectly' ? 'Works perfectly' : 
                         selectedTradeInEvaluation.condition.moonroofOperation === 'not-working' ? 'Not working' : 
                         selectedTradeInEvaluation.condition.moonroofOperation === 'minor-issues' ? 'Minor issues (Legacy)' : 
                         selectedTradeInEvaluation.condition.moonroofOperation}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.smokedInOdors && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Smoked In Or Odors:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.smokedInOdors === 'yes' ? 'Yes' : 
                         selectedTradeInEvaluation.condition.smokedInOdors === 'no' ? 'No' : 
                         selectedTradeInEvaluation.condition.smokedInOdors}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.vehicleModifications && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Vehicle Modifications:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.vehicleModifications}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.accidentHistory && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Accident History:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.accidentHistory}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.vehicleDrivability && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Vehicle Drivability:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.vehicleDrivability}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.mechanicalElectricalStatus && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Mechanical/Electrical Status:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.mechanicalElectricalStatus === 'no-issues' ? 'No mechanical or electrical issues' : 
                         selectedTradeInEvaluation.condition.mechanicalElectricalStatus === 'minor-issues' ? 'Minor issues (working but not perfect)' : 
                         selectedTradeInEvaluation.condition.mechanicalElectricalStatus === 'check-engine-light' ? 'Check Engine Light Or Other Warnings' : 
                         selectedTradeInEvaluation.condition.mechanicalElectricalStatus === 'major-issues' ? 'Major issues (needs repair)' : 
                         selectedTradeInEvaluation.condition.mechanicalElectricalStatus === 'custom-issues' ? 'Custom issues described below' : 
                         selectedTradeInEvaluation.condition.mechanicalElectricalStatus}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.mechanicalElectricalIssues && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Mechanical/Electrical Issues:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.mechanicalElectricalIssues}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.engineIssues && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Engine Issues:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.engineIssues}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.serviceHistory && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Service History:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.serviceHistory}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.otherServiceIssues && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Other Service Issues:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.otherServiceIssues}
                      </p>
                    </div>
                  )}
                  {selectedTradeInEvaluation.condition?.keysAvailable && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Keys Available:</span>
                      <p className="text-gray-900 mt-1 p-2 bg-white rounded border">
                        {selectedTradeInEvaluation.condition.keysAvailable === '1' ? '1 Key' : 
                         selectedTradeInEvaluation.condition.keysAvailable === '2' ? '2 Keys' : 
                         selectedTradeInEvaluation.condition.keysAvailable === '3' ? '3 Keys' : 
                         selectedTradeInEvaluation.condition.keysAvailable === '4' ? '4+ Keys' : 
                         selectedTradeInEvaluation.condition.keysAvailable + ' Keys'}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Tire Information */}
                {(selectedTradeInEvaluation.condition?.tiresReplaced || selectedTradeInEvaluation.condition?.originalTires) && (
                  <div className="mt-4 pt-4 border-t border-orange-200">
                    <h4 className="text-md font-semibold text-orange-800 mb-2">Tire Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedTradeInEvaluation.condition?.tiresReplaced && (
                        <div>
                          <span className="text-sm font-medium text-gray-600">Tires Replaced:</span>
                          <p className="text-gray-900">Yes</p>
                        </div>
                      )}
                      {selectedTradeInEvaluation.condition?.originalTires && (
                        <div>
                          <span className="text-sm font-medium text-gray-600">Original Tires:</span>
                          <p className="text-gray-900">Yes</p>
                        </div>
                      )}
                      {selectedTradeInEvaluation.condition?.replacementMiles && (
                        <div>
                          <span className="text-sm font-medium text-gray-600">Last Tire Replacementt neb:</span>
                          <p className="text-gray-900">{selectedTradeInEvaluation.condition.replacementMiles}</p>
                        </div>
                      )}
                      {selectedTradeInEvaluation.condition?.replacementCount && (
                        <div>
                          <span className="text-sm font-medium text-gray-600">Replacement Count:</span>
                          <p className="text-gray-900">{selectedTradeInEvaluation.condition.replacementCount}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Photos Section */}
              {selectedTradeInEvaluation.photos && selectedTradeInEvaluation.photos.length > 0 && (
                <div className="mt-6 bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Vehicle Photos ({selectedTradeInEvaluation.photos.length})
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {selectedTradeInEvaluation.photos.map((photo, index) => {
                      // Construct the photo URL - photos are stored in uploads/trade-in-photos/
                      const photoUrl = `/uploads/trade-in-photos/${photo.filename}`;
                      return (
                        <div key={index} className="relative group cursor-pointer" onClick={() => openPhotoLightbox(selectedTradeInEvaluation.photos, index)}>
                          <img
                            src={photoUrl}
                            alt={photo.originalName || `Vehicle photo ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-gray-200 group-hover:shadow-lg transition-all duration-200 group-hover:scale-105"
                            style={{
                              imageOrientation: 'from-image',
                              objectFit: 'cover',
                              maxWidth: '100%',
                              maxHeight: '128px'
                            }}
                            onError={(e) => {
                              // If image fails to load, show a placeholder
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04NyA1NC41VjczLjVIMTEzVjU0LjVIODdaTTg3IDU0LjVWNzMuNUgxMTNWNTQuNUg4N1pNOTAgNTcuNUgxMTBWNzAuNUg5MFY1Ny41WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMTAwIDY0QzEwMS42NTcgNjQgMTAzIDYyLjY1NyAxMDMgNjFDMTAzIDU5LjM0MyAxMDEuNjU3IDU4IDEwMCA1OEM5OC4zNDMgNTggOTcgNTkuMzQzIDk3IDYxQzk3IDYyLjY1NyA5OC4zNDMgNjQgMTAwIDY0WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4=';
                            }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 bg-white text-gray-800 px-3 py-1 rounded-full text-sm font-medium transition-opacity flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View
                            </div>
                          </div>
                          {/* Photo info overlay */}
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="text-xs truncate">{photo.originalName}</div>
                            <div className="text-xs text-gray-300">
                              {photo.fileSize && `${(photo.fileSize / 1024 / 1024).toFixed(1)}MB`}
                              {photo.uploadDate && ` • ${new Date(photo.uploadDate).toLocaleDateString()}`}
                            </div>
                          </div>
                          {/* Photo number indicator */}
                          <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full">
                            {index + 1}
                          </div>
    </div>
  );
                    })}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowTradeInDetailModal(false);
                    // Close photo lightbox if open
                    if (showPhotoLightbox) {
                      closePhotoLightbox();
                    }
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                {selectedTradeInEvaluation.status === 'need_reviewed' ? (
                  <button
                    onClick={() => {
                      setTradeInToApprove(selectedTradeInEvaluation);
                      setShowApprovalModal(true);
                    }}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Approve
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowStatusUpdateModal(true);
                      setSelectedStatus('need_reviewed');
                    }}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Update Status
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowTradeInDetailModal(false);
                    handleDeleteTradeIn(selectedTradeInEvaluation);
                  }}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Old modal code removed since we don't use modals anymore */}
      {false && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="text-4xl mb-3">📸</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Generate Photo Upload Link</h2>
              <p className="text-gray-600">
                Create a custom link for customer to upload vehicle photos
              </p>
            </div>

            {/* Vehicle Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="text-sm font-semibold text-gray-700 mb-2">Vehicle:</div>
              <div className="text-gray-900">
                Vehicle Information
              </div>
              <div className="text-sm text-gray-600 mt-1">
                VIN: Not Available
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {}}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={generatePhotoUploadLink}
                disabled={false}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {false ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.102m0 0l4-4a4 4 0 105.656-5.656l-4 4a4 4 0 01-5.656 0z" />
                    </svg>
                    Generate Link
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Lightbox Modal */}
      {showPhotoLightbox && lightboxPhotos.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50" onClick={closePhotoLightbox}>
          <div className="relative w-full h-full flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={closePhotoLightbox}
              className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Previous Button */}
            {lightboxPhotos.length > 1 && (
              <button
                onClick={goToPrevPhoto}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Next Button */}
            {lightboxPhotos.length > 1 && (
              <button
                onClick={goToNextPhoto}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Photo Container */}
            <div className="max-w-full max-h-full">
              <img
                src={lightboxPhotos[currentPhotoIndex]?.url}
                alt={lightboxPhotos[currentPhotoIndex]?.originalName || `Photo ${currentPhotoIndex + 1}`}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                style={{
                  imageOrientation: 'from-image',
                  objectFit: 'contain',
                  maxWidth: '90vw',
                  maxHeight: '80vh'
                }}
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04NyA1NC41VjczLjVIMTEzVjU0LjVIODdaTTg3IDU0LjVWNzMuNUgxMTNWNTQuNUg4N1pNOTAgNTcuNUgxMTBWNzAuNUg5MFY1Ny41WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMTAwIDY0QzEwMS42NTcgNjQgMTAzIDYyLjY1NyAxMDMgNjFDMTAzIDU5LjM0MyAxMDEuNjU3IDU4IDEwMCA1OEM5OC4zNDMgNTggOTcgNTkuMzQzIDk3IDYxQzk3IDYyLjY1NyA5OC4zNDMgNjQgMTAwIDY0WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4=';
                }}
              />
            </div>

            {/* Photo Info */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white px-4 py-2 rounded-lg">
              <div className="text-center">
                <div className="text-sm font-medium">
                  {lightboxPhotos[currentPhotoIndex]?.originalName || `Photo ${currentPhotoIndex + 1}`}
                </div>
                <div className="text-xs text-gray-300 mt-1">
                  {currentPhotoIndex + 1} of {lightboxPhotos.length}
                  {lightboxPhotos[currentPhotoIndex]?.fileSize && (
                    <span> • {(lightboxPhotos[currentPhotoIndex].fileSize / 1024 / 1024).toFixed(1)}MB</span>
                  )}
                  {lightboxPhotos[currentPhotoIndex]?.uploadDate && (
                    <span> • {new Date(lightboxPhotos[currentPhotoIndex].uploadDate).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Photo Thumbnails Strip (for multiple photos) */}
            {lightboxPhotos.length > 1 && (
              <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-2 bg-black bg-opacity-50 p-2 rounded-lg max-w-full overflow-x-auto">
                {lightboxPhotos.map((photo, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPhotoIndex(index)}
                    className={`flex-shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition-all ${
                      index === currentPhotoIndex 
                        ? 'border-white scale-110' 
                        : 'border-transparent hover:border-gray-300 opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={photo.url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      style={{
                        maxWidth: '48px',
                        maxHeight: '48px'
                      }}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04NyA1NC41VjczLjVIMTEzVjU0LjVIODdaTTg3IDU0LjVWNzMuNUgxMTNWNTQuNUg4N1pNOTAgNTcuNUgxMTBWNzAuNUg5MFY1Ny41WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMTAwIDY0QzEwMS42NTcgNjQgMTAzIDYyLjY1NyAxMDMgNjFDMTAzIDU5LjM0MyAxMDEuNjU3IDU4IDEwMCA1OEM5OC4zNDMgNTggOTcgNTkuMzQzIDk3IDYxQzk3IDYyLjY1NyA5OC4zNDMgNjQgMTAwIDY0WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4=';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Instructions */}
            <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-2 rounded-lg text-sm">
              <div className="flex items-center space-x-4">
                {lightboxPhotos.length > 1 && (
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Navigate
                  </span>
                )}
                <span className="flex items-center">
                  <kbd className="bg-gray-700 px-1 rounded text-xs mr-1">ESC</kbd>
                  Close
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusUpdateModal && selectedTradeInEvaluation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Update Trade-In Status</h3>
                <button
                  className="text-gray-400 hover:text-gray-700 text-2xl font-bold"
                  onClick={() => {
                    setShowStatusUpdateModal(false);
                    setSelectedStatus('');
                  }}
                >
                  &times;
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Status: <span className="font-semibold text-blue-600">{
                      selectedTradeInEvaluation.status === 'need_reviewed' ? 'Need Reviewed' : 
                      selectedTradeInEvaluation.status === 'approved' ? 'Approved' : 
                      selectedTradeInEvaluation.status || 'Need Reviewed'
                    }</span>
                  </label>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select New Status:
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="need_reviewed">Need Reviewed</option>
                  </select>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">Customer Information:</h4>
                  <p className="text-sm text-gray-600">
                    {selectedTradeInEvaluation.customerInfo?.firstName} {selectedTradeInEvaluation.customerInfo?.lastName}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedTradeInEvaluation.vehicleInfo?.year} {selectedTradeInEvaluation.vehicleInfo?.make} {selectedTradeInEvaluation.vehicleInfo?.model}
                  </p>
                  <p className="text-sm text-gray-600">
                    VIN: {selectedTradeInEvaluation.vehicleInfo?.vin}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowStatusUpdateModal(false);
                    setSelectedStatus('');
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={statusUpdateLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateTradeInStatus(selectedTradeInEvaluation._id, selectedStatus)}
                  disabled={statusUpdateLoading || !selectedStatus}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {statusUpdateLoading ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval Confirmation Modal */}
      {showApprovalModal && tradeInToApprove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Approve Trade-In</h3>
                <button
                  className="text-gray-400 hover:text-gray-700 text-2xl font-bold"
                  onClick={() => {
                    setShowApprovalModal(false);
                    setTradeInToApprove(null);
                  }}
                >
                  &times;
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Are you sure you want to approve this vehicle?</h4>
                    <p className="text-sm text-gray-600 mt-1">This will change the status to "Approved".</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">Vehicle Details:</h4>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Customer:</span> {tradeInToApprove.customerInfo?.firstName} {tradeInToApprove.customerInfo?.lastName}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Vehicle:</span> {tradeInToApprove.vehicleInfo?.year} {tradeInToApprove.vehicleInfo?.make} {tradeInToApprove.vehicleInfo?.model}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">VIN:</span> {tradeInToApprove.vehicleInfo?.vin}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Mileage:</span> {tradeInToApprove.vehicleInfo?.currentMiles?.toLocaleString()} miles
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setTradeInToApprove(null);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={approvingTradeInId}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setApprovingTradeInId(tradeInToApprove._id);
                    await updateTradeInStatus(tradeInToApprove._id, 'approved');
                    setShowApprovalModal(false);
                    setTradeInToApprove(null);
                    setApprovingTradeInId(null);
                  }}
                  disabled={approvingTradeInId}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {approvingTradeInId ? 'Approving...' : 'Yes, Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Trade-In Confirmation Modal */}
      {showDeleteTradeInModal && tradeInToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Delete Trade-In Evaluation</h3>
                <button
                  className="text-gray-400 hover:text-gray-700 text-2xl font-bold"
                  onClick={() => {
                    setShowDeleteTradeInModal(false);
                    setTradeInToDelete(null);
                  }}
                >
                  &times;
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Are you sure you want to delete this trade-in evaluation?</h4>
                    <p className="text-sm text-gray-600 mt-1">This action cannot be undone.</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">Trade-In Details:</h4>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Customer:</span> {tradeInToDelete.customerInfo?.firstName} {tradeInToDelete.customerInfo?.lastName}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Vehicle:</span> {tradeInToDelete.vehicleInfo?.year} {tradeInToDelete.vehicleInfo?.make} {tradeInToDelete.vehicleInfo?.model}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">VIN:</span> {tradeInToDelete.vehicleInfo?.vin}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Submitted:</span> {tradeInToDelete.submissionDate ? new Date(tradeInToDelete.submissionDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteTradeInModal(false);
                    setTradeInToDelete(null);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={deletingTradeInId}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteTradeIn}
                  disabled={deletingTradeInId}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingTradeInId ? 'Deleting...' : 'Delete Trade-In'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Template Modal */}
      {showEmailTemplateModal && emailTemplateLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-2 sm:p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-3 sm:p-6 w-full max-w-full sm:max-w-4xl relative overflow-y-auto max-h-screen sm:max-h-[95vh]">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-3xl sm:text-2xl font-bold p-2 sm:p-1"
              onClick={() => setShowEmailTemplateModal(false)}
              style={{ minWidth: 44, minHeight: 44 }}
            >
              &times;
            </button>
            
            <div className="pr-12 sm:pr-8 mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Email Template for {emailTemplateLog.customer?.firstName}
              </h2>
              <p className="text-sm text-gray-600">Create a follow-up email template for your customer</p>
            </div>

            <div className="bg-gray-50 p-4 sm:p-6 rounded-xl border border-gray-200">
              {/* Vehicle Information Section */}
              <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-gray-100 mb-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Vehicle Image */}
                  <div className="lg:w-1/3">
                    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={emailTemplateLog.vehicle?.imageOne || 
                             emailTemplateLog.vehicle?.imageUrls?.split(',')[0] || 
                             emailTemplateLog.vehicle?.['Image One'] || 
                             emailTemplateLog.vehicle?.['imageUrls']?.split(',')[0] || 
                             emailTemplateLog.vehicle?.ImageOne || 
                             emailTemplateLog.vehicle?.ImageUrls?.split(',')[0] || 
                             emailTemplateLog.vehicle?.image1 || 
                             emailTemplateLog.vehicle?.Image1 || 
                             'https://via.placeholder.com/400x225?text=No+Image'}
                        alt={`${emailTemplateLog.vehicle?.year} ${emailTemplateLog.vehicle?.make} ${emailTemplateLog.vehicle?.model}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x225?text=No+Image';
                        }}
                      />
                    </div>
                  </div>

                  {/* Vehicle Details */}
                  <div className="lg:w-2/3">
                    <div className="space-y-3">
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                        {emailTemplateLog.vehicle?.year} {emailTemplateLog.vehicle?.make} {emailTemplateLog.vehicle?.model} {emailTemplateLog.vehicle?.trim || ''}
                      </h3>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        {emailTemplateLog.vehicle?.price && (
                          <div>
                            <span className="text-gray-600 font-medium">Price:</span>
                            <span className="ml-2 text-lg font-bold text-green-600">
                              ${Number(emailTemplateLog.vehicle.price).toLocaleString()}
                            </span>
                          </div>
                        )}
                        
                        {emailTemplateLog.vehicle?.mileage && (
                          <div>
                            <span className="text-gray-600 font-medium">Mileage:</span>
                            <span className="ml-2 text-gray-900">
                              {Number(emailTemplateLog.vehicle.mileage).toLocaleString()} miles
                            </span>
                          </div>
                        )}
                        
                        {emailTemplateLog.vehicle?.stockNumber && (
                          <div>
                            <span className="text-gray-600 font-medium">Stock #:</span>
                            <span className="ml-2 text-gray-900">{emailTemplateLog.vehicle.stockNumber}</span>
                          </div>
                        )}
                        
                        {emailTemplateLog.vehicle?.vin && (
                          <div>
                            <span className="text-gray-600 font-medium">VIN:</span>
                            <span className="ml-2 text-gray-900 font-mono text-xs">{emailTemplateLog.vehicle.vin}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Custom Message Section */}
              <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-gray-100">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Your Custom Message</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Add your personalized follow-up message for the customer below:
                </p>
                
                <textarea
                  value={emailCustomMessage}
                  onChange={(e) => setEmailCustomMessage(e.target.value)}
                  placeholder="Enter your custom message here..."
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                />
                
                                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      // Copy the HTML email template to clipboard
                      const vehicle = emailTemplateLog.vehicle;
                      const vehicleName = `${vehicle?.year} ${vehicle?.make} ${vehicle?.model} ${vehicle?.trim || ''}`.trim();
                      // Try multiple price fields to find a valid price
                      let price = vehicle?.price || vehicle?.internetPrice || vehicle?.msrp || vehicle?.MSRP || vehicle?.Price || vehicle?.InternetPrice;
                      price = price && !isNaN(Number(price)) && Number(price) > 0 ? `$${Number(price).toLocaleString()}` : 'Contact for pricing';
                      const mileage = vehicle?.mileage ? `${Number(vehicle.mileage).toLocaleString()} miles` : 'N/A';
                      const stockNumber = vehicle?.stockNumber || 'N/A';
                      const vin = vehicle?.vin || 'N/A';
                      const vehicleImage = vehicle?.imageOne || 
                                         vehicle?.imageUrls?.split(',')[0] || 
                                         vehicle?.['Image One'] || 
                                         vehicle?.['imageUrls']?.split(',')[0] || 
                                         vehicle?.ImageOne || 
                                         vehicle?.ImageUrls?.split(',')[0] || 
                                         vehicle?.image1 || 
                                         vehicle?.Image1 || 
                                         'https://via.placeholder.com/400x225?text=No+Image';
                      
                      const packetLink = `https://buymycarnwi.com${emailTemplateLog.link}`;
                      
                      const htmlEmailContent = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Vehicle Information: ${vehicleName}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #f5f5f5; }
        .container { max-width: 600px; margin: 0 auto; background-color: white; border-radius: 10px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
        
        .vehicle-section { padding: 20px; border-bottom: 1px solid #e5e7eb; }
        .vehicle-card { display: flex; gap: 20px; align-items: center; background-color: #f9fafb; padding: 20px; border-radius: 8px; border: 1px solid #e5e7eb; cursor: pointer; transition: all 0.2s; text-decoration: none; color: inherit; }
        .vehicle-card:hover { transform: translateY(-2px); box-shadow: 0 8px 16px rgba(0,0,0,0.15); background-color: #f3f4f6; }
        .vehicle-image { width: 200px; height: 120px; object-fit: cover; border-radius: 6px; }
        .vehicle-details { flex: 1; }
        .vehicle-title { font-size: 24px; font-weight: bold; color: #1f2937; margin: 0 0 10px 0; }
        .vehicle-info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 10px; }
        .info-item { font-size: 14px; color: #6b7280; }
        .info-value { font-weight: 600; color: #1f2937; }
        .price { font-size: 28px; font-weight: bold; color: #059669; margin: 10px 0; }
        .message-section { padding: 20px; }
        .message-content { line-height: 1.6; color: #374151; white-space: pre-line; font-size: 18px; font-weight: 500; }
        .cta-section { padding: 20px; text-align: center; background-color: #f9fafb; border-top: 1px solid #e5e7eb; }
        .cta-button { display: inline-block; background-color: #2563eb; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold; }
        @media (max-width: 600px) {
            .vehicle-card { flex-direction: column; text-align: center; }
            .vehicle-image { width: 100%; max-width: 300px; height: 180px; }
            .vehicle-info { grid-template-columns: 1fr; }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="vehicle-section">
            <a href="${packetLink}" target="_blank" class="vehicle-card">
                <img src="${vehicleImage}" alt="${vehicleName}" class="vehicle-image" />
                <div class="vehicle-details">
                    <h2 class="vehicle-title">${vehicleName}</h2>
                    <div class="price">${price}</div>
                    <div class="vehicle-info">
                        <div class="info-item">
                            <span style="color: #6b7280;">Mileage:</span>
                            <span class="info-value">${mileage}</span>
                        </div>
                        <div class="info-item">
                            <span style="color: #6b7280;">Stock #:</span>
                            <span class="info-value">${stockNumber}</span>
                        </div>
                        <div class="info-item" style="grid-column: 1 / -1;">
                            <span style="color: #6b7280;">VIN:</span>
                            <span class="info-value" style="font-family: monospace; font-size: 12px;">${vin}</span>
                        </div>
                    </div>
                </div>
            </a>
        </div>
        
        <div class="message-section">
            <div class="message-content">${emailCustomMessage}</div>
        </div>
        
        <div class="cta-section">
            <p style="margin: 0; color: #374151;">Click the vehicle image above to view complete vehicle details and pricing options.</p>
        </div>
    </div>
</body>
</html>`;
                      
                      navigator.clipboard.writeText(htmlEmailContent);
                      setEmailTemplateCopied(true);
                      setTimeout(() => setEmailTemplateCopied(false), 3000);
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                  >
                    {emailTemplateCopied ? (
                      <span className="flex items-center justify-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Copied!
                      </span>
                    ) : (
                      'Copy HTML Email Template'
                    )}
                  </button>
                  
                  <button
                    onClick={() => setShowEmailTemplateModal(false)}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Window Sticker Finder Modal */}
      {showWindowStickerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Window Sticker Finder</h2>
                <button
                  className="text-gray-400 hover:text-gray-700 text-3xl font-bold"
                  onClick={() => {
                    setShowWindowStickerModal(false);
                    setWindowStickerVin('');
                    setWindowStickerResults([]);
                  }}
                >
                  &times;
                </button>
              </div>

              <div className="space-y-6">
                {/* VIN Search Input */}
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter VIN (17 characters)
                    </label>
                    <input
                      type="text"
                      value={windowStickerVin}
                      onChange={(e) => setWindowStickerVin(e.target.value.toUpperCase())}
                      placeholder="Enter 17-character VIN"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-500"
                      maxLength={17}
                    />
                  </div>
                  <button
                    onClick={() => searchWindowStickers(windowStickerVin)}
                    disabled={windowStickerLoading || windowStickerVin.length !== 17}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {windowStickerLoading ? 'Searching...' : 'Search All'}
                  </button>
                </div>

                {/* Search Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-medium text-blue-800 mb-2">Search Information</h3>
                  <p className="text-sm text-blue-700">
                    This tool searches all available window sticker available for the following manufacturers:
                  </p>
                  <ul className="list-disc list-inside text-sm text-blue-700 mt-2 space-y-1">
                    <li><span className="font-medium">GM:</span> Chevrolet, GMC, Buick, Cadillac</li>
                    <li><span className="font-medium">Chrysler:</span> Chrysler, Dodge, Jeep, Ram</li>
                    <li><span className="font-medium">Ford:</span> Ford, Lincoln</li>
                    <li><span className="font-medium">Subaru:</span> Subaru</li>
                  </ul>
                </div>

                {/* Results */}
                {windowStickerResults.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Search Results</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {windowStickerResults.map((result, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-800">{result.manufacturer}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              result.status === 'found' 
                                ? 'bg-green-100 text-green-800' 
                                : result.status === 'not_found'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {result.status === 'found' ? 'Found' : 
                               result.status === 'not_found' ? 'Not Found' : 'Error'}
                            </span>
                          </div>
                          
                          {result.status === 'found' && (
                            <div className="space-y-2">
                              <button
                                onClick={() => window.open(result.url, '_blank')}
                                className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                              >
                                View PDF
                              </button>
                              <a
                                href={result.downloadUrl}
                                download
                                className="block w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium text-center"
                              >
                                Download PDF
                              </a>
                            </div>
                          )}
                          
                          {result.status !== 'found' && (
                            <p className="text-sm text-gray-600">{result.error}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Loading State */}
                {windowStickerLoading && (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-600">Searching all manufacturers...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordResetModal && passwordResetUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md p-6 relative">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold"
              onClick={() => {
                setShowPasswordResetModal(false);
                setPasswordResetUser(null);
                setNewPassword('');
              }}
            >
              &times;
            </button>
            
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Reset Password</h2>
            
            <div className="mb-4">
              <div className="flex items-center mb-2">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-sm">{passwordResetUser.name.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">{passwordResetUser.name}</div>
                  <div className="text-xs text-gray-500">{passwordResetUser.email}</div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                    passwordResetUser.userType === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {passwordResetUser.userType === 'admin' ? 'ADMIN' : 'SALES'}
                  </span>
                </div>
              </div>
            </div>
            
            <form onSubmit={handlePasswordResetSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter new password"
                  required
                  minLength={4}
                />
                <p className="text-xs text-gray-500 mt-1">Password must be at least 4 characters long</p>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordResetModal(false);
                    setPasswordResetUser(null);
                    setNewPassword('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passwordResetLoading || newPassword.length < 4}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {passwordResetLoading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Session Timeout Warning */}
      <SessionTimeoutWarning
        isOpen={showTimeoutWarning}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleLogoutNow}
      />

      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50 max-w-md w-full">
          <div className={`relative p-4 rounded-2xl shadow-2xl border-l-4 transform transition-all duration-300 ${
            toast.type === 'success' 
              ? 'bg-white border-green-500 text-gray-800' 
              : 'bg-white border-red-500 text-gray-800'
          }`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {toast.type === 'success' ? (
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="ml-3 flex-1">
                <div className="flex items-center justify-between">
                  <h3 className={`text-sm font-semibold ${
                    toast.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {toast.type === 'success' ? 'Success!' : 'Error'}
                  </h3>
                  <button
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                    className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  {toast.message}
                </p>
                {toast.link && (
                  <div className="mt-3 p-2 bg-gray-50 rounded-lg border">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-mono truncate mr-2">
                        {toast.link.length > 50 ? `${toast.link.substring(0, 50)}...` : toast.link}
                      </span>
                      <button
                        onClick={() => navigator.clipboard.writeText(toast.link)}
                        className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      </div>
    </div>
    </div>
  );
}
